import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react';
import { appPath, APP_PATHS } from '../../../utils/appPaths.js';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import './processing-report.scss';
import './income-list.scss';
import ProcessingReportHeader from './ProcessingReportHeader';
import ProcessingReportTabs from './ProcessingReportTabs';
import ProcessingReportOverview from './ProcessingReportOverview';
import {
    Container, Row, Col, Card, CardBody, CardHeader, Button, Table, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input,
    Collapse
} from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import ProcessingReportService from '../../../Services/reports/ProcessingReportService';
import { buildTopTitleMetricsLookup, resolveTitleMetrics, buildNamedMetricsLookup, resolveNamedMetrics } from './IncomeListHelpers';
import IncomeService from '../../../Services/income/IncomeService';
import moment from 'moment';
import { Download, Edit, AlertCircle, Check, ChevronDown, ChevronUp, Clock } from 'react-feather';
import TablePagination from './Pagination';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/theme-twilight';
import 'ace-builds/src-noconflict/theme-solarized_dark';
import 'ace-builds/src-noconflict/theme-solarized_light';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/webpack-resolver';

function IncomeReport() {
    const { id } = useParams();
    const navigate = useNavigate();

    // State for edit modal
    const [modal, setModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedArtistId, setSelectedArtistId] = useState(null);
    const [artists, setArtists] = useState([]);
    const [artistFilter, setArtistFilter] = useState('');
    const [distributionTypes, setDistributionTypes] = useState({
        title: false,
        dsp: false,
        country: false,
        typesale: false,
        all: false
    });

    // Pagination state for all distribution tables
    const [pagination, setPagination] = useState({
        title_distribution: { currentPage: 1, itemsPerPage: 10 },
        dsp_distribution: { currentPage: 1, itemsPerPage: 10 },
        country_distribution: { currentPage: 1, itemsPerPage: 10 },
        typesale_distribution: { currentPage: 1, itemsPerPage: 10 }
    });

    // State to store distribution data
    const [distributionData, setDistributionData] = useState({
        title_distribution: null,
        dsp_distribution: null,
        country_distribution: null,
        typesale_distribution: null
    });

    // State for AFR (Artist For Review) data
    const [selectedAfrArtistId, setSelectedAfrArtistId] = useState(null);
    const [afrData, setAfrData] = useState({
        title: null,
        dsp: null,
        country: null,
        typesale: null
    });
    const [afrPagination, setAfrPagination] = useState({
        title: { currentPage: 1, itemsPerPage: 10 },
        dsp: { currentPage: 1, itemsPerPage: 10 },
        country: { currentPage: 1, itemsPerPage: 10 },
        typesale: { currentPage: 1, itemsPerPage: 10 }
    });
    const [afrLoading, setAfrLoading] = useState({
        title: false,
        dsp: false,
        country: false,
        typesale: false
    });
    const [afrModal, setAfrModal] = useState(false);
    const [selectedAfrItem, setSelectedAfrItem] = useState(null);
    const [afrConsolidatedData, setAfrConsolidatedData] = useState([]);

    // State for Artist Mismatch table
    const [artistMismatchData, setArtistMismatchData] = useState([]);
    const [artistMismatchLoading, setArtistMismatchLoading] = useState(false);
    const [artistMismatchPagination, setArtistMismatchPagination] = useState({
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 0
    });
    const [artistMismatchSearch, setArtistMismatchSearch] = useState('');
    const [artistMismatchSortBy, setArtistMismatchSortBy] = useState('isrc'); // 'isrc', 'revenue', 'streams'
    const [artistMismatchSortOrder, setArtistMismatchSortOrder] = useState('asc');
    const [artistMismatchModal, setArtistMismatchModal] = useState(false);
    const [selectedMismatchItem, setSelectedMismatchItem] = useState(null);
    const [selectedMismatchArtistId, setSelectedMismatchArtistId] = useState(null);
    const [mismatchArtistFilter, setMismatchArtistFilter] = useState('');
    const [editingIsrcUpc, setEditingIsrcUpc] = useState(false);
    const [newIsrc, setNewIsrc] = useState('');
    const [newUpc, setNewUpc] = useState('');

    const [activeReportTab, setActiveReportTab] = useState('overview');

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState({
        processingMetadata: true,
        processingTimes: true,
        distributionCounts: true,
        topAnalysis: true,
        dataQuality: true,
        artistDistributions: true,
        breakdownCounts: true,
        afrSection: true,
        rawJson: true
    });

    // State for virtual scrolling
    const [visibleRows, setVisibleRows] = useState(10);

    // State for Ace Editor
    const [aceTheme, setAceTheme] = useState('monokai');
    const [aceFontSize, setAceFontSize] = useState(12);
    const [aceWrapMode, setAceWrapMode] = useState(true);
    const [useAceEditor, setUseAceEditor] = useState(true);
    const [aceError, setAceError] = useState(false);
    const aceEditorRef = useRef(null);

    // Global error handler for Ace Editor
    useEffect(() => {
        const handleError = (event) => {
            if (event.error && event.error.message && event.error.message.includes('worker-json')) {
                console.warn('Ace Editor worker error detected, switching to fallback');
                setAceError(true);
                setUseAceEditor(false);
                event.preventDefault();
            }
        };

        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []);


    // Function to fetch processing report JSON data from S3 URL
    const fetchProcessingReportData = async (url) => {
        try {
            if (!url) {
                console.log('No processing report URL provided');
                return null;
            }
            
            console.log('🔗 Fetching processing report data from URL:', url);
            console.log('📊 URL details:', {
                protocol: url.split('://')[0],
                domain: url.split('://')[1]?.split('/')[0],
                path: url.split('://')[1]?.split('/').slice(1).join('/')
            });
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const jsonData = await response.json();
            console.log('✅ Processing report data fetched successfully:', jsonData);
            console.log('📈 Data structure:', {
                hasProcessingMetadata: !!jsonData.processing_metadata,
                hasDistributionCounts: !!jsonData.distribution_counts,
                hasTopAnalysis: !!jsonData.top_analysis,
                hasDataQuality: !!jsonData.data_quality
            });
            return jsonData;
        } catch (error) {
            console.error('❌ Error fetching processing report data:', error);
            console.error('🔗 Failed URL:', url);
            throw error;
        }
    };

    // Handle pagination change
    const handlePageChange = async (type, page) => {
        console.log(`Changing page for ${type} to ${page}`);

        // Update local pagination state
        setPagination(prev => ({
            ...prev,
            [type]: { ...prev[type], currentPage: page }
        }));

        try {
            // Set loading state for the specific distribution type
            if (type === 'title_distribution') {
                setDistributionData(prev => ({ ...prev, title_distribution: null }));
            } else if (type === 'dsp_distribution') {
                setDistributionData(prev => ({ ...prev, dsp_distribution: null }));
            } else if (type === 'country_distribution') {
                setDistributionData(prev => ({ ...prev, country_distribution: null }));
            } else if (type === 'typesale_distribution') {
                setDistributionData(prev => ({ ...prev, typesale_distribution: null }));
            }

            // Directly call the API with the correct parameters
            let response;
            if (type === 'title_distribution') {
                response = await ProcessingReportService.getTitleDistribution(id, page, pagination.title_distribution.itemsPerPage);
            } else if (type === 'dsp_distribution') {
                response = await ProcessingReportService.getDspDistribution(id, page, pagination.dsp_distribution.itemsPerPage);
            } else if (type === 'country_distribution') {
                response = await ProcessingReportService.getCountryDistribution(id, page, pagination.country_distribution.itemsPerPage);
            } else if (type === 'typesale_distribution') {
                response = await ProcessingReportService.getTypesaleDistribution(id, page, pagination.typesale_distribution.itemsPerPage);
            }

            console.log(`${type} response for page ${page}:`, response);

            // Update the data if the response is successful
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        [type]: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        const updatedPagination = { ...(report.various_artists_distributions.pagination || {}) };
                        updatedPagination[type] = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        [type]: response.data.various_artists_distributions?.[type] || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        } catch (error) {
            console.error(`Error fetching ${type} for page ${page}:`, error);
        }
    };

    // Handle items per page change
    const handleItemsPerPageChange = async (type, itemsPerPage) => {
        console.log(`Changing items per page for ${type} to ${itemsPerPage}`);

        // Update local pagination state
        setPagination(prev => ({
            ...prev,
            [type]: { ...prev[type], itemsPerPage, currentPage: 1 }
        }));

        try {
            // Set loading state for the specific distribution type
            if (type === 'title_distribution') {
                setDistributionData(prev => ({ ...prev, title_distribution: null }));
            } else if (type === 'dsp_distribution') {
                setDistributionData(prev => ({ ...prev, dsp_distribution: null }));
            } else if (type === 'country_distribution') {
                setDistributionData(prev => ({ ...prev, country_distribution: null }));
            } else if (type === 'typesale_distribution') {
                setDistributionData(prev => ({ ...prev, typesale_distribution: null }));
            }

            // Directly call the API with the correct parameters
            let response;
            if (type === 'title_distribution') {
                response = await ProcessingReportService.getTitleDistribution(id, 1, itemsPerPage);
            } else if (type === 'dsp_distribution') {
                response = await ProcessingReportService.getDspDistribution(id, 1, itemsPerPage);
            } else if (type === 'country_distribution') {
                response = await ProcessingReportService.getCountryDistribution(id, 1, itemsPerPage);
            } else if (type === 'typesale_distribution') {
                response = await ProcessingReportService.getTypesaleDistribution(id, 1, itemsPerPage);
            }

            console.log(`${type} response for items per page ${itemsPerPage}:`, response);

            // Update the data if the response is successful
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        [type]: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        const updatedPagination = { ...(report.various_artists_distributions.pagination || {}) };
                        updatedPagination[type] = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        [type]: response.data.various_artists_distributions?.[type] || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        } catch (error) {
            console.error(`Error fetching ${type} for items per page ${itemsPerPage}:`, error);
        }
    };

    // Get paginated data - use the data from our separate distribution queries
    const getPaginatedData = (data, type) => {
        if (!data || !Array.isArray(data)) {
            // Check if we have data from our separate distribution queries
            if (distributionData[type] && Array.isArray(distributionData[type])) {
                return distributionData[type];
            }

            // If we don't have separate distribution data yet, use the data from the main report
            if (report?.various_artists_distributions?.[type] && Array.isArray(report.various_artists_distributions[type])) {
                return report.various_artists_distributions[type];
            }

            // Fallback to empty array if no data is available
            return [];
        }

        // If data is provided and is an array, return it
        return Array.isArray(data) ? data : [];
    };

    // Filtered artists based on search input
    const filteredArtists = Array.isArray(artists) ? artists.filter(artist =>
        artist?.artist_name?.toLowerCase().includes(artistFilter.toLowerCase())
    ) : [];

    // Toggle modal
    const toggleModal = () => {
        const newModalState = !modal;
        setModal(newModalState);

        if (newModalState) {
            // Reset distribution types when opening modal
            setDistributionTypes({
                title: false,
                dsp: false,
                country: false,
                typesale: false,
                all: false
            });
        } else {
            // Reset state when closing modal
            setSelectedArtistId(null);
            setArtistFilter('');
        }
    };

    // Handle checkbox changes
    const handleDistributionTypeChange = (type) => {
        console.log('Checkbox changed:', type, 'Current state:', distributionTypes);

        if (type === 'all') {
            // If 'all' is selected or unselected, update all checkboxes accordingly
            const newValue = !distributionTypes.all;
            setDistributionTypes({
                title: newValue,
                dsp: newValue,
                country: newValue,
                typesale: newValue,
                all: newValue
            });
            console.log('Setting all checkboxes to:', newValue);
        } else {
            // For individual checkboxes
            const newValue = !distributionTypes[type];

            // If we're checking a box and all other boxes are already checked, also check 'all'
            let shouldCheckAll = newValue;
            if (newValue) {
                const otherTypes = ['title', 'dsp', 'country', 'typesale'].filter(t => t !== type);
                shouldCheckAll = otherTypes.every(t => distributionTypes[t]);
            }

            setDistributionTypes(prev => ({
                ...prev,
                [type]: newValue,
                // Only set 'all' to true if all individual types are checked
                all: newValue && shouldCheckAll ? true : false
            }));

            console.log('Updated', type, 'to', newValue, 'All status:', shouldCheckAll);
        }
    };

    // Mutation for updating distribution
    const updateDistributionMutation = useMutation({
        mutationFn: (data) => {
            return ProcessingReportService.updateDistribution(data);
        },
        onSuccess: (response) => {
            // Close modal and show success message
            toggleModal();
            toast.success('Distribution updated successfully');
            // Refetch the report data to update the JSON and remove items that were linked to AFR
            // The backend will automatically update the report JSON when distributions are updated
            refetch();
        },
        onError: (error) => {
            console.error('Error updating distribution:', error);
            toast.error('Error updating distribution: ' + (error.response?.data?.message || error.message));
        }
    });

    // Mutation for updating ISRC/UPC
    const updateIsrcUpcMutation = useMutation({
        mutationFn: (data) => {
            return ProcessingReportService.updateIsrcUpc(data);
        },
        onSuccess: (response) => {
            toast.success('ISRC/UPC updated successfully');
            // Refetch the mismatch data to reflect the changes
            fetchArtistMismatchData(id, artistMismatchPagination.currentPage, artistMismatchPagination.itemsPerPage);
            // Refetch the report data
            refetch();
        },
        onError: (error) => {
            console.error('Error updating ISRC/UPC:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            const errorDetails = error?.response?.data?.errors ? JSON.stringify(error.response.data.errors, null, 2) : '';
            toast.error(`Error updating ISRC/UPC: ${errorMessage}${errorDetails ? '\n\n' + errorDetails : ''}`);
        }
    });

    // Handle form submission
    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        console.log('Submitting form with distribution types:', distributionTypes);

        if (!selectedItem) {
            console.error('No item selected');
            return;
        }

        // Validate artist selection
        if (!selectedArtistId) {
            toast.warning('Please select an artist');
            return;
        }

        // Check if ISRC or UPC contains AFR placeholder values
        const isrc = selectedItem.asset_isrc;
        const upc = selectedItem.product_upc;
        
        const hasPlaceholderIsrc = isrc && (
            isrc === 'AFR_PLACEHOLDER_ISRC' || 
            isrc.includes('AFR_PLACEHOLDER_ISRC') ||
            isrc === 'N/A' || 
            !isrc || 
            isrc.trim() === ''
        );
        
        const hasPlaceholderUpc = upc && (
            upc === 'AFR_PLACEHOLDER_PRODUCT' || 
            upc.includes('AFR_PLACEHOLDER_PRODUCT') ||
            upc === 'N/A' || 
            !upc || 
            upc.trim() === ''
        );
        
        // If both ISRC and UPC are placeholders or invalid, prevent linking
        if (hasPlaceholderIsrc && hasPlaceholderUpc) {
            toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
            return;
        }

        // Determine distribution type
        let distributionType = 'all';

        // Check if any checkboxes are selected
        const anySelected = distributionTypes.title ||
                           distributionTypes.dsp ||
                           distributionTypes.country ||
                           distributionTypes.typesale ||
                           distributionTypes.all;

        if (!anySelected) {
            toast.warning('Please select at least one distribution type');
            return;
        }

        // If "all" is checked or multiple individual types are checked, use "all"
        if (distributionTypes.all) {
            distributionType = 'all';
        } else {
            // Count selected types
            const selectedTypes = [];
            if (distributionTypes.title) selectedTypes.push('title');
            if (distributionTypes.dsp) selectedTypes.push('dsp');
            if (distributionTypes.country) selectedTypes.push('country');
            if (distributionTypes.typesale) selectedTypes.push('typesale');

            if (selectedTypes.length === 1) {
                // If only one type is selected, use that specific type
                distributionType = selectedTypes[0];
            } else if (selectedTypes.length > 1) {
                // If multiple types are selected, use "all"
                distributionType = 'all';
            }
        }

        console.log('Using distribution type:', distributionType);

        // Create request payload
        // Backend expects: isrc or upc (required_without each other), artist_id, and distribution_type
        // distribution_type must be: 'all', 'title', 'dsp', 'country', or 'typesale'
        const payload = {
            distribution_type: distributionType, // Already in correct format: 'all', 'title', 'dsp', 'country', or 'typesale'
            artist_id: selectedArtistId
        };

        // Only include ISRC if it's not a placeholder
        if (!hasPlaceholderIsrc && isrc && isrc !== 'N/A') {
            payload.isrc = isrc;
        }
        
        // Only include UPC if it's not a placeholder and ISRC is not available
        if (!hasPlaceholderUpc && upc && upc !== 'N/A') {
            payload.upc = upc;
        }
        
        // Validate that we have at least one valid identifier
        if (!payload.isrc && !payload.upc) {
            toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
            return;
        }

        console.log('Sending payload:', payload);

        // Submit the update
        updateDistributionMutation.mutate(payload);
    };

    // Fetch main report data
    const { isLoading, isError, data, error, status, refetch } = useQuery({
        queryKey: ['report', id],
        queryFn: () => ProcessingReportService.getReportById(id),
        retry: false, // Don't retry on error
        staleTime: 0,
        onError: (err) => {
            console.error('Error fetching report:', err);
        }
    });

    // Fetch title distribution data
    const {
        isLoading: isTitleLoading
    } = useQuery({
        queryKey: ['title_distribution', id, pagination.title_distribution],
        queryFn: () => ProcessingReportService.getTitleDistribution(
            id,
            pagination.title_distribution.currentPage,
            pagination.title_distribution.itemsPerPage
        ),
        enabled: !!data?.data?.id, // Only run this query if the main report data is loaded
        onSuccess: (response) => {
            console.log('Title distribution response:', response);
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        title_distribution: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        const updatedPagination = { ...report.various_artists_distributions.pagination };
                        updatedPagination.title_distribution = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        title_distribution: response.data.various_artists_distributions?.title_distribution || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        },
        onError: (err) => {
            console.error('Error fetching title distribution:', err);
        }
    });

    // Fetch DSP distribution data
    const {
        isLoading: isDspLoading
    } = useQuery({
        queryKey: ['dsp_distribution', id, pagination.dsp_distribution],
        queryFn: () => ProcessingReportService.getDspDistribution(
            id,
            pagination.dsp_distribution.currentPage,
            pagination.dsp_distribution.itemsPerPage
        ),
        enabled: !!data?.data?.id, // Only run this query if the main report data is loaded
        onSuccess: (response) => {
            console.log('DSP distribution response:', response);
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        dsp_distribution: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        const updatedPagination = { ...report.various_artists_distributions.pagination };
                        updatedPagination.dsp_distribution = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        dsp_distribution: response.data.various_artists_distributions?.dsp_distribution || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        },
        onError: (err) => {
            console.error('Error fetching DSP distribution:', err);
        }
    });

    // Fetch country distribution data
    const {
        isLoading: isCountryLoading
    } = useQuery({
        queryKey: ['country_distribution', id, pagination.country_distribution],
        queryFn: () => ProcessingReportService.getCountryDistribution(
            id,
            pagination.country_distribution.currentPage,
            pagination.country_distribution.itemsPerPage
        ),
        enabled: !!data?.data?.id, // Only run this query if the main report data is loaded
        onSuccess: (response) => {
            console.log('Country distribution response:', response);
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        country_distribution: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        const updatedPagination = { ...report.various_artists_distributions.pagination };
                        updatedPagination.country_distribution = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        country_distribution: response.data.various_artists_distributions?.country_distribution || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        },
        onError: (err) => {
            console.error('Error fetching country distribution:', err);
        }
    });

    // Fetch typesale distribution data
    const {
        isLoading: isTypesaleLoading
    } = useQuery({
        queryKey: ['typesale_distribution', id, pagination.typesale_distribution],
        queryFn: () => ProcessingReportService.getTypesaleDistribution(
            id,
            pagination.typesale_distribution.currentPage,
            pagination.typesale_distribution.itemsPerPage
        ),
        enabled: !!data?.data?.id, // Only run this query if the main report data is loaded
        onSuccess: (response) => {
            console.log('Typesale distribution response:', response);
            if (response?.success && response?.data) {
                // The response structure is different for the dedicated endpoint
                if (response.data.distributions) {
                    // Update the distribution data with the new data
                    setDistributionData(prev => ({
                        ...prev,
                        typesale_distribution: response.data.distributions || []
                    }));

                    // Also update the report's pagination data if available
                    if (report && report.various_artists_distributions && response.data.pagination) {
                        const updatedPagination = { ...report.various_artists_distributions.pagination };
                        updatedPagination.typesale_distribution = response.data.pagination;
                        report.various_artists_distributions.pagination = updatedPagination;
                    }
                } else if (response.data.various_artists_distributions) {
                    // Fallback to the old structure if needed
                    setDistributionData(prev => ({
                        ...prev,
                        typesale_distribution: response.data.various_artists_distributions?.typesale_distribution || []
                    }));

                    // Also update the report's pagination data
                    if (report && report.various_artists_distributions && response.data.various_artists_distributions?.pagination) {
                        if (!report.various_artists_distributions.pagination) {
                            report.various_artists_distributions.pagination = {};
                        }
                        report.various_artists_distributions.pagination = {
                            ...(report.various_artists_distributions.pagination || {}),
                            ...response.data.various_artists_distributions.pagination
                        };
                    }
                }
            }
        },
        onError: (err) => {
            console.error('Error fetching typesale distribution:', err);
        }
    });

    // Functions to fetch AFR data
    const fetchAfrData = async (type, reportId, page = 1, perPage = 10) => {
        if (!reportId) {
            console.warn('No report ID provided for AFR data');
            return;
        }

        setAfrLoading(prev => ({ ...prev, [type]: true }));

        try {
            let response;

            switch (type) {
                case 'title':
                    response = await ProcessingReportService.getAfrTitleDistribution(reportId, page, perPage);
                    break;
                case 'dsp':
                    response = await ProcessingReportService.getAfrDspDistribution(reportId, page, perPage);
                    break;
                case 'country':
                    response = await ProcessingReportService.getAfrCountryDistribution(reportId, page, perPage);
                    break;
                case 'typesale':
                    response = await ProcessingReportService.getAfrTypesaleDistribution(reportId, page, perPage);
                    break;
                default:
                    throw new Error(`Unknown AFR type: ${type}`);
            }

            if (response?.success && response.data) {
                const rows = response.data.distributions || response.data[type] || [];
                setAfrData(prev => ({
                    ...prev,
                    [type]: rows,
                }));

                const pagination = response.data.pagination;
                if (pagination) {
                    setAfrPagination(prev => ({
                        ...prev,
                        [type]: {
                            currentPage: pagination.current_page || page,
                            itemsPerPage: pagination.per_page || perPage,
                        },
                    }));
                }
            }
        } catch (error) {
            console.error(`Error fetching AFR ${type} data:`, error);
            setAfrData(prev => ({ ...prev, [type]: [] }));
        } finally {
            setAfrLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleAfrPageChange = async (type, page) => {
        if (!id) return;

        setAfrPagination(prev => ({
            ...prev,
            [type]: { ...prev[type], currentPage: page }
        }));

        await fetchAfrData(type, id, page, afrPagination[type].itemsPerPage);
    };

    const handleAfrItemsPerPageChange = async (type, itemsPerPage) => {
        if (!id) return;

        setAfrPagination(prev => ({
            ...prev,
            [type]: { itemsPerPage, currentPage: 1 }
        }));

        await fetchAfrData(type, id, 1, itemsPerPage);
    };

    const loadAfrDistributions = async (reportId) => {
        if (!reportId) return;

        setSelectedAfrArtistId(reportId);
        setAfrData({ title: null, dsp: null, country: null, typesale: null });
        setAfrConsolidatedData([]);

        await Promise.all([
            fetchAfrData('title', reportId, 1, afrPagination.title.itemsPerPage),
            fetchAfrData('dsp', reportId, 1, afrPagination.dsp.itemsPerPage),
            fetchAfrData('country', reportId, 1, afrPagination.country.itemsPerPage),
            fetchAfrData('typesale', reportId, 1, afrPagination.typesale.itemsPerPage),
        ]);
    };

    // Open AFR edit modal
    const openAfrEditModal = (item, type) => {
        setSelectedAfrItem({ ...item, afrType: type });
        
        // Set the initial selected artist ID
        if (item.artist_id) {
            setSelectedArtistId(item.artist_id);
        } else {
            setSelectedArtistId(null);
        }

        // Reset the artist filter
        setArtistFilter('');

        // Fetch artists when opening the modal
        fetchArtists();

        setAfrModal(true);
    };

    // Toggle AFR modal
    const toggleAfrModal = () => {
        setAfrModal(!afrModal);
        if (!afrModal) {
            setSelectedAfrItem(null);
            setSelectedArtistId(null);
            setArtistFilter('');
        }
    };

    // Handle AFR form submission
    const handleAfrSubmit = (e) => {
        if (e) e.preventDefault();

        if (!selectedAfrItem) {
            console.error('No AFR item selected');
            return;
        }

        if (!selectedArtistId) {
            toast.warning('Please select an artist');
            return;
        }

        // Check if ISRC or UPC contains AFR placeholder values
        const isrc = selectedAfrItem.asset_isrc;
        const upc = selectedAfrItem.product_upc;
        
        const hasPlaceholderIsrc = isrc && (
            isrc === 'AFR_PLACEHOLDER_ISRC' || 
            isrc.includes('AFR_PLACEHOLDER_ISRC') ||
            isrc === 'N/A' || 
            !isrc || 
            isrc.trim() === ''
        );
        
        const hasPlaceholderUpc = upc && (
            upc === 'AFR_PLACEHOLDER_PRODUCT' || 
            upc.includes('AFR_PLACEHOLDER_PRODUCT') ||
            upc === 'N/A' || 
            !upc || 
            upc.trim() === ''
        );
        
        // If both ISRC and UPC are placeholders or invalid, prevent linking
        if (hasPlaceholderIsrc && hasPlaceholderUpc) {
            toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
            return;
        }

        // Create request payload for AFR update
        // Backend expects: isrc or upc (required_without each other), artist_id, and distribution_type
        // distribution_type must be: 'all', 'title', 'dsp', 'country', or 'typesale'
        const payload = {
            distribution_type: selectedAfrItem.afrType || 'all', // Should be 'all', 'title', 'dsp', 'country', or 'typesale'
            artist_id: selectedArtistId
            // Note: afr_artist_id is not used by the backend - backend updates by ISRC/UPC
        };

        // Only include ISRC if it's not a placeholder
        if (!hasPlaceholderIsrc && isrc && isrc !== 'N/A') {
            payload.isrc = isrc;
        }
        
        // Only include UPC if it's not a placeholder and ISRC is not available
        if (!hasPlaceholderUpc && upc && upc !== 'N/A') {
            payload.upc = upc;
        }
        
        // Validate that we have at least one valid identifier
        if (!payload.isrc && !payload.upc) {
            toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
            return;
        }

        console.log('Sending AFR update payload:', payload);

        // Use the same update mutation
        updateDistributionMutation.mutate(payload, {
            onSuccess: () => {
                toggleAfrModal();
                if (id) {
                    loadAfrDistributions(id);
                }
            }
        });
    };

    const handleDownloadFile = async () => {
        try {
            // Use income_id if available in the report, otherwise use the report ID
            const downloadId = report.income_id || report.id;
            console.log('Downloading raw file for ID:', downloadId);

            const response = await IncomeService.downloadrowFile(downloadId);
            console.log('Download response received:', response);

            if (!response || !response.data) {
                console.error('Invalid response format:', response);
                toast.error('Failed to download raw file: Invalid response format');
                return;
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: response.mimeType || 'application/octet-stream' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.fileName || 'downloaded_file.csv');
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url); // Clean up
            link.remove();
            console.log('Download completed successfully');
        } catch (error) {
            console.error('Failed to download raw file:', error);
            toast.error(`Failed to download raw file: ${error.message || 'Unknown error'}`);
        }
    };

    // Extract the report data if available
    const report = data?.data;
    
    // Get database_data from the new API structure (or fallback to report for old structure)
    const dbData = useMemo(() => {
        return report?.database_data || report;
    }, [report]);
    
    // Helper to get statistics from database_data (new structure) or directly from report (old structure)
    const reportStatistics = useMemo(() => {
        return dbData?.statistics || report?.statistics;
    }, [dbData, report]);
    
    // Debug logging for the report data and URL
    React.useEffect(() => {
        if (report) {
            console.log('Report data from backend:', report);
            console.log('Database data:', report.database_data);
            console.log('S3 data:', report.s3_data);
            console.log('Report status:', report.report_status);
        }
    }, [report]);

    // Get S3 data directly from the report response (no separate fetch needed)
    const processingReportData = useMemo(() => {
        // S3 data is now included in the report response as s3_data
        if (!report?.s3_data) {
            console.log('⚠️ No S3 data in report response');
            return null;
        }
        
        console.log('✅ S3 data found in report response:', report.s3_data);
        return report.s3_data;
    }, [report]);

    // Consolidate AFR data whenever afrData changes
    React.useEffect(() => {
        if (!id) {
            setAfrConsolidatedData([]);
            return;
        }

        const consolidated = [];
        
        // Process title data
        if (afrData.title && Array.isArray(afrData.title)) {
            afrData.title.forEach(item => {
                consolidated.push({
                    ...item,
                    distribution_type: 'Title',
                    name: item.name || item.title_name || 'N/A'
                });
            });
        }
        
        // Process DSP data
        if (afrData.dsp && Array.isArray(afrData.dsp)) {
            afrData.dsp.forEach(item => {
                consolidated.push({
                    ...item,
                    distribution_type: 'DSP',
                    name: item.name || item.dsp_name || 'N/A'
                });
            });
        }
        
        // Process country data
        if (afrData.country && Array.isArray(afrData.country)) {
            afrData.country.forEach(item => {
                consolidated.push({
                    ...item,
                    distribution_type: 'Country',
                    name: item.name || item.country_name || 'N/A'
                });
            });
        }
        
        // Process typesale data
        if (afrData.typesale && Array.isArray(afrData.typesale)) {
            afrData.typesale.forEach(item => {
                consolidated.push({
                    ...item,
                    distribution_type: 'Type Sale',
                    name: item.name || item.typesale_name || 'N/A'
                });
            });
        }
        
        console.log('Consolidated AFR data:', consolidated);
        setAfrConsolidatedData(consolidated);
    }, [afrData, id]);

    useEffect(() => {
        if (id) {
            loadAfrDistributions(id);
        }
    }, [id]);

    // Fetch artist mismatch data for the current report
    const fetchArtistMismatchData = async (reportId, page = 1, perPage = 10) => {
        if (!reportId) return;

        setArtistMismatchLoading(true);
        try {
            // Fetch all distribution types and filter for artist mismatches
            const [titleRes, dspRes, countryRes, typesaleRes] = await Promise.all([
                ProcessingReportService.getTitleDistribution(reportId, page, perPage * 4), // Get more items to filter
                ProcessingReportService.getDspDistribution(reportId, page, perPage * 4),
                ProcessingReportService.getCountryDistribution(reportId, page, perPage * 4),
                ProcessingReportService.getTypesaleDistribution(reportId, page, perPage * 4)
            ]);

            const allMismatches = [];

            // Helper function to check if item has artist mismatch (AFR = Artist For Review)
            // An item is an AFR mismatch if its ISRC or UPC is a placeholder value
            const isAfrPlaceholder = (val) => {
                if (!val) return true;
                const v = String(val).trim();
                return v === '' || v === 'N/A' || v.startsWith('AFR_') || v.includes('AFR_PLACEHOLDER');
            };
            const hasArtistMismatch = (item) => {
                // The backend already pre-filters to AFR records; include all returned items
                // but also guard against items with both valid ISRC and UPC (fully resolved)
                return isAfrPlaceholder(item.asset_isrc) || isAfrPlaceholder(item.product_upc);
            };

            // Process all distributions and group by ISRC
            const isrcMap = new Map();

            // Process title distributions
            if (titleRes?.success && titleRes?.data?.distributions) {
                titleRes.data.distributions.forEach(item => {
                    if (hasArtistMismatch(item)) {
                        const isrc = item.asset_isrc || 'NO_ISRC';
                        if (!isrcMap.has(isrc)) {
                            isrcMap.set(isrc, {
                                ...item,
                                distribution_types: [],
                                names: []
                            });
                        }
                        const existing = isrcMap.get(isrc);
                        if (!existing.distribution_types.includes('Title')) {
                            existing.distribution_types.push('Title');
                        }
                        if (item.title_name || item.asset_title) {
                            existing.names.push(item.title_name || item.asset_title);
                        }
                    }
                });
            }

            // Process DSP distributions
            if (dspRes?.success && dspRes?.data?.distributions) {
                dspRes.data.distributions.forEach(item => {
                    if (hasArtistMismatch(item)) {
                        const isrc = item.asset_isrc || 'NO_ISRC';
                        if (!isrcMap.has(isrc)) {
                            isrcMap.set(isrc, {
                                ...item,
                                distribution_types: [],
                                names: []
                            });
                        }
                        const existing = isrcMap.get(isrc);
                        if (!existing.distribution_types.includes('DSP')) {
                            existing.distribution_types.push('DSP');
                        }
                        if (item.dsp_name) {
                            existing.names.push(item.dsp_name);
                        }
                    }
                });
            }

            // Process country distributions
            if (countryRes?.success && countryRes?.data?.distributions) {
                countryRes.data.distributions.forEach(item => {
                    if (hasArtistMismatch(item)) {
                        const isrc = item.asset_isrc || 'NO_ISRC';
                        if (!isrcMap.has(isrc)) {
                            isrcMap.set(isrc, {
                                ...item,
                                distribution_types: [],
                                names: []
                            });
                        }
                        const existing = isrcMap.get(isrc);
                        if (!existing.distribution_types.includes('Country')) {
                            existing.distribution_types.push('Country');
                        }
                        if (item.country_name) {
                            existing.names.push(item.country_name);
                        }
                    }
                });
            }

            // Process typesale distributions
            if (typesaleRes?.success && typesaleRes?.data?.distributions) {
                typesaleRes.data.distributions.forEach(item => {
                    if (hasArtistMismatch(item)) {
                        const isrc = item.asset_isrc || 'NO_ISRC';
                        if (!isrcMap.has(isrc)) {
                            isrcMap.set(isrc, {
                                ...item,
                                distribution_types: [],
                                names: []
                            });
                        }
                        const existing = isrcMap.get(isrc);
                        if (!existing.distribution_types.includes('Type Sale')) {
                            existing.distribution_types.push('Type Sale');
                        }
                        if (item.typesale_name) {
                            existing.names.push(item.typesale_name);
                        }
                    }
                });
            }

            // Convert map to array and aggregate data
            const groupedMismatches = Array.from(isrcMap.values()).map(item => {
                // Get correct artist info
                let correctProduct;
                if (item.correct_product && typeof item.correct_product === 'object') {
                    correctProduct = item.correct_product;
                } else if (item.correct_artist_name) {
                    correctProduct = { artist_name: item.correct_artist_name };
                } else if (item.correct_artist) {
                    correctProduct = typeof item.correct_artist === 'object'
                        ? item.correct_artist
                        : { artist_name: item.correct_artist };
                } else {
                    correctProduct = { artist_name: "Unknown Artist" };
                }

                // Aggregate revenue and streams from all distribution types
                // Note: We'll use the first item's revenue/streams as they should be the same per ISRC
                return {
                    ...item,
                    asset_isrc: item.asset_isrc || 'N/A',
                    product_upc: item.product_upc || 'N/A',
                    distribution_type: item.distribution_types.join(', '), // Show all types
                    name: [...new Set(item.names)].join(', ') || 'N/A', // Unique names
                    correct_product: correctProduct,
                    correct_artist_name: correctProduct.artist_name
                };
            });

            // Paginate the results
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            const paginatedData = groupedMismatches.slice(startIndex, endIndex);

            setArtistMismatchData(paginatedData);
            setArtistMismatchPagination({
                currentPage: page,
                itemsPerPage: perPage,
                totalItems: groupedMismatches.length,
                totalPages: Math.ceil(groupedMismatches.length / perPage)
            });
        } catch (error) {
            console.error('Error fetching artist mismatch data:', error);
            setArtistMismatchData([]);
        } finally {
            setArtistMismatchLoading(false);
        }
    };

    // Fetch artist mismatch data when report is loaded
    React.useEffect(() => {
        if (id && report && !isLoading) {
            fetchArtistMismatchData(id, 1, 10);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, report, isLoading]);

    // Filter artists for mismatch modal
    const filteredMismatchArtists = useMemo(() => {
        if (!mismatchArtistFilter) return artists;
        const filter = mismatchArtistFilter.toLowerCase();
        return artists.filter(artist =>
            artist.artist_name.toLowerCase().includes(filter) ||
            artist.id.toString().includes(filter)
        );
    }, [artists, mismatchArtistFilter]);

    // Open mismatch edit modal
    const openMismatchEditModal = (item) => {
        setSelectedMismatchItem(item);
        
        // Initialize ISRC/UPC with current values
        setNewIsrc(item.asset_isrc || '');
        setNewUpc(item.product_upc || '');
        
        // Set initial artist ID if there's a correct artist
        if (item.correct_product && item.correct_product.artist_id) {
            setSelectedMismatchArtistId(item.correct_product.artist_id);
        } else {
            setSelectedMismatchArtistId(null);
        }
        
        setMismatchArtistFilter('');
        setEditingIsrcUpc(false);
        setArtistMismatchModal(true);
    };

    // Toggle mismatch modal
    const toggleMismatchModal = () => {
        setArtistMismatchModal(!artistMismatchModal);
        if (!artistMismatchModal) {
            setSelectedMismatchItem(null);
            setSelectedMismatchArtistId(null);
            setMismatchArtistFilter('');
            setEditingIsrcUpc(false);
            setNewIsrc('');
            setNewUpc('');
        }
    };

    // Handle ISRC/UPC update
    const handleUpdateIsrcUpc = async (e) => {
        if (e) e.preventDefault();
        
        if (!selectedMismatchItem) {
            toast.error('No item selected');
            return Promise.reject(new Error('No item selected'));
        }

        const isrc = selectedMismatchItem.asset_isrc;
        const upc = selectedMismatchItem.product_upc;
        
        // Use newIsrc/newUpc if provided, otherwise use current values
        const effectiveNewIsrc = (newIsrc && newIsrc.trim() !== '') ? newIsrc.trim() : null;
        const effectiveNewUpc = (newUpc && newUpc.trim() !== '') ? newUpc.trim() : null;
        
        // Check if at least one value is provided (new or current)
        if (!effectiveNewIsrc && !effectiveNewUpc && !isrc && !upc) {
            toast.error('Please provide at least one ISRC or UPC value');
            return Promise.reject(new Error('No values provided'));
        }
        
        // If no new values provided, nothing to update
        if (!effectiveNewIsrc && !effectiveNewUpc) {
            return Promise.resolve([]);
        }

        // Check if new values are placeholders (only if they're being changed)
        if (effectiveNewIsrc) {
            const hasPlaceholderNewIsrc = effectiveNewIsrc === 'AFR_PLACEHOLDER_ISRC' || 
                                         effectiveNewIsrc.includes('AFR_PLACEHOLDER_ISRC') ||
                                         effectiveNewIsrc === 'N/A' || 
                                         effectiveNewIsrc.trim() === '';
            if (hasPlaceholderNewIsrc) {
                toast.error('New ISRC cannot be a placeholder or invalid value');
                return Promise.reject(new Error('Placeholder ISRC not allowed'));
            }
        }
        
        if (effectiveNewUpc) {
            const hasPlaceholderNewUpc = effectiveNewUpc === 'AFR_PLACEHOLDER_PRODUCT' || 
                                        effectiveNewUpc.includes('AFR_PLACEHOLDER_PRODUCT') ||
                                        effectiveNewUpc === 'N/A' || 
                                        effectiveNewUpc.trim() === '';
            if (hasPlaceholderNewUpc) {
                toast.error('New UPC cannot be a placeholder or invalid value');
                return Promise.reject(new Error('Placeholder UPC not allowed'));
            }
        }

        try {
            // Determine distribution type from the item
            // The distribution_type field contains comma-separated types like "Title, DSP, Country"
            // We need to update all types, so we'll need to call the API for each type
            const distributionTypes = selectedMismatchItem.distribution_type 
                ? selectedMismatchItem.distribution_type.split(',').map(t => t.trim().toLowerCase())
                : ['title', 'dsp', 'country', 'typesale'];

            // Map distribution type names to API values
            const typeMap = {
                'title': 'title',
                'dsp': 'dsp',
                'country': 'country',
                'type sale': 'typesale',
                'typesale': 'typesale'
            };

            // Update each distribution type
            const updatePromises = [];
            for (const type of distributionTypes) {
                const apiType = typeMap[type.toLowerCase()] || type.toLowerCase();
                if (['title', 'dsp', 'country', 'typesale'].includes(apiType)) {
                    const payload = {
                        distribution_type: apiType,
                        old_isrc: isrc && isrc !== 'N/A' && !isrc.includes('AFR_PLACEHOLDER') ? isrc : null,
                        old_upc: upc && upc !== 'N/A' && !upc.includes('AFR_PLACEHOLDER') ? upc : null,
                    };

                    if (effectiveNewIsrc) {
                        payload.new_isrc = effectiveNewIsrc;
                    }
                    if (effectiveNewUpc) {
                        payload.new_upc = effectiveNewUpc;
                    }

                    // Only add if we have at least one old identifier
                    if (payload.old_isrc || payload.old_upc) {
                        updatePromises.push(updateIsrcUpcMutation.mutateAsync(payload));
                    }
                }
            }

            if (updatePromises.length === 0) {
                toast.error('Cannot update: No valid old ISRC or UPC found');
                return Promise.reject(new Error('No valid old identifier'));
            }

            const results = await Promise.all(updatePromises);
            
            // Refresh the mismatch data
            await fetchArtistMismatchData(id, artistMismatchPagination.currentPage, artistMismatchPagination.itemsPerPage);
            
            // Update the selected item with new values
            setSelectedMismatchItem({
                ...selectedMismatchItem,
                asset_isrc: newIsrc || selectedMismatchItem.asset_isrc,
                product_upc: newUpc || selectedMismatchItem.product_upc
            });
            
            // Exit edit mode
            setEditingIsrcUpc(false);
            
            return Promise.resolve(results);
        } catch (error) {
            console.error('Error updating ISRC/UPC:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            const errorDetails = error?.response?.data?.errors ? JSON.stringify(error.response.data.errors, null, 2) : '';
            toast.error(`Error updating ISRC/UPC: ${errorMessage}${errorDetails ? '\n\n' + errorDetails : ''}`);
            return Promise.reject(error);
        }
    };

    // Handle mismatch submit - update all distributions with the same ISRC
    const handleMismatchSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedMismatchItem || !selectedMismatchArtistId) {
            toast.warning('Please select an artist');
            return;
        }

        // Use newIsrc/newUpc if provided, otherwise use current values
        const effectiveIsrc = (newIsrc && newIsrc.trim() !== '') ? newIsrc.trim() : (selectedMismatchItem.asset_isrc || '');
        const effectiveUpc = (newUpc && newUpc.trim() !== '') ? newUpc.trim() : (selectedMismatchItem.product_upc || '');
        
        // Check if ISRC or UPC contains AFR placeholder values
        const hasPlaceholderIsrc = effectiveIsrc && (
            effectiveIsrc === 'AFR_PLACEHOLDER_ISRC' || 
            effectiveIsrc.includes('AFR_PLACEHOLDER_ISRC') ||
            effectiveIsrc === 'N/A' || 
            !effectiveIsrc || 
            effectiveIsrc.trim() === ''
        );
        
        const hasPlaceholderUpc = effectiveUpc && (
            effectiveUpc === 'AFR_PLACEHOLDER_PRODUCT' || 
            effectiveUpc.includes('AFR_PLACEHOLDER_PRODUCT') ||
            effectiveUpc === 'N/A' || 
            !effectiveUpc || 
            effectiveUpc.trim() === ''
        );
        
        // If both ISRC and UPC are placeholders or invalid, prevent linking
        if (hasPlaceholderIsrc && hasPlaceholderUpc) {
            toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
            return;
        }
        
        // If ISRC is placeholder but UPC is valid, check if we can use UPC
        if (hasPlaceholderIsrc && !hasPlaceholderUpc) {
            // Use UPC instead, but warn user
            if (!window.confirm('ISRC is a placeholder, but UPC is available. Continue using UPC to link with the correct artist?')) {
                return;
            }
        }
        
        // If UPC is placeholder but ISRC is valid, use ISRC
        if (hasPlaceholderUpc && !hasPlaceholderIsrc) {
            // Use ISRC, this is fine
        }
        
        // If both are valid, use ISRC (preferred)
        if (!hasPlaceholderIsrc && !hasPlaceholderUpc) {
            // Both are valid, use ISRC
        }

        try {
            // Get the selected artist name
            const selectedArtist = artists.find(a => a.id === selectedMismatchArtistId);
            if (!selectedArtist) {
                toast.error('Selected artist not found');
                return;
            }

            // The backend updates by ISRC/UPC, not by distribution_id
            // It expects distribution_type to be: 'all', 'title', 'dsp', 'country', or 'typesale'
            // Since we want to update ALL distributions with the same ISRC, we use 'all'
            const payload = {
                artist_id: selectedMismatchArtistId,
                distribution_type: 'all' // Update all distribution types (title, dsp, country, typesale)
            };

            // Only include ISRC if it's not a placeholder
            if (!hasPlaceholderIsrc && effectiveIsrc && effectiveIsrc !== 'N/A') {
                payload.isrc = effectiveIsrc;
            }
            
            // Only include UPC if it's not a placeholder and ISRC is not available
            if (!hasPlaceholderUpc && effectiveUpc && effectiveUpc !== 'N/A') {
                payload.upc = effectiveUpc;
            }
            
            // Validate that we have at least one valid identifier
            if (!payload.isrc && !payload.upc) {
                toast.error('Cannot link with correct artist: Both ISRC and UPC are placeholders or invalid. Please update the ISRC or UPC first before linking with the correct artist.');
                return;
            }

            console.log('Updating distributions with payload:', payload);

            // Send single update request - backend will update all matching distributions
            const response = await updateDistributionMutation.mutateAsync(payload);

            // Check if update was successful (status 200)
            if (response?.success || (response?.status === 200 || response?.statusCode === 200)) {
                // Refetch the report data to update the JSON and remove items that were linked to AFR
                // The backend will automatically update the report JSON when distributions are updated
                console.log('Update successful, refetching report data...');
                await refetch();

                // Refresh the mismatch data to reflect the changes
                await fetchArtistMismatchData(id, artistMismatchPagination.currentPage, artistMismatchPagination.itemsPerPage);
                
                // Close the modal
                toggleMismatchModal();
                
                // Show success message with details from response
                const updatedCount = response?.updated_records?.total || 0;
                const message = response?.message || `Successfully updated distributions with ${effectiveIsrc ? 'ISRC ' + effectiveIsrc : 'UPC ' + effectiveUpc} to artist: ${selectedArtist.artist_name}`;
                toast.success(`${message}. Updated ${updatedCount} record(s) across all distribution types. The report has been refreshed to reflect the changes.`);
            } else {
                throw new Error('Update was not successful');
            }
            
            } catch (error) {
                console.error('Error updating mismatch distributions:', error);
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                const errorDetails = error?.response?.data?.errors ? JSON.stringify(error.response.data.errors, null, 2) : '';
                toast.error(`Error updating distributions: ${errorMessage}${errorDetails ? '\n\n' + errorDetails : ''}`);
            }
    };

    // Deep comparison utility function
    const deepCompare = useCallback((obj1, obj2, path = '') => {
        if (obj1 === obj2) return { isEqual: true, differences: [] };
        
        if (obj1 === null || obj2 === null) {
            return {
                isEqual: obj1 === obj2,
                differences: obj1 !== obj2 ? [{ path, value1: obj1, value2: obj2 }] : []
            };
        }
        
        if (typeof obj1 !== typeof obj2) {
            return {
                isEqual: false,
                differences: [{ path, value1: obj1, value2: obj2 }]
            };
        }
        
        if (typeof obj1 !== 'object' || obj1 instanceof Date) {
            // For numbers, allow small floating point differences (especially for revenue)
            if (typeof obj1 === 'number' && typeof obj2 === 'number') {
                const isEqual = Math.abs(obj1 - obj2) < 0.01; // Allow 0.01 difference for revenue
                return {
                    isEqual,
                    differences: !isEqual ? [{ path, value1: obj1, value2: obj2 }] : []
                };
            }
            return {
                isEqual: obj1 === obj2,
                differences: obj1 !== obj2 ? [{ path, value1: obj1, value2: obj2 }] : []
            };
        }
        
        if (Array.isArray(obj1) !== Array.isArray(obj2)) {
            return {
                isEqual: false,
                differences: [{ path, value1: obj1, value2: obj2 }]
            };
        }
        
        const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
        const differences = [];
        let allEqual = true;
        
        for (const key of allKeys) {
            const currentPath = path ? `${path}.${key}` : key;
            const val1 = obj1?.[key];
            const val2 = obj2?.[key];
            
            if (!(key in (obj1 || {}))) {
                differences.push({ path: currentPath, value1: undefined, value2: val2 });
                allEqual = false;
            } else if (!(key in (obj2 || {}))) {
                differences.push({ path: currentPath, value1: val1, value2: undefined });
                allEqual = false;
            } else {
                const result = deepCompare(val1, val2, currentPath);
                if (!result.isEqual) {
                    differences.push(...result.differences);
                    allEqual = false;
                }
            }
        }
        
        return { isEqual: allEqual, differences };
    }, []);

    // Extract and normalize backend API data structure
    // Backend API now returns: {success: true, data: {database_data: {...}, s3_data: {...}}}
    const backendProcessingReportData = useMemo(() => {
        if (!report) return null;
        
        // Use database_data from the new API structure
        const dbData = report.database_data || report;
        const dbStats = dbData.statistics || {};
        const dbArtistInfo = dbData.artist_info || {};
        const dbTopAnalysis = dbData.top_analysis || {};
        const titleMetricsLookup = buildTopTitleMetricsLookup(report);
        const dspMetricsLookup = buildNamedMetricsLookup(report, 'dsps');
        const countryMetricsLookup = buildNamedMetricsLookup(report, 'countries');
        const saleTypeMetricsLookup = buildNamedMetricsLookup(report, 'sale_types');

        // Each distribution breakdown carries the full file revenue (Lambda/S3 semantics).
        const distribution = dbStats.distribution || {};
        const fileTotalRevenue =
            distribution.total_revenue ??
            distribution.title_revenue ??
            dbStats.database_totals?.total_revenue ??
            dbArtistInfo.total_revenue ??
            null;

        const pickBreakdownRevenue = (field) => {
            const raw = distribution[field];
            const parsed = raw !== null && raw !== undefined && raw !== ''
                ? parseFloat(String(raw).replace(/,/g, ''))
                : NaN;
            if (!Number.isNaN(parsed) && parsed > 0) {
                return raw;
            }
            return fileTotalRevenue;
        };

        const mapTopTitleItem = (item) => {
            const metrics = resolveTitleMetrics(item, titleMetricsLookup);
            return {
                name: item.name,
                isrc: item.isrc || item.asset_isrc || null,
                revenue: item.revenue,
                streams: metrics.streams,
                downloads: metrics.downloads,
                count: item.count,
                percentage: item.percentage,
                revenue_percentage: item.revenue_percentage
            };
        };

        const mapTopDspItem = (item) => {
            const metrics = resolveNamedMetrics(item, dspMetricsLookup);
            return {
                name: item.name,
                streams: metrics.streams,
                downloads: metrics.downloads,
                revenue: item.revenue,
                titles_count: item.titles_count ?? item.count ?? null,
                count: item.count,
                percentage: item.percentage,
                revenue_percentage: item.revenue_percentage
            };
        };

        const mapTopCountryItem = (item) => {
            const metrics = resolveNamedMetrics(item, countryMetricsLookup);
            return {
                name: item.name,
                streams: metrics.streams,
                downloads: metrics.downloads,
                revenue: item.revenue,
                titles_count: item.titles_count ?? item.count ?? null,
                count: item.count,
                percentage: item.percentage,
                revenue_percentage: item.revenue_percentage
            };
        };

        const mapSaleTypeItem = (item) => {
            const metrics = resolveNamedMetrics(item, saleTypeMetricsLookup);
            return {
                name: item.name,
                streams: metrics.streams,
                downloads: metrics.downloads,
                revenue: item.revenue,
                total_units: metrics.total_units ?? item.total_units ?? null,
                streams_percentage: metrics.streams_percentage ?? item.streams_percentage ?? null,
                downloads_percentage: metrics.downloads_percentage ?? item.downloads_percentage ?? null,
                count: item.count,
                percentage: item.percentage,
                revenue_percentage: item.revenue_percentage
            };
        };
        
        // Backend API structure has data nested in fields like statistics, top_titles, etc.
        // We need to transform it to match S3 JSON structure for comparison
        const transformed = {
            processing_metadata: {
                total_rows: dbStats.total_rows,
                processed_rows: dbStats.processed_rows,
                error_count: dbStats.error_entries || 0,
                chunks_processed: null, // Not in backend API
                unique_isrcs: null, // Not in backend API
                total_records: dbStats.saved_entries,
                processing_date: report.created_at,
                total_revenue: dbArtistInfo.total_revenue || dbStats.database_totals?.total_revenue,
                total_streams: dbStats.database_totals?.total_streams || null,
                total_downloads: dbStats.database_totals?.total_downloads || null,
                average_revenue_per_title: dbArtistInfo.average_revenue_per_entry,
                processing_times: {} // Not in backend API
            },
            distribution_counts: {
                title_distribution: dbStats.distribution?.saving_title_distribution,
                dsp_distribution: dbStats.distribution?.saving_dsp_distribution,
                country_distribution: dbStats.distribution?.saving_country_distribution,
                typesale_distribution: dbStats.distribution?.saving_typesale_distribution,
                total: distribution.total,
                // Revenue: prefer API field, fall back to file total when per-table sum is 0/missing
                title_revenue: pickBreakdownRevenue('title_revenue'),
                dsp_revenue: pickBreakdownRevenue('dsp_revenue'),
                country_revenue: pickBreakdownRevenue('country_revenue'),
                typesale_revenue: pickBreakdownRevenue('typesale_revenue'),
                total_revenue: fileTotalRevenue ?? distribution.total_revenue
            },
            top_analysis: {
                top_titles: (dbTopAnalysis.top_titles || dbData.top_titles)?.map(mapTopTitleItem) || [],
                top_dsps: (dbTopAnalysis.top_dsps || dbData.top_dsps)?.map(mapTopDspItem) || [],
                top_countries: (dbTopAnalysis.top_countries || dbData.top_countries)?.map(mapTopCountryItem) || [],
                top_artists: ((dbTopAnalysis.top_artists && dbTopAnalysis.top_artists.length > 0)
                    ? dbTopAnalysis.top_artists.map(item => ({
                        name: item.name,
                        revenue: item.revenue,
                        streams: item.streams ?? null,
                        downloads: item.downloads ?? null,
                        titles_count: item.titles_count || item.count || null,
                        isrcs_count: item.isrcs_count || null,
                        count: item.count,
                        percentage: item.percentage,
                        revenue_percentage: item.revenue_percentage
                    }))
                    : (dbData.top_artists && dbData.top_artists.length > 0)
                    ? dbData.top_artists.map(item => ({
                        name: item.name,
                        revenue: item.revenue,
                        streams: item.streams || null,
                        downloads: item.downloads || null,
                        titles_count: item.titles_count || item.count || null,
                        isrcs_count: item.isrcs_count || null,
                        count: item.count,
                        percentage: item.percentage,
                        revenue_percentage: item.revenue_percentage
                    }))
                    : (report.s3_data?.top_artists && report.s3_data.top_artists.length > 0)
                        ? report.s3_data.top_artists.map(item => ({
                            name: item.name,
                            revenue: item.revenue,
                            streams: null, // Not in s3_data.top_artists
                            downloads: null, // Not in s3_data.top_artists
                            titles_count: item.count || null,
                            isrcs_count: null,
                            count: item.count,
                            percentage: item.percentage,
                            revenue_percentage: item.revenue_percentage
                        }))
                        : (report.s3_data?.top_analysis?.top_artists && report.s3_data.top_analysis.top_artists.length > 0)
                            ? report.s3_data.top_analysis.top_artists.map(item => ({
                                name: item.name,
                                revenue: item.revenue,
                                streams: item.streams || null,
                                downloads: item.downloads || null,
                                titles_count: item.titles_count || null,
                                isrcs_count: item.isrcs_count || null,
                                count: item.titles_count || null,
                                percentage: null,
                                revenue_percentage: null
                            }))
                            : []),
                sale_types: (dbTopAnalysis.sale_types || dbData.sale_types)?.map(mapSaleTypeItem) || []
            },
            data_quality: null, // Not in backend API
            breakdown_counts: null, // Not in backend API
            artist_distributions: [] // Not in backend API
        };
        
        return transformed;
    }, [report]);

    // Normalize S3 JSON data for comparison (it already has the right structure)
    const normalizeS3Data = useCallback((data) => {
        if (!data) return null;
        return data;
    }, []);

    // Compare the two data sources
    const comparisonResult = useMemo(() => {
        const backendData = backendProcessingReportData; // Already normalized
        const s3Data = normalizeS3Data(processingReportData);
        
        if (!backendData && !s3Data) {
            return {
                isEqual: true,
                differences: []
            };
        }
        if (!backendData || !s3Data) {
            return {
                isEqual: false,
                differences: [{ path: 'root', value1: backendData ? 'Backend data exists' : 'Missing', value2: s3Data ? 'S3 data exists' : 'Missing' }]
            };
        }
        
        const result = deepCompare(backendData, s3Data);
        // Ensure result always has a differences array
        return {
            isEqual: result?.isEqual || false,
            differences: Array.isArray(result?.differences) ? result.differences : []
        };
    }, [backendProcessingReportData, processingReportData, normalizeS3Data, deepCompare]);


    // Helper function to check if a path matches any difference
    const getComparisonStatus = useCallback((dataPath) => {
        if (!comparisonResult || comparisonResult.isEqual) return 'match';
        
        const differences = comparisonResult.differences;
        if (!Array.isArray(differences)) return 'match';
        
        const pathStr = String(dataPath);
        const hasDifference = differences.some(diff => 
            diff?.path && (pathStr.includes(diff.path) || diff.path.includes(pathStr))
        );
        
        return hasDifference ? 'mismatch' : 'match';
    }, [comparisonResult]);

    // Helper function to get value with comparison styling
    const renderComparisonValue = useCallback((value, path) => {
        const status = getComparisonStatus(path);
        const bgColor = status === 'match' ? '#d4edda' : '#f8d7da';
        const textColor = status === 'match' ? '#155724' : '#721c24';
        
        if (value === null || value === undefined) {
            return <span style={{ backgroundColor: bgColor, color: textColor, padding: '2px 6px', borderRadius: '3px' }}>N/A</span>;
        }
        
        if (typeof value === 'object') {
            return (
                <pre style={{ backgroundColor: bgColor, color: textColor, padding: '8px', borderRadius: '3px', margin: 0, fontSize: '12px' }}>
                    {JSON.stringify(value, null, 2)}
                </pre>
            );
        }
        
        return (
            <span style={{ backgroundColor: bgColor, color: textColor, padding: '2px 6px', borderRadius: '3px' }}>
                {typeof value === 'number' ? value.toLocaleString() : String(value)}
            </span>
        );
    }, [getComparisonStatus]);

    // Optimized memoized data processing
    const processedData = useMemo(() => {
        if (!processingReportData) return null;
        
        // S3 data structure from the new API
        const s3Distribution = processingReportData.distribution_counts || {};
        
        return {
            metadata: processingReportData.processing_metadata,
            distribution: {
                ...s3Distribution,
                // Include revenue fields from S3 data
                title_revenue: s3Distribution.title_revenue || '0',
                dsp_revenue: s3Distribution.dsp_revenue || '0',
                country_revenue: s3Distribution.country_revenue || '0',
                typesale_revenue: s3Distribution.typesale_revenue || '0',
                total_revenue: s3Distribution.totaltotal_revenue || s3Distribution.total_revenue || '0'
            },
            topAnalysis: processingReportData.top_analysis,
            dataQuality: processingReportData.data_quality,
            breakdown: processingReportData.breakdown_counts,
            processingTimes: processingReportData.processing_metadata?.processing_times || {},
            artistDistributions: processingReportData.artist_distributions || []
        };
    }, [processingReportData]);

    // Process backend data for display
    const processedBackendData = useMemo(() => {
        if (!backendProcessingReportData) return null;
        
        return {
            metadata: backendProcessingReportData.processing_metadata,
            distribution: backendProcessingReportData.distribution_counts,
            topAnalysis: backendProcessingReportData.top_analysis,
            dataQuality: backendProcessingReportData.data_quality,
            breakdown: backendProcessingReportData.breakdown_counts,
            processingTimes: backendProcessingReportData.processing_metadata?.processing_times || {},
            artistDistributions: backendProcessingReportData.artist_distributions || []
        };
    }, [backendProcessingReportData]);

    // Calculate comparison score (out of 10) based on key metrics
    // Must be defined after processedBackendData and processedData
    const comparisonScore = useMemo(() => {
        let score = 0;
        const totalMetrics = 4;
        
        // 1. Total Records comparison (2.5 points)
        const breakdownTotal = (processedData?.breakdown?.total_records || 0) + 
                               (processedData?.breakdown?.dsp_records || 0) + 
                               (processedData?.breakdown?.territory_records || 0) + 
                               (processedData?.breakdown?.sale_type_records || 0);
        const backendTotalRecords = processedBackendData?.metadata?.total_records || 0;
        if (breakdownTotal > 0 && backendTotalRecords > 0 && breakdownTotal === backendTotalRecords) {
            score += 2.5;
        }
        
        // 2. Total Revenue comparison (2.5 points)
        const backendRevenueRaw = reportStatistics?.database_totals?.total_revenue || processedBackendData?.metadata?.total_revenue || null;
        const s3RevenueRaw = processedData?.metadata?.total_revenue || null;
        const backendRevenue = backendRevenueRaw !== null ? Number(backendRevenueRaw) : null;
        const s3Revenue = s3RevenueRaw !== null ? Number(s3RevenueRaw) : null;
        if (backendRevenue !== null && s3Revenue !== null && !isNaN(backendRevenue) && !isNaN(s3Revenue) && Math.abs(backendRevenue - s3Revenue) < 0.01) {
            score += 2.5;
        }
        
        // 3. Total Streams comparison (2.5 points)
        const backendStreams = reportStatistics?.database_totals?.total_streams || processedBackendData?.metadata?.total_streams || null;
        const s3Streams = processedData?.metadata?.total_streams || null;
        if (backendStreams !== null && s3Streams !== null && backendStreams === s3Streams) {
            score += 2.5;
        }
        
        // 4. Total Downloads comparison (2.5 points)
        const backendDownloads = reportStatistics?.database_totals?.total_downloads || processedBackendData?.metadata?.total_downloads || null;
        const s3Downloads = processedData?.metadata?.total_downloads || null;
        if (backendDownloads !== null && s3Downloads !== null && backendDownloads === s3Downloads) {
            score += 2.5;
        }
        
        return {
            score: Math.round(score),
            total: 10,
            percentage: Math.round((score / 10) * 100)
        };
    }, [report, processedBackendData, processedData]);

    // Memoized table data for virtual scrolling
    const topTitlesData = useMemo(() => {
        const titles = processedData?.topAnalysis?.top_titles;
        return Array.isArray(titles) ? titles.slice(0, visibleRows) : [];
    }, [processedData?.topAnalysis?.top_titles, visibleRows]);

    const topDSPsData = useMemo(() => {
        const dsps = processedData?.topAnalysis?.top_dsps;
        return Array.isArray(dsps) ? dsps.slice(0, visibleRows) : [];
    }, [processedData?.topAnalysis?.top_dsps, visibleRows]);

    const topCountriesData = useMemo(() => {
        const countries = processedData?.topAnalysis?.top_countries;
        return Array.isArray(countries) ? countries.slice(0, visibleRows) : [];
    }, [processedData?.topAnalysis?.top_countries, visibleRows]);

    const topArtistsData = useMemo(() => {
        const artists = processedData?.topAnalysis?.top_artists;
        return Array.isArray(artists) ? artists.slice(0, visibleRows) : [];
    }, [processedData?.topAnalysis?.top_artists, visibleRows]);

    // Helper function to calculate missing records for distribution types
    const calculateMissingRecords = useCallback((distributionType, backendValue, s3Value) => {
        // Missing records = S3 value (expected) - Backend value (actual saved)
        // Only show missing if S3 value is greater than backend value
        if (s3Value === null || s3Value === undefined || backendValue === null || backendValue === undefined) {
            return null;
        }
        
        const s3Count = typeof s3Value === 'number' ? s3Value : 0;
        const backendCount = typeof backendValue === 'number' ? backendValue : 0;
        
        // Missing records = expected (S3) - actual (Backend)
        const missing = s3Count - backendCount;
        
        // Only return missing if there are actually missing records (S3 > Backend)
        return missing > 0 ? missing : 0;
    }, [reportStatistics]);

    // Helper function to compare values (count or revenue)
    const compareValues = useCallback((val1, val2, tolerance = 0.01) => {
        if (val1 === null || val1 === undefined || val2 === null || val2 === undefined) return false;
        
        const num1 = typeof val1 === 'string' ? parseFloat(val1) : val1;
        const num2 = typeof val2 === 'string' ? parseFloat(val2) : val2;
        
        if (isNaN(num1) || isNaN(num2)) return false;
        
        // For revenue, use tolerance for floating point comparison
        return Math.abs(num1 - num2) < tolerance;
    }, []);

    // Row tone for Backend vs S3 comparison tables
    const getCompareRowTone = useCallback((backendValue, s3Value, { tolerance = 0.01, forceInfo = false } = {}) => {
        if (forceInfo) return 'info';
        const backendMissing = backendValue === null || backendValue === undefined;
        const s3Missing = s3Value === null || s3Value === undefined;
        if (backendMissing && s3Missing) return 'neutral';
        if (backendMissing || s3Missing) return 'neutral';

        const backendNum = typeof backendValue === 'string' ? parseFloat(backendValue) : backendValue;
        const s3Num = typeof s3Value === 'string' ? parseFloat(s3Value) : s3Value;
        const bothNumeric = typeof backendNum === 'number' && typeof s3Num === 'number' && !Number.isNaN(backendNum) && !Number.isNaN(s3Num);

        if (bothNumeric) {
            if (compareValues(backendValue, s3Value, tolerance)) return 'match';
            return 'mismatch';
        }

        if (String(backendValue) === String(s3Value)) return 'match';
        return 'mismatch';
    }, [compareValues]);

    // Helper function to render comparison table rows
    const renderComparisonRow = useCallback((label, backendValue, s3Value, path, showMissingRecords = false, distributionType = null, revenue = null, s3Revenue = null, forceBlue = false) => {
        const status = getComparisonStatus(path);
        
        let rowTone = 'neutral';
        
        // If forceBlue is true, always use info styling regardless of match status
        if (forceBlue) {
            rowTone = 'info';
        } else {
        
        // Check if we should compare revenue (for Total row or when showMissingRecords is true)
        const shouldCompareRevenue = (revenue !== null && s3Revenue !== null) && (showMissingRecords || label === 'Total');
        
        if (shouldCompareRevenue) {
            // Compare count values
            const countMatch = compareValues(backendValue, s3Value);
            
            // Compare revenue values (with tolerance for floating point)
            const revenueMatch = compareValues(revenue, s3Revenue, 0.01);
            
            // Check if values are "near" (close but not exact match)
            // For revenue: within 5% difference
            // For count: within 1% difference
            let revenueNear = false;
            let countNear = false;
            
            if (revenue !== null && s3Revenue !== null) {
                const rev1 = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
                const rev2 = typeof s3Revenue === 'string' ? parseFloat(s3Revenue) : s3Revenue;
                if (!isNaN(rev1) && !isNaN(rev2) && rev1 > 0 && rev2 > 0) {
                    const revenueDiff = Math.abs(rev1 - rev2);
                    const revenueAvg = (rev1 + rev2) / 2;
                    const revenuePercentDiff = (revenueDiff / revenueAvg) * 100;
                    revenueNear = !revenueMatch && revenuePercentDiff <= 5; // Within 5%
                }
            }
            
            if (backendValue !== null && s3Value !== null) {
                const count1 = typeof backendValue === 'string' ? parseFloat(backendValue) : backendValue;
                const count2 = typeof s3Value === 'string' ? parseFloat(s3Value) : s3Value;
                if (!isNaN(count1) && !isNaN(count2) && count1 > 0 && count2 > 0) {
                    const countDiff = Math.abs(count1 - count2);
                    const countAvg = (count1 + count2) / 2;
                    const countPercentDiff = (countDiff / countAvg) * 100;
                    countNear = !countMatch && countPercentDiff <= 1; // Within 1%
                }
            }
            
            // Determine color based on matches
            if (countMatch && revenueMatch) {
                rowTone = 'match';
            } else if (countMatch || revenueMatch || countNear || revenueNear) {
                rowTone = 'neutral';
            } else {
                rowTone = 'mismatch';
            }
        } else {
            // Compare numeric values directly (e.g. Error Count, Unique ISRCs)
            rowTone = getCompareRowTone(backendValue, s3Value);
            // Fall back to path diff only when values can't be compared
            if (rowTone === 'neutral' && status === 'mismatch') {
                rowTone = 'mismatch';
            }
        }
        }
        
        const formatValue = (val) => {
            if (val === null || val === undefined) return 'N/A';
            if (typeof val === 'object' && !Array.isArray(val)) {
                return JSON.stringify(val, null, 2);
            }
            if (Array.isArray(val)) {
                return `[${val.length} items]`;
            }
            if (typeof val === 'number') {
                return val.toLocaleString();
            }
            return String(val);
        };
        
        const formatRevenue = (rev, showZero = false) => {
            // If showZero is true, always show revenue (even if 0)
            if (showZero) {
                const revenueNum = typeof rev === 'string' ? parseFloat(rev) : (rev || 0);
                if (isNaN(revenueNum)) return '0.00';
                return revenueNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            // Otherwise, only show if revenue exists
            if (!rev && rev !== 0) return null;
            const revenueNum = typeof rev === 'string' ? parseFloat(rev) : rev;
            if (isNaN(revenueNum)) return null;
            return revenueNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        
        // Calculate missing records by comparing S3 (expected) vs Backend (actual saved)
        const missingRecords = showMissingRecords ? calculateMissingRecords(distributionType, backendValue, s3Value) : null;
        const formattedRevenue = revenue !== null && revenue !== undefined
            ? formatRevenue(revenue, showMissingRecords)
            : null;
        // Always show S3 revenue (even if 0) as requested
        const formattedS3Revenue = formatRevenue(s3Revenue, true);
        
        return (
            <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                <th>{label}</th>
                <td>
                    <div>
                        <div style={{ marginBottom: formattedRevenue ? '4px' : '0' }}>
                            <strong>{formatValue(backendValue)}</strong>
                            {formattedRevenue && (
                                <span style={{ display: 'block', fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    💰 Revenue: {formattedRevenue} {report?.provider_currency || 'EUR'}
                                </span>
                            )}
                        </div>
                        {showMissingRecords && missingRecords !== null && missingRecords > 0 && (
                            <div className="alert alert-warning small mt-2 mb-0" style={{ fontSize: '11px', padding: '6px' }}>
                                ⚠️ <strong>Missing {missingRecords.toLocaleString()} records</strong> in database
                            </div>
                        )}
                    </div>
                </td>
                <td>
                    <div>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>{formatValue(s3Value)}</strong>
                            {formattedS3Revenue && (
                                <span style={{ display: 'block', fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    💰 Revenue: {formattedS3Revenue} {report?.provider_currency || 'EUR'}
                                </span>
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    }, [getComparisonStatus, calculateMissingRecords, report, compareValues, getCompareRowTone]);

    // Helper function to render comparison table for arrays
    const renderArrayComparisonTable = useCallback((title, backendArray, s3Array, pathPrefix, columns) => {
        // Ensure both are arrays
        const safeBackendArray = Array.isArray(backendArray) ? backendArray : [];
        const safeS3Array = Array.isArray(s3Array) ? s3Array : [];
        
        // Create maps for matching by name (and ISRC for titles)
        const backendMapByName = new Map();
        const s3MapByName = new Map();
        const backendMapByIsrc = new Map();
        const s3MapByIsrc = new Map();
        const allNames = new Set();
        
        safeBackendArray.forEach((item, idx) => {
            const name = item?.name;
            if (name) {
                backendMapByName.set(name, item);
                allNames.add(name);
            }
            // For titles, also index by ISRC
            if (pathPrefix.includes('top_titles') && item?.isrc) {
                backendMapByIsrc.set(item.isrc, item);
            }
        });
        
        safeS3Array.forEach((item, idx) => {
            const name = item?.name;
            if (name) {
                s3MapByName.set(name, item);
                allNames.add(name);
            }
            // For titles, also index by ISRC
            if (pathPrefix.includes('top_titles') && item?.isrc) {
                s3MapByIsrc.set(item.isrc, item);
            }
        });
        
        // Build matched rows - prioritize name matching, then ISRC for titles
        const matchedRows = [];
        const processedBackendIndices = new Set();
        const processedS3Indices = new Set();
        
        // First, match by name
        allNames.forEach(name => {
            const backendItem = backendMapByName.get(name);
            const s3Item = s3MapByName.get(name);
            
            if (backendItem || s3Item) {
                matchedRows.push({
                    name,
                    backendItem,
                    s3Item,
                    matchKey: name
                });
                
                if (backendItem) {
                    const idx = safeBackendArray.indexOf(backendItem);
                    if (idx !== -1) processedBackendIndices.add(idx);
                }
                if (s3Item) {
                    const idx = safeS3Array.indexOf(s3Item);
                    if (idx !== -1) processedS3Indices.add(idx);
                }
            }
        });
        
        // For titles, also try ISRC matching for items not matched by name
        if (pathPrefix.includes('top_titles')) {
            backendMapByIsrc.forEach((backendItem, isrc) => {
                if (backendItem && !processedBackendIndices.has(safeBackendArray.indexOf(backendItem))) {
                    const s3Item = s3MapByIsrc.get(isrc);
                    if (s3Item && !processedS3Indices.has(safeS3Array.indexOf(s3Item))) {
                        matchedRows.push({
                            name: backendItem.name || s3Item.name || `ISRC: ${isrc}`,
                            backendItem,
                            s3Item,
                            matchKey: isrc
                        });
                        
                        const backendIdx = safeBackendArray.indexOf(backendItem);
                        const s3Idx = safeS3Array.indexOf(s3Item);
                        if (backendIdx !== -1) processedBackendIndices.add(backendIdx);
                        if (s3Idx !== -1) processedS3Indices.add(s3Idx);
                    }
                }
            });
        }
        
        // Add unmatched items from backend
        safeBackendArray.forEach((item, idx) => {
            if (!processedBackendIndices.has(idx)) {
                matchedRows.push({
                    name: item?.name || `Item ${idx + 1}`,
                    backendItem: item,
                    s3Item: null,
                    matchKey: item?.name || idx
                });
            }
        });
        
        // Add unmatched items from S3
        safeS3Array.forEach((item, idx) => {
            if (!processedS3Indices.has(idx)) {
                matchedRows.push({
                    name: item?.name || `Item ${idx + 1}`,
                    backendItem: null,
                    s3Item: item,
                    matchKey: item?.name || idx
                });
            }
        });
        
        // For top_artists: Sort to put "Pending Review - PR" first, then limit to 10
        // For top_countries, top_dsps, and top_titles: Limit to 10 items
        let displayRows = matchedRows;
        if (pathPrefix.includes('top_artists')) {
            // Sort: "Pending Review - PR" first, then others
            displayRows = [...matchedRows].sort((a, b) => {
                const aName = a.backendItem?.name || a.s3Item?.name || '';
                const bName = b.backendItem?.name || b.s3Item?.name || '';
                const aIsPending = aName === 'Pending Review - PR';
                const bIsPending = bName === 'Pending Review - PR';
                
                if (aIsPending && !bIsPending) return -1;
                if (!aIsPending && bIsPending) return 1;
                return 0;
            });
            // Limit to 10 items
            displayRows = displayRows.slice(0, 10);
        } else if (pathPrefix.includes('top_countries') || pathPrefix.includes('top_dsps') || pathPrefix.includes('top_titles')) {
            // Limit to 10 items
            displayRows = matchedRows.slice(0, 10);
        }
        
        return (
            <div className="mb-4">
                <h6 className="mb-3">{title}</h6>
                <div className="table-responsive">
                    <Table bordered responsive size="sm">
                        <thead>
                            <tr>
                                <th style={{ backgroundColor: '#f8f9fa' }}>#</th>
                                {columns.map(col => (
                                    <th key={col.key} style={{ backgroundColor: '#f8f9fa' }}>{col.header}</th>
                                ))}
                                <th style={{ backgroundColor: '#e7f3ff' }}>Backend API Value</th>
                                <th style={{ backgroundColor: '#fff4e6' }}>S3 JSON Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((row, idx) => {
                                const itemPath = `${pathPrefix}[${row.matchKey}]`;
                                
                                // Check if this is "Pending Review - PR"
                                const artistName = row.backendItem?.name || row.s3Item?.name || '';
                                const isPendingReview = artistName === 'Pending Review - PR';
                                
                                // Determine if matched (both have items with same name or ISRC)
                                const isMatched = row.backendItem && row.s3Item && (
                                    (row.backendItem.name === row.s3Item.name) ||
                                    (pathPrefix.includes('top_titles') && row.backendItem.isrc && row.s3Item.isrc && row.backendItem.isrc === row.s3Item.isrc)
                                );
                                
                                // Set colors: blue for "Pending Review - PR", otherwise use match colors
                                let borderColor, bgColor;
                                if (isPendingReview) {
                                    borderColor = '#007bff';
                                    bgColor = '#e7f3ff';
                                } else {
                                    borderColor = isMatched ? '#c3e6cb' : '#f5c6cb';
                                    bgColor = isMatched ? '#d4edda' : '#f8d7da';
                                }
                                
                                return (
                                    <tr key={idx} style={{ 
                                        borderLeft: `4px solid ${borderColor}`,
                                        backgroundColor: bgColor
                                    }}>
                                        <td>{idx + 1}</td>
                                        {columns.map(col => (
                                            <td key={col.key}>
                                                {row.backendItem?.[col.key] || row.s3Item?.[col.key] || '-'}
                                            </td>
                                        ))}
                                        <td style={{ backgroundColor: bgColor }}>
                                            {pathPrefix.includes('sale_types') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.backendItem ? (
                                                        <>
                                                            <div><strong>Streams:</strong> {row.backendItem.streams !== null && row.backendItem.streams !== undefined ? row.backendItem.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.backendItem.downloads !== null && row.backendItem.downloads !== undefined ? row.backendItem.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.backendItem.revenue !== null && row.backendItem.revenue !== undefined ? `${Number(row.backendItem.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_artists') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.backendItem ? (
                                                        <>
                                                            <div><strong>Revenue:</strong> {row.backendItem.revenue !== null && row.backendItem.revenue !== undefined ? `${Number(row.backendItem.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Streams:</strong> {row.backendItem.streams !== null && row.backendItem.streams !== undefined ? row.backendItem.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.backendItem.downloads !== null && row.backendItem.downloads !== undefined ? row.backendItem.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Titles Count:</strong> {row.backendItem.titles_count !== null && row.backendItem.titles_count !== undefined ? row.backendItem.titles_count.toLocaleString() : 'null'}</div>
                                                            <div><strong>ISRCs Count:</strong> {row.backendItem.isrcs_count !== null && row.backendItem.isrcs_count !== undefined ? row.backendItem.isrcs_count.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue Percentage:</strong> {row.backendItem.revenue_percentage !== null && row.backendItem.revenue_percentage !== undefined ? `${Number(row.backendItem.revenue_percentage).toFixed(2)}%` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_countries') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.backendItem ? (
                                                        <>
                                                            <div><strong>Count:</strong> {row.backendItem.count !== null && row.backendItem.count !== undefined ? row.backendItem.count.toLocaleString() : 'null'}</div>
                                                            <div><strong>Streams:</strong> {row.backendItem.streams !== null && row.backendItem.streams !== undefined ? row.backendItem.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.backendItem.downloads !== null && row.backendItem.downloads !== undefined ? row.backendItem.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.backendItem.revenue !== null && row.backendItem.revenue !== undefined ? `${Number(row.backendItem.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Revenue Percentage:</strong> {row.backendItem.revenue_percentage !== null && row.backendItem.revenue_percentage !== undefined ? `${Number(row.backendItem.revenue_percentage).toFixed(2)}%` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_dsps') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.backendItem ? (
                                                        <>
                                                            <div><strong>Streams:</strong> {row.backendItem.streams !== null && row.backendItem.streams !== undefined ? row.backendItem.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.backendItem.downloads !== null && row.backendItem.downloads !== undefined ? row.backendItem.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.backendItem.revenue !== null && row.backendItem.revenue !== undefined ? `${Number(row.backendItem.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Titles Count:</strong> {row.backendItem.titles_count !== null && row.backendItem.titles_count !== undefined ? row.backendItem.titles_count.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue Percentage:</strong> {row.backendItem.revenue_percentage !== null && row.backendItem.revenue_percentage !== undefined ? `${Number(row.backendItem.revenue_percentage).toFixed(2)}%` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_titles') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.backendItem ? (
                                                        <>
                                                            <div><strong>Name:</strong> {row.backendItem.name || 'null'}</div>
                                                            <div><strong>ISRC:</strong> {row.backendItem.isrc || 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.backendItem.revenue !== null && row.backendItem.revenue !== undefined ? `${Number(row.backendItem.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Streams:</strong> {row.backendItem.streams !== null && row.backendItem.streams !== undefined ? row.backendItem.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.backendItem.downloads !== null && row.backendItem.downloads !== undefined ? row.backendItem.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue Percentage:</strong> {row.backendItem.revenue_percentage !== null && row.backendItem.revenue_percentage !== undefined ? `${Number(row.backendItem.revenue_percentage).toFixed(2)}%` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : (
                                            <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(row.backendItem || null, null, 2)}
                                            </pre>
                                            )}
                                        </td>
                                        <td style={{ backgroundColor: bgColor }}>
                                            {pathPrefix.includes('sale_types') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.s3Item ? (
                                                        <>
                                                            <div><strong>Streams:</strong> {row.s3Item.streams !== null && row.s3Item.streams !== undefined ? row.s3Item.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.s3Item.downloads !== null && row.s3Item.downloads !== undefined ? row.s3Item.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.s3Item.revenue !== null && row.s3Item.revenue !== undefined ? `${Number(row.s3Item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_artists') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.s3Item ? (
                                                        <>
                                                            <div><strong>Revenue:</strong> {row.s3Item.revenue !== null && row.s3Item.revenue !== undefined ? `${Number(row.s3Item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Streams:</strong> {row.s3Item.streams !== null && row.s3Item.streams !== undefined ? row.s3Item.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.s3Item.downloads !== null && row.s3Item.downloads !== undefined ? row.s3Item.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Titles Count:</strong> {row.s3Item.titles_count !== null && row.s3Item.titles_count !== undefined ? row.s3Item.titles_count.toLocaleString() : 'null'}</div>
                                                            <div><strong>ISRCs Count:</strong> {row.s3Item.isrcs_count !== null && row.s3Item.isrcs_count !== undefined ? row.s3Item.isrcs_count.toLocaleString() : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_countries') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.s3Item ? (
                                                        <>
                                                            <div><strong>Streams:</strong> {row.s3Item.streams !== null && row.s3Item.streams !== undefined ? row.s3Item.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.s3Item.downloads !== null && row.s3Item.downloads !== undefined ? row.s3Item.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.s3Item.revenue !== null && row.s3Item.revenue !== undefined ? `${Number(row.s3Item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Titles Count:</strong> {row.s3Item.titles_count !== null && row.s3Item.titles_count !== undefined ? row.s3Item.titles_count.toLocaleString() : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_dsps') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.s3Item ? (
                                                        <>
                                                            <div><strong>Streams:</strong> {row.s3Item.streams !== null && row.s3Item.streams !== undefined ? row.s3Item.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.s3Item.downloads !== null && row.s3Item.downloads !== undefined ? row.s3Item.downloads.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.s3Item.revenue !== null && row.s3Item.revenue !== undefined ? `${Number(row.s3Item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Titles Count:</strong> {row.s3Item.titles_count !== null && row.s3Item.titles_count !== undefined ? row.s3Item.titles_count.toLocaleString() : 'null'}</div>
                                                            <div><strong>Revenue Percentage:</strong> {row.s3Item.revenue_percentage !== null && row.s3Item.revenue_percentage !== undefined ? `${Number(row.s3Item.revenue_percentage).toFixed(2)}%` : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : pathPrefix.includes('top_titles') ? (
                                                <div style={{ fontSize: '12px' }}>
                                                    {row.s3Item ? (
                                                        <>
                                                            <div><strong>Name:</strong> {row.s3Item.name || 'null'}</div>
                                                            <div><strong>ISRC:</strong> {row.s3Item.isrc || 'null'}</div>
                                                            <div><strong>Revenue:</strong> {row.s3Item.revenue !== null && row.s3Item.revenue !== undefined ? `${Number(row.s3Item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` : 'null'}</div>
                                                            <div><strong>Streams:</strong> {row.s3Item.streams !== null && row.s3Item.streams !== undefined ? row.s3Item.streams.toLocaleString() : 'null'}</div>
                                                            <div><strong>Downloads:</strong> {row.s3Item.downloads !== null && row.s3Item.downloads !== undefined ? row.s3Item.downloads.toLocaleString() : 'null'}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ color: '#999' }}>N/A</div>
                                                    )}
                                                </div>
                                            ) : (
                                            <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(row.s3Item || null, null, 2)}
                                            </pre>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </div>
        );
    }, [getComparisonStatus]);

    // Toggle section collapse
    const toggleSection = useCallback((section) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    // Load more rows for virtual scrolling
    const loadMoreRows = useCallback(() => {
        setVisibleRows(prev => Math.min(prev + 10, 50)); // Max 50 rows
    }, []);

    // Debug logging (only in development)
    React.useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('Report data:', report);
            console.log('Processing report data:', processingReportData);
            console.log('Processed data:', processedData);
        }
    }, [report, processingReportData, processedData]);

    // Memoized table component for better performance
    const OptimizedTable = memo(({ data, columns, title, onLoadMore, hasMore }) => (
        <Card>
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{title}</h6>
                    {hasMore && (
                        <Button size="sm" color="primary" onClick={onLoadMore}>
                            Load More
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                <div className="table-responsive">
                    <Table striped size="sm">
                        <thead>
                            <tr>
                                {columns.map((col, index) => (
                                    <th key={index}>{col.header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index}>
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex}>
                                            {col.render ? col.render(item) : item[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    ));

    // Memoized collapsible section component
    const CollapsibleSection = memo(({ title, isCollapsed, onToggle, children, icon: Icon }) => (
        <div className={`pr-section ${!isCollapsed ? 'pr-section--open' : ''}`}>
            <div className="pr-section__toggle" onClick={onToggle} role="button" tabIndex={0}>
                <h6 className="d-flex align-items-center">
                    {Icon && <Icon className="me-2 pr-panel__icon" size={16} />}
                    {title}
                </h6>
                {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </div>
            <Collapse isOpen={!isCollapsed}>
                <div className="pr-section__body">{children}</div>
            </Collapse>
        </div>
    ));

    // Memoized JSON Editor component with Ace Editor
    const JsonEditor = memo(({ data, onDownload }) => {
        const jsonString = useMemo(() => JSON.stringify(data, null, 2), [data]);
        
        const themes = [
            { value: 'monokai', label: 'Monokai' },
            { value: 'github', label: 'GitHub' },
            { value: 'tomorrow', label: 'Tomorrow' },
            { value: 'twilight', label: 'Twilight' },
            { value: 'solarized_dark', label: 'Solarized Dark' },
            { value: 'solarized_light', label: 'Solarized Light' }
        ];

        const handleAceError = useCallback(() => {
            setAceError(true);
            setUseAceEditor(false);
        }, []);

        const toggleEditor = useCallback(() => {
            setUseAceEditor(!useAceEditor);
            setAceError(false);
        }, [useAceEditor]);

        return (
            <div className="json-editor-container">
                {/* Editor Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center">
                            <Label className="me-2 mb-0 small">Editor:</Label>
                            <Button
                                size="sm"
                                color={useAceEditor ? "primary" : "outline-secondary"}
                                onClick={toggleEditor}
                                disabled={aceError}
                            >
                                {useAceEditor ? 'Ace Editor' : 'Simple View'}
                            </Button>
                            {aceError && (
                                <small className="text-warning ms-2">
                                    Ace Editor failed, using fallback
                                </small>
                            )}
                        </div>
                        {useAceEditor && !aceError && (
                            <>
                                <div className="d-flex align-items-center">
                                    <Label className="me-2 mb-0 small">Theme:</Label>
                                    <Input
                                        type="select"
                                        size="sm"
                                        value={aceTheme}
                                        onChange={(e) => setAceTheme(e.target.value)}
                                        style={{ width: '150px' }}
                                    >
                                        {themes.map(theme => (
                                            <option key={theme.value} value={theme.value}>
                                                {theme.label}
                                            </option>
                                        ))}
                                    </Input>
                                </div>
                                <div className="d-flex align-items-center">
                                    <Label className="me-2 mb-0 small">Font Size:</Label>
                                    <Input
                                        type="select"
                                        size="sm"
                                        value={aceFontSize}
                                        onChange={(e) => setAceFontSize(parseInt(e.target.value))}
                                        style={{ width: '80px' }}
                                    >
                                        <option value={10}>10px</option>
                                        <option value={11}>11px</option>
                                        <option value={12}>12px</option>
                                        <option value={13}>13px</option>
                                        <option value={14}>14px</option>
                                        <option value={16}>16px</option>
                                    </Input>
                                </div>
                                <div className="form-check">
                                    <Input
                                        type="checkbox"
                                        id="wrapMode"
                                        checked={aceWrapMode}
                                        onChange={(e) => setAceWrapMode(e.target.checked)}
                                    />
                                    <Label className="form-check-label small" for="wrapMode">
                                        Word Wrap
                                    </Label>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">
                            {jsonString.length.toLocaleString()} characters
                        </small>
                        <Button 
                            size="sm" 
                            color="outline-primary"
                            onClick={onDownload}
                        >
                            <Download size={14} className="me-1" />
                            Download
                        </Button>
                    </div>
                </div>

                {/* Editor Content */}
                {useAceEditor && !aceError ? (
                    <div className="ace-editor-wrapper" style={{ border: '1px solid #dee2e6', borderRadius: '4px' }}>
                        {(() => {
                            try {
                                return (
                                    <AceEditor
                                        ref={aceEditorRef}
                                        mode="json"
                                        theme={aceTheme}
                                        value={jsonString}
                                        readOnly={true}
                                        fontSize={aceFontSize}
                                        width="100%"
                                        height="400px"
                                        setOptions={{
                                            enableBasicAutocompletion: false,
                                            enableLiveAutocompletion: false,
                                            enableSnippets: false,
                                            showLineNumbers: true,
                                            tabSize: 2,
                                            wrap: aceWrapMode,
                                            showGutter: true,
                                            highlightActiveLine: true,
                                            showPrintMargin: false,
                                            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                                            useWorker: false,
                                            useSoftTabs: true,
                                            behavioursEnabled: false,
                                            wrapBehavioursEnabled: false
                                        }}
                                        editorProps={{ 
                                            $blockScrolling: true,
                                            $useWorker: false
                                        }}
                                        onLoad={(editor) => {
                                            try {
                                                editor.renderer.setScrollMargin(10, 10, 0, 0);
                                                editor.renderer.setPadding(10);
                                                // Disable web workers
                                                editor.getSession().setUseWorker(false);
                                            } catch (error) {
                                                console.warn('Ace Editor onLoad error:', error);
                                                handleAceError();
                                            }
                                        }}
                                        onError={handleAceError}
                                    />
                                );
                            } catch (error) {
                                console.warn('Ace Editor render error:', error);
                                handleAceError();
                                return null;
                            }
                        })()}
                    </div>
                ) : (
                    <div className="json-viewer" style={{ 
                        border: '1px solid #dee2e6', 
                        borderRadius: '4px',
                        maxHeight: '400px',
                        overflow: 'auto',
                        fontSize: aceFontSize + 'px',
                        fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
                        lineHeight: '1.4',
                        padding: '15px',
                        backgroundColor: aceTheme === 'monokai' ? '#272822' : 
                                       aceTheme === 'github' ? '#ffffff' :
                                       aceTheme === 'tomorrow' ? '#1d1f21' :
                                       aceTheme === 'twilight' ? '#141414' :
                                       aceTheme === 'solarized_dark' ? '#002b36' :
                                       aceTheme === 'solarized_light' ? '#fdf6e3' : '#ffffff',
                        color: aceTheme === 'monokai' ? '#f8f8f2' : 
                               aceTheme === 'github' ? '#24292e' :
                               aceTheme === 'tomorrow' ? '#c5c8c6' :
                               aceTheme === 'twilight' ? '#f8f8f8' :
                               aceTheme === 'solarized_dark' ? '#839496' :
                               aceTheme === 'solarized_light' ? '#586e75' : '#24292e'
                    }}>
                        <pre style={{ margin: 0, whiteSpace: aceWrapMode ? 'pre-wrap' : 'pre' }}>
                            {jsonString}
                        </pre>
                    </div>
                )}
            </div>
        );
    });

    // Check if the report has the necessary data
    const hasValidReportData = report &&
        (reportStatistics ||
         dbData.artist_info ||
         dbData.top_artists ||
         dbData.top_titles ||
         dbData.top_dsps ||
         dbData.top_countries ||
         dbData.sale_types ||
         report.top_performing_assets ||
         report.artist_details);

    // Initialize Bootstrap tabs when the component mounts or when the report data changes
    useEffect(() => {
        if (report && report.various_artists_distributions) {
            // Make sure the DOM is ready before initializing tabs
            setTimeout(() => {
                try {
                    // Get all tab elements
                    const tabElements = document.querySelectorAll('#distribution-tabs button[data-bs-toggle="tab"]');

                    // Add click event listeners to each tab
                    tabElements.forEach(tab => {
                        tab.addEventListener('click', function(event) {
                            event.preventDefault();

                            // Remove active class from all tabs
                            tabElements.forEach(t => {
                                t.classList.remove('active');
                                t.setAttribute('aria-selected', 'false');
                            });

                            // Add active class to clicked tab
                            this.classList.add('active');
                            this.setAttribute('aria-selected', 'true');

                            // Get the target pane ID
                            const targetId = this.getAttribute('data-bs-target');

                            // Hide all tab panes
                            const tabPanes = document.querySelectorAll('.tab-pane');
                            tabPanes.forEach(pane => {
                                pane.classList.remove('show', 'active');
                            });

                            // Show the target pane
                            const targetPane = document.querySelector(targetId);
                            if (targetPane) {
                                targetPane.classList.add('show', 'active');
                            }
                        });
                    });

                    console.log('Tabs initialized successfully');
                } catch (error) {
                    console.error('Error initializing tabs:', error);
                }
            }, 500);
        }
    }, [report]);

    // Check if we have a server error (like 500)
    const isServerError = isError && (error?.response?.status === 500 || error?.message?.includes('500'));

    // Show a card with no data when receiving a 500 status code for non-existent IDs
    const showNoDataCard = isServerError || (status === 'error' && error?.response?.status === 500);

    // Fetch artists for the dropdown
    const fetchArtists = async () => {
        try {
            const response = await ProcessingReportService.getArtists();
            if (response && response.success && response.artists) {
                setArtists(response.artists);
                console.log('Artists fetched:', response.artists.length);
            } else {
                console.warn('Unexpected response format:', response);
            }
        } catch (error) {
            console.error('Error fetching artists:', error);
        }
    };

    // Load artists on component mount
    useEffect(() => {
        fetchArtists();
    }, []);

    // Function to open the edit modal with the selected item
    const openEditModal = (item) => {
        setSelectedItem(item);

        // Set the initial selected artist ID
        if (item.correct_product && item.correct_product.artist_id) {
            setSelectedArtistId(item.correct_product.artist_id);
        } else {
            // Default to "Unknown Artist" or another default ID
            setSelectedArtistId(null);
        }

        // Reset the artist filter
        setArtistFilter('');

        // Fetch artists when opening the modal
        fetchArtists();

        toggleModal();
    };

    return (
        <>
            {/* Edit Distribution Modal */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    Edit Distribution
                </ModalHeader>
                <ModalBody>
                    {selectedItem && (
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>UPC</Label>
                                        <Input type="text" value={selectedItem.product_upc} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Current Artist</Label>
                                        <Input type="text" value="Unknown Artist" disabled />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label>Search Artists</Label>
                                        <Input
                                            type="text"
                                            placeholder="Type to search artists..."
                                            value={artistFilter}
                                            onChange={(e) => setArtistFilter(e.target.value)}
                                            className="mb-2"
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Select Artist</Label>
                                        <Input
                                            type="select"
                                            value={selectedArtistId || ''}
                                            onChange={(e) => setSelectedArtistId(e.target.value ? parseInt(e.target.value) : null)}
                                            style={{ height: 'auto', maxHeight: '200px' }}
                                        >
                                            <option value="">Select an artist...</option>
                                            {filteredArtists.map(artist => (
                                                <option key={artist.id} value={artist.id}>
                                                    {artist.artist_name} (ID: {artist.id})
                                                </option>
                                            ))}
                                        </Input>
                                        {filteredArtists.length === 0 && (
                                            <div className="text-danger mt-1">No artists match your search</div>
                                        )}
                                        {artists.length > 0 && (
                                            <small className="form-text text-muted">
                                                Showing {filteredArtists.length} of {artists.length} artists
                                            </small>
                                        )}
                                        <div className="mt-2">
                                            <small className="form-text text-muted">
                                                {selectedItem.correct_product ?
                                                    <strong className="text-success">Recommended artist: {selectedItem.correct_product.artist_name} (ID: {selectedItem.correct_product.artist_id})</strong> :
                                                    "No recommended artist available"}
                                            </small>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup className="mt-3">
                                <Label className="d-block mb-2">Distribution Types to Update</Label>
                                <div className="distribution-types-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                    <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 15px', background: distributionTypes.all ? '#f8f9fa' : 'white' }}>
                                        <input
                                            type="checkbox"
                                            id="all"
                                            checked={distributionTypes.all}
                                            onChange={() => handleDistributionTypeChange('all')}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <Label for="all" style={{ margin: 0, cursor: 'pointer' }}>All Types</Label>
                                    </div>
                                    <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 15px', background: (distributionTypes.title || distributionTypes.all) ? '#f8f9fa' : 'white', opacity: distributionTypes.all ? 0.7 : 1 }}>
                                        <input
                                            type="checkbox"
                                            id="title"
                                            checked={distributionTypes.title || distributionTypes.all}
                                            onChange={() => handleDistributionTypeChange('title')}
                                            disabled={distributionTypes.all}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <Label for="title" style={{ margin: 0, cursor: 'pointer' }}>Title</Label>
                                    </div>
                                    <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 15px', background: (distributionTypes.dsp || distributionTypes.all) ? '#f8f9fa' : 'white', opacity: distributionTypes.all ? 0.7 : 1 }}>
                                        <input
                                            type="checkbox"
                                            id="dsp"
                                            checked={distributionTypes.dsp || distributionTypes.all}
                                            onChange={() => handleDistributionTypeChange('dsp')}
                                            disabled={distributionTypes.all}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <Label for="dsp" style={{ margin: 0, cursor: 'pointer' }}>DSP</Label>
                                    </div>
                                    <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 15px', background: (distributionTypes.country || distributionTypes.all) ? '#f8f9fa' : 'white', opacity: distributionTypes.all ? 0.7 : 1 }}>
                                        <input
                                            type="checkbox"
                                            id="country"
                                            checked={distributionTypes.country || distributionTypes.all}
                                            onChange={() => handleDistributionTypeChange('country')}
                                            disabled={distributionTypes.all}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <Label for="country" style={{ margin: 0, cursor: 'pointer' }}>Country</Label>
                                    </div>
                                    <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 15px', background: (distributionTypes.typesale || distributionTypes.all) ? '#f8f9fa' : 'white', opacity: distributionTypes.all ? 0.7 : 1 }}>
                                        <input
                                            type="checkbox"
                                            id="typesale"
                                            checked={distributionTypes.typesale || distributionTypes.all}
                                            onChange={() => handleDistributionTypeChange('typesale')}
                                            disabled={distributionTypes.all}
                                            style={{ marginRight: '8px', width: '18px', height: '18px' }}
                                        />
                                        <Label for="typesale" style={{ margin: 0, cursor: 'pointer' }}>Type Sale</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Form>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={updateDistributionMutation.isLoading}
                    >
                        {updateDistributionMutation.isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            <>
                                <Check size={14} className="me-1" /> Update Distribution
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* AFR Edit Modal */}
            <Modal isOpen={afrModal} toggle={toggleAfrModal} size="lg">
                <ModalHeader toggle={toggleAfrModal}>
                    Edit Artist For Review (AFR) Distribution
                </ModalHeader>
                <ModalBody>
                    {selectedAfrItem && (
                        <Form onSubmit={handleAfrSubmit}>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>UPC / ISRC</Label>
                                        <Input 
                                            type="text" 
                                            value={selectedAfrItem.product_upc || selectedAfrItem.asset_isrc || 'N/A'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Current Artist</Label>
                                        <Input 
                                            type="text" 
                                            value={selectedAfrItem.artist_name || 'Unknown Artist'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label>Search Artists</Label>
                                        <Input
                                            type="text"
                                            placeholder="Type to search artists..."
                                            value={artistFilter}
                                            onChange={(e) => setArtistFilter(e.target.value)}
                                            className="mb-2"
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Select Correct Artist</Label>
                                        <Input
                                            type="select"
                                            value={selectedArtistId || ''}
                                            onChange={(e) => setSelectedArtistId(e.target.value ? parseInt(e.target.value) : null)}
                                            style={{ height: 'auto', maxHeight: '200px' }}
                                        >
                                            <option value="">Select an artist...</option>
                                            {filteredArtists.map(artist => (
                                                <option key={artist.id} value={artist.id}>
                                                    {artist.artist_name} (ID: {artist.id})
                                                </option>
                                            ))}
                                        </Input>
                                        {filteredArtists.length === 0 && (
                                            <div className="text-danger mt-1">No artists match your search</div>
                                        )}
                                        {artists.length > 0 && (
                                            <small className="form-text text-muted">
                                                Showing {filteredArtists.length} of {artists.length} artists
                                            </small>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup className="mt-3">
                                <Label className="d-block mb-2">Distribution Type</Label>
                                <div className="alert alert-info">
                                    <strong>Type:</strong> {selectedAfrItem.afrType || 'All'}
                                </div>
                            </FormGroup>
                        </Form>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleAfrModal}>Cancel</Button>
                    <Button
                        color="primary"
                        onClick={handleAfrSubmit}
                        disabled={updateDistributionMutation.isLoading}
                    >
                        {updateDistributionMutation.isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            <>
                                <Check size={14} className="me-1" /> Update AFR Distribution
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Artist Mismatch Edit Modal - Unified Design */}
            <Modal isOpen={artistMismatchModal} toggle={toggleMismatchModal} size="lg">
                <ModalHeader toggle={toggleMismatchModal}>
                    <AlertCircle size={18} className="me-2" style={{ color: '#ffc107' }} />
                    Update Distribution & Link with Correct Artist
                </ModalHeader>
                <ModalBody>
                    {selectedMismatchItem && (() => {
                        const isrc = selectedMismatchItem.asset_isrc;
                        const upc = selectedMismatchItem.product_upc;
                        const hasPlaceholderIsrc = isrc && (
                            isrc === 'AFR_PLACEHOLDER_ISRC' || 
                            isrc.includes('AFR_PLACEHOLDER_ISRC') ||
                            isrc === 'N/A' || 
                            !isrc || 
                            isrc.trim() === ''
                        );
                        const hasPlaceholderUpc = upc && (
                            upc === 'AFR_PLACEHOLDER_PRODUCT' || 
                            upc.includes('AFR_PLACEHOLDER_PRODUCT') ||
                            upc === 'N/A' || 
                            !upc || 
                            upc.trim() === ''
                        );
                        const canLink = !(hasPlaceholderIsrc && hasPlaceholderUpc);
                        
                        // Check if ISRC/UPC values have changed
                        const isrcChanged = newIsrc && newIsrc.trim() !== '' && newIsrc.trim() !== (isrc || '');
                        const upcChanged = newUpc && newUpc.trim() !== '' && newUpc.trim() !== (upc || '');
                        const hasIsrcUpcChanges = isrcChanged || upcChanged;
                        
                        return (
                        <Form onSubmit={(e) => {
                            e.preventDefault();
                            // If ISRC/UPC changed, update them first, then link artist
                            if (hasIsrcUpcChanges) {
                                handleUpdateIsrcUpc(e).then(() => {
                                    // After ISRC/UPC update, if artist is selected, link it
                                    if (selectedMismatchArtistId) {
                                        setTimeout(() => {
                                            handleMismatchSubmit(e);
                                        }, 500);
                                    }
                                });
                            } else {
                                // Just link artist
                                handleMismatchSubmit(e);
                            }
                        }}>
                            {/* Current Information */}
                            <div className="mb-4">
                                <h6 className="mb-3" style={{ color: '#495057', borderBottom: '2px solid #dee2e6', paddingBottom: '8px' }}>
                                    Current Information
                                </h6>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label className="fw-bold text-muted" style={{ fontSize: '0.85rem' }}>Distribution Types</Label>
                                            <div className="mt-1">
                                                <Badge color="info" className="me-1">{selectedMismatchItem.distribution_type || 'N/A'}</Badge>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <Label className="fw-bold text-muted" style={{ fontSize: '0.85rem' }}>Current Artist</Label>
                                            <div className="mt-1">
                                                <Badge color="warning">Unknown Artist</Badge>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* ISRC/UPC Section */}
                            <div className="mb-4" style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                <h6 className="mb-3" style={{ color: '#495057' }}>
                                    <Edit size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
                                    Update ISRC / UPC
                                </h6>
                                {!canLink && (
                                    <div className="alert alert-danger mb-3">
                                        <strong>⚠️ Required:</strong> Both ISRC and UPC are placeholders. Please update at least one before linking with an artist.
                                    </div>
                                )}
                                {(hasPlaceholderIsrc || hasPlaceholderUpc) && canLink && (
                                    <div className="alert alert-warning mb-3">
                                        <strong>⚠️ Warning:</strong> {hasPlaceholderIsrc ? 'ISRC' : 'UPC'} is a placeholder. Consider updating it.
                                    </div>
                                )}
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>
                                                ISRC {hasPlaceholderIsrc && <Badge color="danger" className="ms-2">Placeholder</Badge>}
                                            </Label>
                                            <Input
                                                type="text"
                                                value={newIsrc}
                                                onChange={(e) => setNewIsrc(e.target.value)}
                                                placeholder={isrc && !hasPlaceholderIsrc ? isrc : "Enter ISRC"}
                                                style={{ 
                                                    borderColor: hasPlaceholderIsrc ? '#dc3545' : '#ced4da',
                                                    backgroundColor: hasPlaceholderIsrc ? '#fff5f5' : '#fff'
                                                }}
                                            />
                                            {isrc && !hasPlaceholderIsrc && (
                                                <small className="text-muted">Current: {isrc}</small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>
                                                UPC 
                                            </Label>
                                            <Input
                                                type="text"
                                                value={newUpc}
                                                onChange={(e) => setNewUpc(e.target.value)}
                                                placeholder={upc && !hasPlaceholderUpc ? upc : "Enter UPC"}
                                                style={{ 
                                                    borderColor: hasPlaceholderUpc ? '#dc3545' : '#ced4da',
                                                    backgroundColor: hasPlaceholderUpc ? '#fff5f5' : '#fff'
                                                }}
                                            />
                                            {upc && !hasPlaceholderUpc && (
                                                <small className="text-muted">Current: {upc}</small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="alert alert-info mb-0 mt-2" style={{ fontSize: '0.875rem' }}>
                                    <small>
                                        <strong>Note:</strong> At least one of ISRC or UPC must be valid (not a placeholder). 
                                        This will update all distributions with the same ISRC/UPC across all distribution types.
                                    </small>
                                </div>
                            </div>

                            {/* Artist Selection Section */}
                            <div className="mb-3" style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                <h6 className="mb-3" style={{ color: '#495057' }}>
                                    <Check size={16} className="me-2" style={{ verticalAlign: 'middle' }} />
                                    Link with Correct Artist
                                </h6>
                                
                                {selectedMismatchItem.correct_product && selectedMismatchItem.correct_product.artist_name && (
                                    <div className="alert alert-success mb-3">
                                        <strong>💡 Recommended:</strong> {selectedMismatchItem.correct_product.artist_name}
                                        {selectedMismatchItem.correct_product.artist_id && (
                                            <Button
                                                color="link"
                                                size="sm"
                                                className="p-0 ms-2"
                                                onClick={() => setSelectedMismatchArtistId(selectedMismatchItem.correct_product.artist_id)}
                                                style={{ textDecoration: 'underline', padding: 0 }}
                                            >
                                                Use this artist
                                            </Button>
                                        )}
                                    </div>
                                )}

                                <FormGroup>
                                    <Label>Search Artists</Label>
                                    <Input
                                        type="text"
                                        placeholder="Type to search artists..."
                                        value={mismatchArtistFilter}
                                        onChange={(e) => setMismatchArtistFilter(e.target.value)}
                                        className="mb-2"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Select Artist <span className="text-danger">*</span></Label>
                                    <Input
                                        type="select"
                                        value={selectedMismatchArtistId || ''}
                                        onChange={(e) => setSelectedMismatchArtistId(e.target.value ? parseInt(e.target.value) : null)}
                                        style={{ height: 'auto', maxHeight: '300px' }}
                                        required
                                    >
                                        <option value="">Select an artist...</option>
                                        {filteredMismatchArtists.map(artist => (
                                            <option key={artist.id} value={artist.id}>
                                                {artist.artist_name} (ID: {artist.id})
                                            </option>
                                        ))}
                                    </Input>
                                    {filteredMismatchArtists.length === 0 && (
                                        <div className="text-danger mt-1">No artists match your search</div>
                                    )}
                                    {artists.length > 0 && (
                                        <small className="form-text text-muted">
                                            Showing {filteredMismatchArtists.length} of {artists.length} artists
                                        </small>
                                    )}
                                </FormGroup>
                            </div>

                            {/* Summary */}
                            <div className="alert alert-light border" style={{ fontSize: '0.875rem' }}>
                                <strong>Summary:</strong> This will update all distributions with the same ISRC/UPC 
                            
                                across all distribution types ({selectedMismatchItem.distribution_type || 'N/A'}) 
                                {hasIsrcUpcChanges && ' with the new ISRC/UPC values'} 
                                {selectedMismatchArtistId && ' and link them with the selected artist'}.
                            </div>
                        </Form>
                        );
                    })()}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleMismatchModal}>Cancel</Button>
                    {selectedMismatchItem && (() => {
                        const isrc = selectedMismatchItem.asset_isrc;
                        const upc = selectedMismatchItem.product_upc;
                        const hasPlaceholderIsrc = isrc && (
                            isrc === 'AFR_PLACEHOLDER_ISRC' || 
                            isrc.includes('AFR_PLACEHOLDER_ISRC') ||
                            isrc === 'N/A' || 
                            !isrc || 
                            isrc.trim() === ''
                        );
                        const hasPlaceholderUpc = upc && (
                            upc === 'AFR_PLACEHOLDER_PRODUCT' || 
                            upc.includes('AFR_PLACEHOLDER_PRODUCT') ||
                            upc === 'N/A' || 
                            !upc || 
                            upc.trim() === ''
                        );
                        const canLink = !(hasPlaceholderIsrc && hasPlaceholderUpc);
                        
                        // Check if ISRC/UPC values have changed
                        const isrcChanged = newIsrc && newIsrc.trim() !== '' && newIsrc.trim() !== (isrc || '');
                        const upcChanged = newUpc && newUpc.trim() !== '' && newUpc.trim() !== (upc || '');
                        const hasIsrcUpcChanges = isrcChanged || upcChanged;
                        
                        // Check if at least one valid identifier exists (current or new)
                        // Use newIsrc/newUpc if provided, otherwise use current isrc/upc
                        const effectiveIsrc = (newIsrc && newIsrc.trim() !== '') ? newIsrc.trim() : (isrc || '');
                        const effectiveUpc = (newUpc && newUpc.trim() !== '') ? newUpc.trim() : (upc || '');
                        
                        const hasValidIsrc = effectiveIsrc && 
                                            effectiveIsrc !== 'AFR_PLACEHOLDER_ISRC' && 
                                            !effectiveIsrc.includes('AFR_PLACEHOLDER_ISRC') &&
                                            effectiveIsrc !== 'N/A' && 
                                            effectiveIsrc.trim() !== '';
                        const hasValidUpc = effectiveUpc && 
                                            effectiveUpc !== 'AFR_PLACEHOLDER_PRODUCT' && 
                                            !effectiveUpc.includes('AFR_PLACEHOLDER_PRODUCT') &&
                                            effectiveUpc !== 'N/A' && 
                                            effectiveUpc.trim() !== '';
                        const hasValidIdentifier = hasValidIsrc || hasValidUpc;
                        
                        const canSubmit = hasValidIdentifier && 
                                         selectedMismatchArtistId && 
                                         !updateDistributionMutation.isLoading && 
                                         !updateIsrcUpcMutation.isLoading;
                        
                        return (
                            <Button
                                color="primary"
                                onClick={(e) => {
                                    e.preventDefault();
                                    // If ISRC/UPC changed, update them first, then link artist
                                    if (hasIsrcUpcChanges) {
                                        handleUpdateIsrcUpc(e).then(() => {
                                            // After ISRC/UPC update, if artist is selected, link it
                                            if (selectedMismatchArtistId) {
                                                setTimeout(() => {
                                                    handleMismatchSubmit(e);
                                                }, 500);
                                            }
                                        }).catch(() => {
                                            // Error already handled in handleUpdateIsrcUpc
                                        });
                                    } else {
                                        // Just link artist
                                        handleMismatchSubmit(e);
                                    }
                                }}
                                disabled={!canSubmit}
                            >
                                {(updateDistributionMutation.isLoading || updateIsrcUpcMutation.isLoading) ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Check size={14} className="me-1" /> 
                                        {hasIsrcUpcChanges ? 'Update ISRC/UPC & Link Artist' : 'Link with Artist'}
                                    </>
                                )}
                            </Button>
                        );
                    })()}
                </ModalFooter>
            </Modal>

            <Breadcrumbs
                parent="Income"
                title="Processing Report"
                mainTitle="Processing Report Details"
                buttons={[
                    <Button
                        size="sm"
                        color="primary"
                        onClick={() => navigate(appPath('/income/income-list'))}
                    >
                        <i className="fa fa-arrow-left me-1"></i> Back to Incomes
                    </Button>,
                ]}
            />

            <div className="rd-processing-report">
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                                {isLoading ? (
                                    <div className="pr-loading">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3">Loading report data...</p>
                                    </div>
                                ) : showNoDataCard ? (
                                    <Card>
                                        <CardBody className="text-center">
                                            <div className="mb-4">
                                                <i className="fa fa-file-alt fa-3x text-muted"></i>
                                            </div>
                                            <h4>No Report Data Available</h4>
                                            <p className="text-muted">
                                                The requested report could not be found or has not been generated yet.
                                            </p>
                                            <Button
                                                color="primary"
                                                onClick={() => navigate(appPath('/income/income-list'))}
                                            >
                                                <i className="fa fa-arrow-left me-1"></i> Back to Incomes
                                            </Button>
                                        </CardBody>
                                    </Card>
                                ) : isError ? (
                                    <div className="alert alert-danger">
                                        <h4 className="alert-heading">Error Loading Report</h4>
                                        <p>
                                            {error?.response?.status === 500
                                                ? "The requested report could not be found. It may have been deleted or the ID is invalid."
                                                : `Error: ${error.message}`
                                            }
                                        </p>
                                        <hr />
                                        <p className="mb-0">
                                            Please check the report ID and try again, or go back to the Income to select a valid report.
                                        </p>
                                    </div>
                                ) : !report ? (
                                    <div className="alert alert-warning">
                                        <h4 className="alert-heading">No Report Data</h4>
                                        <p>No data was returned for this report ID.</p>
                                    </div>
                                ) : (backendProcessingReportData || processingReportData) ? (
                                    <>
                                        {report && (
                                            <ProcessingReportHeader
                                                report={report}
                                                comparisonScore={comparisonScore}
                                                reportStatistics={reportStatistics}
                                                onDownloadFile={handleDownloadFile}
                                            />
                                        )}

                                        <ProcessingReportTabs
                                            activeTab={activeReportTab}
                                            onChange={setActiveReportTab}
                                            mismatchCount={artistMismatchPagination.totalItems || 0}
                                        />

                                        {activeReportTab === 'overview' && (
                                            <ProcessingReportOverview
                                                mismatchCount={artistMismatchPagination.totalItems || 0}
                                                onGoToMismatches={() => setActiveReportTab('mismatches')}
                                                onGoToCompare={() => setActiveReportTab('compare')}
                                                reportStatistics={reportStatistics}
                                                processedData={processedData}
                                                processedBackendData={processedBackendData}
                                            />
                                        )}

                                        {activeReportTab === 'mismatches' && report && (
                                            <div className="inc-panel pr-mismatch">
                                                <div className="pr-mismatch__head">
                                                    <h5 className="pr-mismatch__title">Artist mismatch distributions</h5>
                                                    {artistMismatchData.length > 0 && (
                                                        <div className="pr-mismatch__search">
                                                            <Input
                                                                type="text"
                                                                placeholder="Search ISRC, UPC, title…"
                                                                value={artistMismatchSearch}
                                                                onChange={(e) => setArtistMismatchSearch(e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="pr-mismatch__body">
                                                    {artistMismatchLoading ? (
                                                        <div className="text-center p-5">
                                                            <div className="spinner-border text-warning" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            <p className="mt-3 text-muted">Loading artist mismatch data...</p>
                                                        </div>
                                                    ) : (() => {
                                                        // Filter and sort data
                                                        let filteredData = artistMismatchData;
                                                        
                                                        // Apply search filter
                                                        if (artistMismatchSearch) {
                                                            const searchLower = artistMismatchSearch.toLowerCase();
                                                            filteredData = artistMismatchData.filter(item => 
                                                                (item.asset_isrc && item.asset_isrc.toLowerCase().includes(searchLower)) ||
                                                                (item.product_upc && item.product_upc.toLowerCase().includes(searchLower)) ||
                                                                (item.name && item.name.toLowerCase().includes(searchLower)) ||
                                                                (item.correct_artist_name && item.correct_artist_name.toLowerCase().includes(searchLower))
                                                            );
                                                        }
                                                        
                                                        // Apply sorting
                                                        filteredData = [...filteredData].sort((a, b) => {
                                                            let aVal, bVal;
                                                            if (artistMismatchSortBy === 'revenue') {
                                                                aVal = parseFloat(a.total_revenue || a.revenue || 0);
                                                                bVal = parseFloat(b.total_revenue || b.revenue || 0);
                                                            } else if (artistMismatchSortBy === 'streams') {
                                                                aVal = parseInt(a.streams || 0);
                                                                bVal = parseInt(b.streams || 0);
                                                            } else {
                                                                aVal = (a.asset_isrc || '').toLowerCase();
                                                                bVal = (b.asset_isrc || '').toLowerCase();
                                                            }
                                                            
                                                            if (artistMismatchSortOrder === 'asc') {
                                                                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
                                                            } else {
                                                                return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
                                                            }
                                                        });
                                                        
                                                        return filteredData.length > 0 ? (
                                                        <>
                                                            <div className="pr-mismatch-table-wrap">
                                                                <Table bordered hover className="align-middle mb-0 pr-mismatch-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Type</th>
                                                                            <th style={{ minWidth: '200px' }}>Title</th>
                                                                            <th
                                                                                className="pr-sortable"
                                                                                onClick={() => {
                                                                                    if (artistMismatchSortBy === 'isrc') {
                                                                                        setArtistMismatchSortOrder(artistMismatchSortOrder === 'asc' ? 'desc' : 'asc');
                                                                                    } else {
                                                                                        setArtistMismatchSortBy('isrc');
                                                                                        setArtistMismatchSortOrder('asc');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                ISRC {artistMismatchSortBy === 'isrc' && (artistMismatchSortOrder === 'asc' ? '↑' : '↓')}
                                                                            </th>
                                                                            <th>UPC</th>
                                                                            <th
                                                                                className="pr-sortable text-end"
                                                                                onClick={() => {
                                                                                    if (artistMismatchSortBy === 'revenue') {
                                                                                        setArtistMismatchSortOrder(artistMismatchSortOrder === 'asc' ? 'desc' : 'asc');
                                                                                    } else {
                                                                                        setArtistMismatchSortBy('revenue');
                                                                                        setArtistMismatchSortOrder('desc');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Revenue {artistMismatchSortBy === 'revenue' && (artistMismatchSortOrder === 'asc' ? '↑' : '↓')}
                                                                            </th>
                                                                            <th
                                                                                className="pr-sortable text-end"
                                                                                onClick={() => {
                                                                                    if (artistMismatchSortBy === 'streams') {
                                                                                        setArtistMismatchSortOrder(artistMismatchSortOrder === 'asc' ? 'desc' : 'asc');
                                                                                    } else {
                                                                                        setArtistMismatchSortBy('streams');
                                                                                        setArtistMismatchSortOrder('desc');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Streams {artistMismatchSortBy === 'streams' && (artistMismatchSortOrder === 'asc' ? '↑' : '↓')}
                                                                            </th>
                                                                            <th>Artist</th>
                                                                            <th>Correct artist</th>
                                                                            <th>Error</th>
                                                                            <th className="text-center pr-mismatch-table__action">Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {filteredData.map((item, index) => {
                                                                            const correctProduct = item.correct_product || { artist_name: item.correct_artist_name || "Unknown Artist" };
                                                                            
                                                                            // Parse distribution types to show badges
                                                                            const types = item.distribution_type ? item.distribution_type.split(', ') : [];

                                                                            const titleText = item.name || 'N/A';
                                                                            const isLongTitle = titleText.length > 50;
                                                                            
                                                                            return (
                                                                                <tr key={`mismatch-${item.asset_isrc || index}-${index}`}>
                                                                                    <td>
                                                                                        <div className="d-flex flex-wrap gap-1">
                                                                                            {types.map((type, idx) => (
                                                                                                <Badge 
                                                                                                    key={idx}
                                                                                                    color={
                                                                                                        type === 'Title' ? 'primary' :
                                                                                                        type === 'DSP' ? 'info' :
                                                                                                        type === 'Country' ? 'success' :
                                                                                                        'warning'
                                                                                                    }
                                                                                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                                                                >
                                                                                                    {type}
                                                                                                </Badge>
                                                                                            ))}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', maxWidth: '250px' }}>
                                                                                        {isLongTitle ? (
                                                                                            <span 
                                                                                                title={titleText}
                                                                                                style={{ 
                                                                                                    display: 'block',
                                                                                                    overflow: 'hidden',
                                                                                                    textOverflow: 'ellipsis',
                                                                                                    whiteSpace: 'nowrap',
                                                                                                    cursor: 'help'
                                                                                                }}
                                                                                            >
                                                                                                {titleText.substring(0, 50)}...
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span>{titleText}</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                                        {item.asset_isrc || <span className="text-muted">N/A</span>}
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                                        {item.product_upc || <span className="text-muted">N/A</span>}
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', textAlign: 'right', fontWeight: '500' }}>
                                                                                        {item.total_revenue || item.revenue
                                                                                            ? `${parseFloat(item.total_revenue || item.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}`
                                                                                            : <span className="text-muted">N/A</span>}
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', textAlign: 'right' }}>
                                                                                        {item.streams ? item.streams.toLocaleString() : <span className="text-muted">N/A</span>}
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                                                                                        <Badge color="secondary" style={{ fontSize: '0.8rem' }}>Unknown Artist</Badge>
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                                                                                        <div className="d-flex align-items-center">
                                                                                            <span className="me-2" style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ffc107', display: 'inline-block', flexShrink: 0 }}></span>
                                                                                            <span style={{ fontWeight: '500', color: '#856404' }}>{correctProduct.artist_name}</span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle' }}>
                                                                                        <Badge color="warning" style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}>
                                                                                            <AlertCircle size={12} className="me-1" />
                                                                                            Mismatch
                                                                                        </Badge>
                                                                                    </td>
                                                                                    <td style={{ padding: '12px', verticalAlign: 'middle', textAlign: 'center', width: '100px', position: 'sticky', right: 0, backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa', zIndex: 4 }}>
                                                                                        <Button
                                                                                            color="danger"
                                                                                            size="sm"
                                                                                            className="btn-icon rounded-circle"
                                                                                            style={{ 
                                                                                                width: '36px', 
                                                                                                height: '36px',
                                                                                                display: 'inline-flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                transition: 'all 0.2s',
                                                                                                padding: 0,
                                                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                                            }}
                                                                                            onClick={() => openMismatchEditModal(item)}
                                                                                            title="Edit and link with correct artist"
                                                                                            onMouseEnter={(e) => {
                                                                                                e.currentTarget.style.transform = 'scale(1.1)';
                                                                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                                                                            }}
                                                                                            onMouseLeave={(e) => {
                                                                                                e.currentTarget.style.transform = 'scale(1)';
                                                                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                                                            }}
                                                                                        >
                                                                                            <Edit size={16} />
                                                                                        </Button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top flex-wrap gap-3">
                                                                <div className="d-flex align-items-center flex-wrap gap-2">
                                                                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>Items per page:</span>
                                                                    <Input
                                                                        type="select"
                                                                        className="pagination-select"
                                                                        value={artistMismatchPagination.itemsPerPage}
                                                                        onChange={(e) => {
                                                                            const newPerPage = parseInt(e.target.value);
                                                                            fetchArtistMismatchData(id, 1, newPerPage);
                                                                        }}
                                                                        style={{ width: 'auto', fontSize: '0.875rem' }}
                                                                    >
                                                                        <option value="10">10</option>
                                                                        <option value="20">20</option>
                                                                        <option value="50">50</option>
                                                                        <option value="100">100</option>
                                                                    </Input>
                                                                    <span className="text-muted ms-2" style={{ fontSize: '0.875rem' }}>
                                                                        Showing {filteredData.length > 0 ? 1 : 0} - {Math.min(filteredData.length, artistMismatchPagination.itemsPerPage)} of {filteredData.length} {filteredData.length === 1 ? 'result' : 'results'}
                                                                        {artistMismatchSearch && ` (filtered from ${artistMismatchPagination.totalItems} total)`}
                                                                    </span>
                                                                </div>
                                                                {artistMismatchPagination.totalPages > 1 && (
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <Button
                                                                            color="outline-secondary"
                                                                            size="sm"
                                                                            disabled={artistMismatchPagination.currentPage === 1}
                                                                            onClick={() => fetchArtistMismatchData(id, artistMismatchPagination.currentPage - 1, artistMismatchPagination.itemsPerPage)}
                                                                        >
                                                                            Previous
                                                                        </Button>
                                                                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                                                            Page {artistMismatchPagination.currentPage} of {artistMismatchPagination.totalPages}
                                                                        </span>
                                                                        <Button
                                                                            color="outline-secondary"
                                                                            size="sm"
                                                                            disabled={artistMismatchPagination.currentPage === artistMismatchPagination.totalPages}
                                                                            onClick={() => fetchArtistMismatchData(id, artistMismatchPagination.currentPage + 1, artistMismatchPagination.itemsPerPage)}
                                                                        >
                                                                            Next
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </>
                                                        ) : (
                                                            <div className="alert alert-info text-center py-4">
                                                                <AlertCircle size={24} className="mb-2" />
                                                                <p className="mb-0">
                                                                    {artistMismatchSearch 
                                                                        ? `No results found for "${artistMismatchSearch}". Try a different search term.`
                                                                        : 'No artist mismatches found in this report. All distributions are correctly linked.'}
                                                                </p>
                                                                {artistMismatchSearch && (
                                                                    <Button 
                                                                        color="link" 
                                                                        size="sm" 
                                                                        className="mt-2"
                                                                        onClick={() => setArtistMismatchSearch('')}
                                                                    >
                                                                        Clear search
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}

                                        {activeReportTab === 'compare' && (
                                        <>
                                        {/* Side-by-Side Comparison Display */}
                                                            {isLoading ? (
                                                                <div className="pr-loading">
                                                                    <div className="spinner-border text-primary" role="status">
                                                                        <span className="visually-hidden">Loading processing report data...</span>
                                                                    </div>
                                                                    <p className="mt-3">Loading processing report data...</p>
                                                                </div>
                                                            ) : isError ? (
                                                                <div className="alert alert-danger">
                                                                    <h6 className="alert-heading">Error Loading Processing Report</h6>
                                                                    <p className="mb-0">
                                                                        {error?.message || 'Failed to load processing report data'}
                                                                    </p>
                                                                </div>
                                        ) : (
                                                                <div>
                                                <p className="pr-compare-intro">
                                                    Compare saved database records (Backend) against the S3 processing JSON. Expand each section to inspect differences.
                                                </p>
                                                                        <CollapsibleSection
                                                                            title="Processing Metadata"
                                                                            isCollapsed={collapsedSections.processingMetadata}
                                                                            onToggle={() => toggleSection('processingMetadata')}
                                                                            icon={Check}
                                                                        >
                                                                                    <Table bordered responsive size="sm" className="pr-compare-table">
                                                        <thead>
                                                                                            <tr>
                                                                <th>Field</th>
                                                                <th>Backend API</th>
                                                                <th>S3 JSON</th>
                                                                                            </tr>
                                                        </thead>
                                                                                        <tbody>
                                                            {(() => {
                                                                const backendVal = processedBackendData?.metadata?.total_rows;
                                                                const s3Val = processedData?.metadata?.total_rows;
                                                                const rowTone = getCompareRowTone(backendVal, s3Val);
                                                                return (
                                                            <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                <th>Total Rows</th>
                                                                <td>
                                                                    <div>
                                                                        <strong>{backendVal?.toLocaleString() || 'N/A'}</strong>
                                                                        <div className="text-muted small mt-1">
                                                                            From saved records in database (output file)
                                                                        </div>
                                                                        {s3Val && (
                                                                            <div className="text-info small mt-1">
                                                                                Note: Raw file has {s3Val.toLocaleString()} rows (not for comparison with processed_rows)
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <strong>{s3Val?.toLocaleString() || 'N/A'}</strong>
                                                                        <div className="text-muted small mt-1">
                                                                            From raw file processing ({s3Val?.toLocaleString() || 'N/A'} rows total from raw file)
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const backendVal = processedBackendData?.metadata?.processed_rows;
                                                                const s3Val = processedData?.metadata?.processed_rows;
                                                                const rowTone = getCompareRowTone(backendVal, s3Val);
                                                                return (
                                                            <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                <th>Processed Rows</th>
                                                                <td>
                                                                    <div>
                                                                        <strong>{backendVal?.toLocaleString() || 'N/A'}</strong>
                                                                        <div className="text-muted small mt-1">
                                                                            From database saved records (output file: {backendVal?.toLocaleString() || 'N/A'} saved)
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <strong>{s3Val?.toLocaleString() || 'N/A'}</strong>
                                                                        <div className="text-muted small mt-1">
                                                                            From raw file processing ({s3Val?.toLocaleString() || 'N/A'} rows processed, not database saved)
                                                                        </div>
                                                                    </div>
                                                                                                </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {renderComparisonRow(
                                                                'Error Count',
                                                                processedBackendData?.metadata?.error_count,
                                                                processedData?.metadata?.error_count,
                                                                'processing_metadata.error_count'
                                                            )}
                                                            {renderComparisonRow(
                                                                'Chunks Processed',
                                                                processedBackendData?.metadata?.chunks_processed,
                                                                processedData?.metadata?.chunks_processed,
                                                                'processing_metadata.chunks_processed',
                                                                false,
                                                                null,
                                                                null,
                                                                null,
                                                                true
                                                            )}
                                                            {(() => {
                                                                const backendIsrcs = reportStatistics?.database_totals?.unique_isrcs || processedBackendData?.metadata?.unique_isrcs || null;
                                                                const s3Isrcs = processedData?.metadata?.unique_isrcs || null;
                                                                const rowTone = getCompareRowTone(backendIsrcs, s3Isrcs);
                                                                
                                                                return (
                                                                    <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                        <th>Unique ISRCs</th>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{backendIsrcs !== null ? backendIsrcs.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From statistics.database_totals (unique_isrcs)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{s3Isrcs !== null ? s3Isrcs.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From processing_metadata (unique_isrcs)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const breakdownTotal = (processedData?.breakdown?.total_records || 0) + 
                                                                                       (processedData?.breakdown?.dsp_records || 0) + 
                                                                                       (processedData?.breakdown?.territory_records || 0) + 
                                                                                       (processedData?.breakdown?.sale_type_records || 0);
                                                                const backendTotal = processedBackendData?.metadata?.total_records || 0;
                                                                const rowTone = getCompareRowTone(backendTotal, breakdownTotal > 0 ? breakdownTotal : null);
                                                                
                                                                return (
                                                                    <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                        <th>Total Records</th>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{backendTotal.toLocaleString()}</strong>
                                                                                {breakdownTotal > 0 && backendTotal > 0 && 
                                                                                 breakdownTotal !== backendTotal && (
                                                                                    <div className="alert alert-warning small mt-2 mb-0" style={{ fontSize: '11px', padding: '6px' }}>
                                                                                        ⚠️ <strong>Warning:</strong> Missing {((breakdownTotal - backendTotal).toLocaleString())} records in database. Please check why some data was not saved.
                                                                                    </div>
                                                                                )}
                                                                                <div className="text-muted small mt-1">
                                                                                    From database saved entries (saved_entries)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{breakdownTotal > 0 ? breakdownTotal.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From breakdown_counts (total_records + dsp_records + territory_records + sale_type_records)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const backendRevenueRaw = reportStatistics?.database_totals?.total_revenue || processedBackendData?.metadata?.total_revenue || null;
                                                                const s3RevenueRaw = processedData?.metadata?.total_revenue || null;
                                                                const backendRevenue = backendRevenueRaw !== null ? Number(backendRevenueRaw) : null;
                                                                const s3Revenue = s3RevenueRaw !== null ? Number(s3RevenueRaw) : null;
                                                                const rowTone = getCompareRowTone(backendRevenue, s3Revenue);
                                                                const currency = report?.provider_currency || 'EUR';
                                                                
                                                                return (
                                                                    <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                        <th>Total Revenue</th>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{backendRevenue !== null && !isNaN(backendRevenue) ? `${backendRevenue.toFixed(2)} ${currency}` : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From statistics.database_totals (total_revenue)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{s3Revenue !== null && !isNaN(s3Revenue) ? `${s3Revenue.toFixed(2)} ${currency}` : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From processing_metadata (total_revenue)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const backendStreams = reportStatistics?.database_totals?.total_streams || processedBackendData?.metadata?.total_streams || null;
                                                                const s3Streams = processedData?.metadata?.total_streams || null;
                                                                const rowTone = getCompareRowTone(backendStreams, s3Streams);
                                                                
                                                                return (
                                                                    <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                        <th>Total Streams</th>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{backendStreams !== null ? backendStreams.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From statistics.database_totals (total_streams)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{s3Streams !== null ? s3Streams.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From processing_metadata (total_streams)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {(() => {
                                                                const backendDownloads = reportStatistics?.database_totals?.total_downloads || processedBackendData?.metadata?.total_downloads || null;
                                                                const s3Downloads = processedData?.metadata?.total_downloads || null;
                                                                const rowTone = getCompareRowTone(backendDownloads, s3Downloads);
                                                                
                                                                return (
                                                                    <tr className={`pr-compare-row pr-compare-row--${rowTone}`}>
                                                                        <th>Total Downloads</th>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{backendDownloads !== null ? backendDownloads.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From statistics.database_totals (total_downloads)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div>
                                                                                <strong>{s3Downloads !== null ? s3Downloads.toLocaleString() : 'N/A'}</strong>
                                                                                <div className="text-muted small mt-1">
                                                                                    From processing_metadata (total_downloads)
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                                            </tr>
                                                                );
                                                            })()}
                                                            {renderComparisonRow(
                                                                'Average Revenue Per Title',
                                                                processedBackendData?.metadata?.average_revenue_per_title,
                                                                processedData?.metadata?.average_revenue_per_title,
                                                                'processing_metadata.average_revenue_per_title'
                                                            )}
                                                            {renderComparisonRow(
                                                                'Processing Date',
                                                                processedBackendData?.metadata?.processing_date ? moment(processedBackendData.metadata.processing_date).format('YYYY-MM-DD HH:mm:ss') : null,
                                                                processedData?.metadata?.processing_date ? moment(processedData.metadata.processing_date).format('YYYY-MM-DD HH:mm:ss') : null,
                                                                'processing_metadata.processing_date'
                                                            )}
                                                                                        </tbody>
                                                                                    </Table>
                                                                        </CollapsibleSection>

                                                                    {/* Processing Times - New Section */}
                                                                    {processedData?.processingTimes && Object.keys(processedData.processingTimes).length > 0 && (
                                                                        <CollapsibleSection
                                                                            title="Processing Times"
                                                                            isCollapsed={collapsedSections.processingTimes}
                                                                            onToggle={() => toggleSection('processingTimes')}
                                                                            icon={Clock}
                                                                        >
                                                                            <Row>
                                                                                <Col md="12">
                                                                                    <Table bordered responsive size="sm">
                                                                                        <tbody>
                                                                                            {Object.entries(processedData.processingTimes).map(([key, value]) => {
                                                                                                // Define custom labels for each processing step
                                                                                                const stepLabels = {
                                                                                                    data_reading: "Reading Raw Data",
                                                                                                    data_processing: "Processing & Aggregating",
                                                                                                    data_formatting: "Preparing Output",
                                                                                                    csv_generation: "Generating CSV File",
                                                                                                    json_report_generation: "Generating Analytics"
                                                                                                };
                                                                                                
                                                                                                const label = stepLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                                                                
                                                                                                return (
                                                                                                    <tr key={key}>
                                                                                                        <td><strong>{label}</strong></td>
                                                                                                        <td>{value}s</td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                            {/* Add total processing time if available */}
                                                                                            {processedData.metadata?.total_processing && (
                                                                                                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                                                                                    <td><strong>Total Processing</strong></td>
                                                                                                    <td>{processedData.metadata.total_processing}s</td>
                                                                                                </tr>
                                                                                            )}
                                                                                            {/* Add total CSV generation time if available */}
                                                                                            {processedData.metadata?.total_csv_generation && (
                                                                                                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                                                                                    <td><strong>Total CSV Generation</strong></td>
                                                                                                    <td>{processedData.metadata.total_csv_generation}s</td>
                                                                                                </tr>
                                                                                            )}
                                                                                        </tbody>
                                                                                    </Table>
                                                                                </Col>
                                                                            </Row>
                                                                        </CollapsibleSection>
                                                                    )}

                                                {/* Distribution Counts - Side-by-Side Comparison */}
                                                {(processedBackendData?.distribution || processedData?.distribution) && (
                                                                        <CollapsibleSection
                                                                            title="Distribution Counts"
                                                                            isCollapsed={collapsedSections.distributionCounts}
                                                                            onToggle={() => toggleSection('distributionCounts')}
                                                                            icon={Check}
                                                                        >
                                                        <Table bordered responsive size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '200px', backgroundColor: '#f8f9fa' }}>Field</th>
                                                                    <th style={{ backgroundColor: '#e7f3ff' }}>Backend API</th>
                                                                    <th style={{ backgroundColor: '#fff4e6' }}>S3 JSON</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {renderComparisonRow(
                                                                    'Title Distribution',
                                                                    processedBackendData?.distribution?.title_distribution,
                                                                    processedData?.distribution?.title_distribution,
                                                                    'distribution_counts.title_distribution',
                                                                    true,
                                                                    'saving_title_distribution',
                                                                    processedBackendData?.distribution?.title_revenue,
                                                                    processedData?.distribution?.title_revenue || '0'
                                                                )}
                                                                {renderComparisonRow(
                                                                    'DSP Distribution',
                                                                    processedBackendData?.distribution?.dsp_distribution,
                                                                    processedData?.distribution?.dsp_distribution,
                                                                    'distribution_counts.dsp_distribution',
                                                                    true,
                                                                    'saving_dsp_distribution',
                                                                    processedBackendData?.distribution?.dsp_revenue,
                                                                    processedData?.distribution?.dsp_revenue || '0'
                                                                )}
                                                                {renderComparisonRow(
                                                                    'Country Distribution',
                                                                    processedBackendData?.distribution?.country_distribution,
                                                                    processedData?.distribution?.country_distribution,
                                                                    'distribution_counts.country_distribution',
                                                                    true,
                                                                    'saving_country_distribution',
                                                                    processedBackendData?.distribution?.country_revenue,
                                                                    processedData?.distribution?.country_revenue || '0'
                                                                )}
                                                                {renderComparisonRow(
                                                                    'Type Sale Distribution',
                                                                    processedBackendData?.distribution?.typesale_distribution,
                                                                    processedData?.distribution?.typesale_distribution,
                                                                    'distribution_counts.typesale_distribution',
                                                                    true,
                                                                    'saving_typesale_distribution',
                                                                    processedBackendData?.distribution?.typesale_revenue,
                                                                    processedData?.distribution?.typesale_revenue || '0'
                                                                )}
                                                                {renderComparisonRow(
                                                                    'Total',
                                                                    processedBackendData?.distribution?.total,
                                                                    processedData?.distribution?.total,
                                                                    'distribution_counts.total',
                                                                    false,
                                                                    null,
                                                                    processedBackendData?.distribution?.total_revenue,
                                                                    processedData?.distribution?.total_revenue || '0'
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                                        </CollapsibleSection>
                                                                    )}

                                                {/* Top Analysis - Side-by-Side Comparison */}
                                                {(processedBackendData?.topAnalysis || processedData?.topAnalysis) && (
                                                                        <CollapsibleSection
                                                                            title="Top Analysis"
                                                                            isCollapsed={collapsedSections.topAnalysis}
                                                                            onToggle={() => toggleSection('topAnalysis')}
                                                                            icon={Check}
                                                                        >
                                                        {/* Top Titles Comparison */}
                                                        {renderArrayComparisonTable(
                                                            "Top Titles",
                                                            processedBackendData?.topAnalysis?.top_titles,
                                                            processedData?.topAnalysis?.top_titles,
                                                            "top_analysis.top_titles",
                                                            [{ key: 'name', header: 'Name' }]
                                                        )}

                                                        {/* Top DSPs Comparison */}
                                                        {renderArrayComparisonTable(
                                                            "Top DSPs",
                                                            processedBackendData?.topAnalysis?.top_dsps,
                                                            processedData?.topAnalysis?.top_dsps,
                                                            "top_analysis.top_dsps",
                                                            [{ key: 'name', header: 'Name' }]
                                                        )}

                                                        {/* Top Countries Comparison */}
                                                        {renderArrayComparisonTable(
                                                            "Top Countries",
                                                            processedBackendData?.topAnalysis?.top_countries,
                                                            processedData?.topAnalysis?.top_countries,
                                                            "top_analysis.top_countries",
                                                            [{ key: 'name', header: 'Name' }]
                                                        )}

                                                        {/* Top Artists Comparison */}
                                                        {renderArrayComparisonTable(
                                                            "Top Artists",
                                                            processedBackendData?.topAnalysis?.top_artists,
                                                            processedData?.topAnalysis?.top_artists,
                                                            "top_analysis.top_artists",
                                                            [{ key: 'name', header: 'Name' }]
                                                        )}

                                                        {/* Sale Types Comparison */}
                                                        {renderArrayComparisonTable(
                                                            "Sale Types",
                                                            processedBackendData?.topAnalysis?.sale_types,
                                                            processedData?.topAnalysis?.sale_types,
                                                            "top_analysis.sale_types",
                                                            [{ key: 'name', header: 'Name' }]
                                                        )}
                                                                        </CollapsibleSection>
                                                                    )}

                                                                    {/* Data Quality - Optimized with Collapsible */}
                                                                    {processedData?.dataQuality && (
                                                                        <CollapsibleSection
                                                                            title="Data Quality"
                                                                            isCollapsed={collapsedSections.dataQuality}
                                                                            onToggle={() => toggleSection('dataQuality')}
                                                                            icon={Check}
                                                                        >
                                                                            <Row>
                                                                                <Col md="4">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-success">{processedData.dataQuality.completeness_score}%</h4>
                                                                                            <p className="mb-0">Completeness Score</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                                <Col md="4">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-primary">{processedData.dataQuality.data_quality_grade}</h4>
                                                                                            <p className="mb-0">Quality Grade</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                                <Col md="4">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-warning">{processedData.dataQuality.anomalies?.length || 0}</h4>
                                                                                            <p className="mb-0">Anomalies Found</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                            </Row>
                                                                            {processedData.dataQuality.anomalies && processedData.dataQuality.anomalies.length > 0 && (
                                                                                <div className="mt-3">
                                                                                    <h6>Data Anomalies</h6>
                                                                                    {processedData.dataQuality.anomalies.map((anomaly, index) => (
                                                                                        <div key={index} className="alert alert-warning">
                                                                                            <strong>{anomaly.type}:</strong> {anomaly.description}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </CollapsibleSection>
                                                                    )}

                                                                    {/* Artist Distributions - New Section */}
                                                                    {processedData?.artistDistributions && processedData.artistDistributions.length > 0 && (
                                                                        <CollapsibleSection
                                                                            title="Artist Distributions"
                                                                            isCollapsed={collapsedSections.artistDistributions}
                                                                            onToggle={() => toggleSection('artistDistributions')}
                                                                            icon={Edit}
                                                                        >
                                                                            <Row>
                                                                                <Col md="12">
                                                                                    <div className="table-responsive">
                                                                                        <Table bordered responsive size="sm">
                                                                                            <thead>
                                                                                                <tr>
                                                                                                    <th>Artist</th>
                                                                                                    <th>ISRCs</th>
                                                                                                    <th>Revenue</th>
                                                                                                    <th>Streams</th>
                                                                                                    <th>Downloads</th>
                                                                                                    <th>Titles</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {processedData.artistDistributions.slice(0, 20).map((artist, index) => (
                                                                                                    <tr key={index}>
                                                                                                        <td><strong>{artist.artist}</strong></td>
                                                                                                        <td>{artist.isrcs_count}</td>
                                                                                                        <td>{artist.revenue?.toFixed(2)} {report?.provider_currency || 'EUR'}</td>
                                                                                                        <td>{artist.streams?.toLocaleString()}</td>
                                                                                                        <td>{artist.downloads?.toLocaleString() || '0'}</td>
                                                                                                        <td>{artist.title_distributions?.count || 0}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                    {processedData.artistDistributions.length > 20 && (
                                                                                        <p className="text-muted small mt-2">
                                                                                            Showing top 20 artists out of {processedData.artistDistributions.length} total
                                                                                        </p>
                                                                                    )}
                                                                                </Col>
                                                                            </Row>
                                                                        </CollapsibleSection>
                                                                    )}

                                                                    {/* Breakdown Counts - New Section */}
                                                                    {processedData?.breakdown && (
                                                                        <CollapsibleSection
                                                                            title="Breakdown Counts"
                                                                            isCollapsed={collapsedSections.breakdownCounts}
                                                                            onToggle={() => toggleSection('breakdownCounts')}
                                                                            icon={Check}
                                                                        >
                                                                            <Row>
                                                                                <Col md="3">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-primary">{processedData.breakdown.total_records || 0}</h4>
                                                                                            <p className="mb-0">Total Records</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                                <Col md="3">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-info">{processedData.breakdown.dsp_records || 0}</h4>
                                                                                            <p className="mb-0">DSP Records</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                                <Col md="3">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-warning">{processedData.breakdown.territory_records || 0}</h4>
                                                                                            <p className="mb-0">Territory Records</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                                <Col md="3">
                                                                                    <Card className="text-center">
                                                                                        <CardBody>
                                                                                            <h4 className="text-success">{processedData.breakdown.sale_type_records || 0}</h4>
                                                                                            <p className="mb-0">Sale Type Records</p>
                                                                                        </CardBody>
                                                                                    </Card>
                                                                                </Col>
                                                                            </Row>
                                                                        </CollapsibleSection>
                                                                    )}

                                                                </div>
                                                            )}
                                        </>
                                        )}

                                        {activeReportTab === 'json' && (
                                            <div className="inc-panel pr-json-panel">
                                                <JsonEditor
                                                    data={processingReportData}
                                                    onDownload={() => {
                                                        const blob = new Blob([JSON.stringify(processingReportData, null, 2)], { type: 'application/json' });
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `processing_report_${id}.json`;
                                                        a.click();
                                                        URL.revokeObjectURL(url);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : !hasValidReportData ? (
                                    <Card>
                                        <CardBody className="text-center">
                                            <div className="mb-4">
                                                <i className="fa fa-chart-bar fa-3x text-muted"></i>
                                            </div>
                                            <h4>Report Processing</h4>
                                            <p className="text-muted">
                                                The report exists but detailed statistics are still being processed.
                                                Please check back later for complete information.
                                            </p>
                                            <div className="mt-4">
                                                <p><a>Report ID:</a> {report.id}</p>
                                                {report.provider_name && <p><a>Provider:</a> {report.provider_name}</p>}
                                                {report.created_at && <p><a>Created:</a> {moment(report.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>}
                                            </div>
                                            <div className="mt-4">
                                                <Button
                                                    color="primary"
                                                    onClick={() => window.location.reload()}
                                                    className="me-2"
                                                >
                                                    <i className="fa fa-refresh me-1"></i> Refresh
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    onClick={() => navigate(appPath('/income/income-list'))}
                                                >
                                                    <i className="fa fa-arrow-left me-1"></i> Back to Incomes
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <>
                                        <div className="mb-4">
                                            <h6 className="section-title">Report Summary</h6>
                                            <Row>
                                                <Col md="12">
                                                    <Table bordered responsive className="summary-table">
                                                        <tbody>
                                                            <tr>
                                                                <th style={{ width: '200px', backgroundColor: '#f8f9fa' }}>Report ID</th>
                                                                <td>{report.id}</td>
                                                            </tr>
                                                            <tr>
                                                                <th style={{ backgroundColor: '#f8f9fa' }}>Provider</th>
                                                                <td>{report.provider_name}</td>
                                                            </tr>
                                                            <tr>
                                                                <th style={{ backgroundColor: '#f8f9fa' }}>File</th>
                                                                <td>
                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <Button
                                                                            color="primary"
                                                                            size="sm"
                                                                            className="ms-2"
                                                                            onClick={handleDownloadFile}
                                                                            title="Download raw file"
                                                                        >
                                                                            <Download size={14} className="me-1" /> Download Raw File
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th style={{ backgroundColor: '#f8f9fa' }}>Report Status</th>
                                                                <td>
                                                                    <Badge color={report.report_status === 'available' ? 'success' : 'warning'}>
                                                                        {report.report_status || 'Unknown'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th>Created At</th>
                                                                <td>{moment(report.created_at).format('YYYY-MM-DD HH:mm:ss')}</td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </Col>
                                                <div className="statistics-container mb-4">
                                                    <Row className="statistics-row g-3">
                                                        {reportStatistics && (
                                                            <Col md="4">
                                                                <Card className="h-100">
                                                                    <CardBody className="p-3">
                                                                        <h6 className="mb-2">Processing Statistics</h6>
                                                                        <div className="small-stats">
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Total Rows:</span>
                                                                                <a>{reportStatistics.total_rows}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Processed Rows:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.processed_rows === reportStatistics.total_rows ? '#28a745' : '#dc3545'
                                                                                }}>
                                                                                    {reportStatistics.processed_rows}
                                                                                    {reportStatistics.processed_rows === reportStatistics.total_rows && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Matched Entries:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.matched_entries === reportStatistics.total_rows ? '#28a745' : '#dc3545'
                                                                                }}>
                                                                                    {reportStatistics.matched_entries}
                                                                                    {reportStatistics.matched_entries === reportStatistics.total_rows && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Saved Entries:</span>
                                                                                <a>{reportStatistics.saved_entries}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Skipped Entries:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.skipped_entries > 0 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.skipped_entries}
                                                                                    {reportStatistics.skipped_entries === 0 && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Error Entries:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.error_entries > 0 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.error_entries}
                                                                                    {reportStatistics.error_entries === 0 && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            </Col>
                                                        )}

                                                        {reportStatistics?.distribution && (
                                                            <Col md="4">
                                                                <Card className="h-100">
                                                                    <CardBody className="p-3">
                                                                        <h6 className="mb-2">Distribution Counts</h6>
                                                                        <div className="small-stats">
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>DSP Distribution:</span>
                                                                                <a>{reportStatistics.distribution?.saving_dsp_distribution ?? reportStatistics.distribution?.dsp_distribution}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Country Distribution:</span>
                                                                                <a>{reportStatistics.distribution?.saving_country_distribution ?? reportStatistics.distribution?.country_distribution}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Title Distribution:</span>
                                                                                <a>{reportStatistics.distribution?.saving_title_distribution ?? reportStatistics.distribution?.title_distribution}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Type Sale Distribution:</span>
                                                                                <a>{reportStatistics.distribution?.saving_typesale_distribution ?? reportStatistics.distribution?.typesale_distribution}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Total:</span>
                                                                                <a>{reportStatistics.distribution?.total}</a>
                                                                            </div>
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            </Col>
                                                        )}

                                                        {reportStatistics?.validation && (
                                                            <Col md="4">
                                                                <Card className="h-100">
                                                                    <CardBody className="p-3">
                                                                        <h6 className="mb-2">Validation Data</h6>
                                                                        <div className="small-stats">
                                                                            <h6 className="text-muted mb-2 small">Assets</h6>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Total:</span>
                                                                                <a>{reportStatistics.validation?.assets?.total}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Matched:</span>
                                                                                <a>{reportStatistics.validation?.assets?.matched}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Missing:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.validation?.assets?.missing > 0 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.validation?.assets?.missing}
                                                                                    {reportStatistics.validation?.assets?.missing === 0 && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Match %:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.validation?.assets?.match_percentage < 100 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.validation?.assets?.match_percentage}%
                                                                                    {reportStatistics.validation?.assets?.match_percentage === 100 && ' ✓'}
                                                                                </a>
                                                                            </div>

                                                                            <h6 className="text-muted mb-2 mt-2 small">Products</h6>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Total:</span>
                                                                                <a>{reportStatistics.validation?.products?.total}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Matched:</span>
                                                                                <a>{reportStatistics.validation?.products?.matched}</a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Missing:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.validation?.products?.missing > 0 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.validation?.products?.missing}
                                                                                    {reportStatistics.validation?.products?.missing === 0 && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                <span>Match %:</span>
                                                                                <a style={{
                                                                                    color: reportStatistics.validation?.products?.match_percentage < 100 ? '#dc3545' : '#28a745'
                                                                                }}>
                                                                                    {reportStatistics.validation?.products?.match_percentage}%
                                                                                    {reportStatistics.validation?.products?.match_percentage === 100 && ' ✓'}
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </div>
                                            </Row>
                                        </div>

                                        {/* Processing Report Data Section - PRIMARY SOURCE */}
                                        {processingReportData && (
                                            <div className="mb-4">
                                                <h6 className="section-title">Processing Report Data</h6>
                                                <Row>
                                                    <Col md="12">
                                                        <Card>
                                                            <CardHeader>
                                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                    {isLoading && (
                                                                        <div className="" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </CardHeader>
                                                            <CardBody>
                                                                {isLoading ? (
                                                                    <div className="text-center p-4">
                                                                        <div className="spinner-border text-primary" role="status">
                                                                            <span className="visually-hidden">Loading processing report data...</span>
                                                                        </div>
                                                                        <p className="mt-3">Loading processing report data...</p>
                                                                    </div>
                                                                ) : isError ? (
                                                                    <div className="alert alert-danger">
                                                                        <h6 className="alert-heading">Error Loading Processing Report</h6>
                                                                        <p className="mb-0">
                                                                            {error?.message || 'Failed to load processing report data'}
                                                                        </p>
                                                                    </div>
                                                                ) : processingReportData ? (
                                                                    <div>
                                                                        {/* Processing Metadata */}
                                                                        {processingReportData.processing_metadata && (
                                                                            <div className="mb-4">
                                                                                <h6>Processing Metadata</h6>
                                                                                <Row>
                                                                                    <Col md="6">
                                                                                        <Table bordered responsive size="sm">
                                                                                            <tbody>
                                                                                                <tr>
                                                                                                    <th style={{ width: '200px', backgroundColor: '#f8f9fa' }}>Total Rows</th>
                                                                                                    <td>{processingReportData.processing_metadata.total_rows?.toLocaleString()}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Processed Rows</th>
                                                                                                    <td>{processingReportData.processing_metadata.processed_rows?.toLocaleString()}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Error Count</th>
                                                                                                    <td>
                                                                                                        <Badge color={processingReportData.processing_metadata.error_count === 0 ? 'success' : 'danger'}>
                                                                                                            {processingReportData.processing_metadata.error_count}
                                                                                                        </Badge>
                                                                                                    </td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Chunks Processed</th>
                                                                                                    <td>{processingReportData.processing_metadata.chunks_processed}</td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </Col>
                                                                                    <Col md="6">
                                                                                        <Table bordered responsive size="sm">
                                                                                            <tbody>
                                                                                                <tr>
                                                                                                    <th style={{ width: '200px', backgroundColor: '#f8f9fa' }}>Unique ISRCs</th>
                                                                                                    <td>{processingReportData.processing_metadata.unique_isrcs}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Total Records</th>
                                                                                                    <td>{processingReportData.processing_metadata.total_records?.toLocaleString()}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Total Revenue</th>
                                                                                                    <td>{processingReportData.processing_metadata.total_revenue?.toFixed(2)} {report?.provider_currency || 'EUR'}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Total Streams</th>
                                                                                                    <td>{processingReportData.processing_metadata.total_streams?.toLocaleString()}</td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </Col>
                                                                                </Row>
                                                                                <div className="mt-3">
                                                                                    <p className="text-muted small">
                                                                                        <strong>Processing Date:</strong> {moment(processingReportData.processing_metadata.processing_date).format('YYYY-MM-DD HH:mm:ss')}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Distribution Counts */}
                                                                        {processingReportData.distribution_counts && (
                                                                            <div className="mb-4">
                                                                                <h6>Distribution Counts</h6>
                                                                                <Row>
                                                                                    <Col md="3">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-primary">{processingReportData.distribution_counts.title_distribution}</h4>
                                                                                                <p className="mb-0">Title Distributions</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                    <Col md="3">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-info">{processingReportData.distribution_counts.dsp_distribution}</h4>
                                                                                                <p className="mb-0">DSP Distributions</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                    <Col md="3">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-success">{processingReportData.distribution_counts.country_distribution}</h4>
                                                                                                <p className="mb-0">Country Distributions</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                    <Col md="3">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-warning">{processingReportData.distribution_counts.typesale_distribution}</h4>
                                                                                                <p className="mb-0">Type Sale Distributions</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                </Row>
                                                                            </div>
                                                                        )}

                                                                        {/* Top Analysis */}
                                                                        {processingReportData.top_analysis && (
                                                                            <div className="mb-4">
                                                                                <h6>Top Analysis</h6>
                                                                                <Row>
                                                                                    {/* Top Titles */}
                                                                                    {processingReportData.top_analysis.top_titles && (
                                                                                        <Col md="6" className="mb-3">
                                                                                            <Card>
                                                                                                <CardHeader>
                                                                                                    <h6 className="mb-0">Top Titles</h6>
                                                                                                </CardHeader>
                                                                                                <CardBody>
                                                                                                    <div className="table-responsive">
                                                                                                        <Table striped size="sm">
                                                                                                            <thead>
                                                                                                                <tr>
                                                                                                                    <th>Title</th>
                                                                                                                    <th>Revenue</th>
                                                                                                                    <th>Streams</th>
                                                                                                                </tr>
                                                                                                            </thead>
                                                                                                            <tbody>
                                                                                                                {processingReportData.top_analysis.top_titles.slice(0, 10).map((title, index) => (
                                                                                                                    <tr key={index}>
                                                                                                                        <td>{title.name}</td>
                                                                                                                        <td>{title.revenue?.toFixed(2)} {report?.provider_currency || 'EUR'}</td>
                                                                                                                        <td>{title.streams?.toLocaleString()}</td>
                                                                                                                    </tr>
                                                                                                                ))}
                                                                                                            </tbody>
                                                                                                        </Table>
                                                                                                    </div>
                                                                                                </CardBody>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    )}

                                                                                    {/* Top DSPs */}
                                                                                    {processingReportData.top_analysis.top_dsps && (
                                                                                        <Col md="6" className="mb-3">
                                                                                            <Card>
                                                                                                <CardHeader>
                                                                                                    <h6 className="mb-0">Top DSPs</h6>
                                                                                                </CardHeader>
                                                                                                <CardBody>
                                                                                                    <div className="table-responsive">
                                                                                                        <Table striped size="sm">
                                                                                                            <thead>
                                                                                                                <tr>
                                                                                                                    <th>DSP</th>
                                                                                                                    <th>Revenue</th>
                                                                                                                    <th>Streams</th>
                                                                                                                </tr>
                                                                                                            </thead>
                                                                                                            <tbody>
                                                                                                                {processingReportData.top_analysis.top_dsps.slice(0, 10).map((dsp, index) => (
                                                                                                                    <tr key={index}>
                                                                                                                        <td>{dsp.name}</td>
                                                                                                                        <td>{dsp.revenue?.toFixed(2)} {report?.provider_currency || 'EUR'}</td>
                                                                                                                        <td>{dsp.streams?.toLocaleString()}</td>
                                                                                                                    </tr>
                                                                                                                ))}
                                                                                                            </tbody>
                                                                                                        </Table>
                                                                                                    </div>
                                                                                                </CardBody>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    )}
                                                                                </Row>

                                                                                {/* Top Countries and Artists */}
                                                                                <Row>
                                                                                    {/* Top Countries */}
                                                                                    {processingReportData.top_analysis.top_countries && (
                                                                                        <Col md="6" className="mb-3">
                                                                                            <Card>
                                                                                                <CardHeader>
                                                                                                    <h6 className="mb-0">Top Countries</h6>
                                                                                                </CardHeader>
                                                                                                <CardBody>
                                                                                                    <div className="table-responsive">
                                                                                                        <Table striped size="sm">
                                                                                                            <thead>
                                                                                                                <tr>
                                                                                                                    <th>Country</th>
                                                                                                                    <th>Revenue</th>
                                                                                                                    <th>Streams</th>
                                                                                                                </tr>
                                                                                                            </thead>
                                                                                                            <tbody>
                                                                                                                {processingReportData.top_analysis.top_countries.slice(0, 10).map((country, index) => (
                                                                                                                    <tr key={index}>
                                                                                                                        <td>{country.name}</td>
                                                                                                                        <td>{country.revenue?.toFixed(2)} {report?.provider_currency || 'EUR'}</td>
                                                                                                                        <td>{country.streams?.toLocaleString()}</td>
                                                                                                                    </tr>
                                                                                                                ))}
                                                                                                            </tbody>
                                                                                                        </Table>
                                                                                                    </div>
                                                                                                </CardBody>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    )}

                                                                                    {/* Top Artists */}
                                                                                    {(processingReportData.top_analysis?.top_artists || processingReportData.top_artists) && (
                                                                                        <Col md="6" className="mb-3">
                                                                                            <Card>
                                                                                                <CardHeader>
                                                                                                    <h6 className="mb-0">Top Artists</h6>
                                                                                                </CardHeader>
                                                                                                <CardBody>
                                                                                                    <div className="table-responsive">
                                                                                                        <Table striped size="sm">
                                                                                                            <thead>
                                                                                                                <tr>
                                                                                                                    <th>Artist</th>
                                                                                                                    <th>Revenue</th>
                                                                                                                    <th>Streams</th>
                                                                                                                    <th>Count</th>
                                                                                                                </tr>
                                                                                                            </thead>
                                                                                                            <tbody>
                                                                                                                {(processingReportData.top_analysis?.top_artists || processingReportData.top_artists || []).slice(0, 10).map((artist, index) => (
                                                                                                                    <tr key={index}>
                                                                                                                        <td>{artist.name}</td>
                                                                                                                        <td>
                                                                                                                            {artist.revenue !== null && artist.revenue !== undefined 
                                                                                                                                ? `${Number(artist.revenue).toFixed(2)} ${report?.provider_currency || 'EUR'}` 
                                                                                                                                : 'N/A'}
                                                                                                                        </td>
                                                                                                                        <td>
                                                                                                                            {artist.streams !== null && artist.streams !== undefined 
                                                                                                                                ? artist.streams.toLocaleString() 
                                                                                                                                : 'N/A'}
                                                                                                                        </td>
                                                                                                                        <td>
                                                                                                                            {artist.count !== null && artist.count !== undefined 
                                                                                                                                ? artist.count.toLocaleString() 
                                                                                                                                : artist.titles_count !== null && artist.titles_count !== undefined
                                                                                                                                    ? artist.titles_count.toLocaleString()
                                                                                                                                    : 'N/A'}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                ))}
                                                                                                            </tbody>
                                                                                                        </Table>
                                                                                                    </div>
                                                                                                </CardBody>
                                                                                            </Card>
                                                                                        </Col>
                                                                                    )}
                                                                                </Row>
                                                                            </div>
                                                                        )}

                                                                        {/* Data Quality */}
                                                                        {processingReportData.data_quality && (
                                                                            <div className="mb-4">
                                                                                <h6>Data Quality</h6>
                                                                                <Row>
                                                                                    <Col md="4">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-success">{processingReportData.data_quality.completeness_score}%</h4>
                                                                                                <p className="mb-0">Completeness Score</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                    <Col md="4">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-primary">{processingReportData.data_quality.data_quality_grade}</h4>
                                                                                                <p className="mb-0">Quality Grade</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                    <Col md="4">
                                                                                        <Card className="text-center">
                                                                                            <CardBody>
                                                                                                <h4 className="text-warning">{processingReportData.data_quality.anomalies?.length || 0}</h4>
                                                                                                <p className="mb-0">Anomalies Found</p>
                                                                                            </CardBody>
                                                                                        </Card>
                                                                                    </Col>
                                                                                </Row>
                                                                                {processingReportData.data_quality.anomalies && processingReportData.data_quality.anomalies.length > 0 && (
                                                                                    <div className="mt-3">
                                                                                        <h6>Data Anomalies</h6>
                                                                                        {processingReportData.data_quality.anomalies.map((anomaly, index) => (
                                                                                            <div key={index} className="alert alert-warning">
                                                                                                <strong>{anomaly.type}:</strong> {anomaly.description}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {/* Raw JSON Data */}
                                                                        <div className="mt-3">
                                                                            <h6>Raw JSON Data</h6>
                                                                            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto', fontSize: '12px' }}>
                                                                                {JSON.stringify(processingReportData, null, 2)}
                                                                            </pre>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center p-4">
                                                                        <i className="fa fa-file-code fa-3x text-muted mb-3"></i>
                                                                        <p className="text-muted">No processing report data available</p>
                                                                    </div>
                                                                )}
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}


                                        {report.various_artists_distributions && (
                                            <div className="mb-4">

                                                    <CardHeader>
                                                        <h5 className="mb-0">Unknown Artist Distributions</h5>
                                                        {report?.various_artists_distributions?.counts && (
                                                            <div className="mt-3 d-flex flex-wrap" style={{ gap: '10px' }}>
                                                                <Badge color="primary" className="p-2" style={{ fontWeight: '500' }}>
                                                                    Title Distributions: {report.various_artists_distributions.counts.title_distribution || 0}
                                                                </Badge>
                                                                <Badge color="info"  style={{ fontWeight: '500' }}>
                                                                    DSP Distributions: {report.various_artists_distributions.counts.dsp_distribution || 0}
                                                                </Badge>
                                                                <Badge color="success"  style={{ fontWeight: '500' }}>
                                                                    Country Distributions: {report.various_artists_distributions.counts.country_distribution || 0}
                                                                </Badge>
                                                                <Badge color="warning"  style={{ fontWeight: '500' }}>
                                                                    Type Sale Distributions: {report.various_artists_distributions.counts.typesale_distribution || 0}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </CardHeader>
                                                    <CardBody>
                                                        {/* Custom tabs with better styling */}
                                                        <div className="nav-tabs-custom">
                                                            <ul className="nav nav-tabs nav-fill" id="distribution-tabs" role="tablist">
                                                                <li className="nav-item" role="presentation">
                                                                    <button
                                                                        className="nav-link active"
                                                                        id="title-tab"
                                                                        data-bs-toggle="tab"
                                                                        data-bs-target="#title-distributions"
                                                                        type="button"
                                                                        role="tab"
                                                                        aria-controls="title-distributions"
                                                                        aria-selected="true"
                                                                    >
                                                                        Title Distributions
                                                                    </button>
                                                                </li>
                                                                <li className="nav-item" role="presentation">
                                                                    <button
                                                                        className="nav-link"
                                                                        id="dsp-tab"
                                                                        data-bs-toggle="tab"
                                                                        data-bs-target="#dsp-distributions"
                                                                        type="button"
                                                                        role="tab"
                                                                        aria-controls="dsp-distributions"
                                                                        aria-selected="false"
                                                                    >
                                                                        DSP Distributions
                                                                    </button>
                                                                </li>
                                                                <li className="nav-item" role="presentation">
                                                                    <button
                                                                        className="nav-link"
                                                                        id="country-tab"
                                                                        data-bs-toggle="tab"
                                                                        data-bs-target="#country-distributions"
                                                                        type="button"
                                                                        role="tab"
                                                                        aria-controls="country-distributions"
                                                                        aria-selected="false"
                                                                    >
                                                                        Country Distributions
                                                                    </button>
                                                                </li>
                                                                <li className="nav-item" role="presentation">
                                                                    <button
                                                                        className="nav-link"
                                                                        id="typesale-tab"
                                                                        data-bs-toggle="tab"
                                                                        data-bs-target="#typesale-distributions"
                                                                        type="button"
                                                                        role="tab"
                                                                        aria-controls="typesale-distributions"
                                                                        aria-selected="false"
                                                                    >
                                                                        Type Sale Distributions
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>

                                                        <div className="tab-content" id="distributionTabContent">
                                                            <div
                                                                className="tab-pane fade show active"
                                                                id="title-distributions"
                                                                role="tabpanel"
                                                                aria-labelledby="title-tab"
                                                            >
                                                                {isTitleLoading ? (
                                                                    <div className="text-center p-3">
                                                                        <div className="spinner-border text-primary" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                        <p className="mt-2">Loading title distribution data...</p>
                                                                    </div>
                                                                ) : report?.various_artists_distributions?.title_distribution && Array.isArray(report.various_artists_distributions.title_distribution) && report.various_artists_distributions.title_distribution.length > 0 ? (
                                                                    <div className="table-responsive mt-3">
                                                                        <Table bordered striped hover className="align-middle">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Title</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Revenue</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Streams</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Artist</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Correct Artist</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Error</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Action</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {getPaginatedData(report?.various_artists_distributions?.title_distribution, 'title_distribution').map((item, index) => (
                                                                                    <tr key={index}>
                                                                                        <td>{item.title_name}</td>
                                                                                        <td>{item.asset_isrc}</td>
                                                                                        <td>{item.product_upc}</td>
                                                                                        <td>{parseFloat(item.total_revenue).toFixed(2)} {report.provider_currency}</td>
                                                                                        <td>{item.streams}</td>
                                                                                        <td style={{ color: item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? "#dc3545" : "" }}>
                                                                                            <a>Unknown Artist</a>
                                                                                        </td>
                                                                                        <td>
                                                                                            <div className="artist-name-container">
                                                                                                {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                    <>
                                                                                                        <span className="artist-dot"></span>
                                                                                                        <span className="artist-name">{item.correct_product.artist_name}</span>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <span>{item.correct_product ? item.correct_product.artist_name : "Unknown Artist"}</span>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                        <td>
                                                                                            {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-warning me-1" />
                                                                                                    <span className="text-warning">
                                                                                                        <a>Artist is incorrect</a>
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : item.correct_product ? (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-warning me-1" />
                                                                                                    <span className="text-warning">
                                                                                                        Product is correct but asset needs review
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-danger me-1" />
                                                                                                    <span className="text-danger">Pending Discovery</span>
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            <Button
                                                                                                color="primary"
                                                                                                size="sm"
                                                                                                className="btn-icon"
                                                                                                onClick={() => openEditModal(item)}
                                                                                            >
                                                                                                <Edit size={14} />
                                                                                            </Button>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                                {report?.various_artists_distributions?.title_distribution && report.various_artists_distributions.title_distribution.length === 0 && (
                                                                                    <tr>
                                                                                        <td colSpan="9" className="text-center">No data available</td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </Table>

                                                                        {/* Pagination for Title Distribution */}
                                                                        {report?.various_artists_distributions?.pagination?.title_distribution?.total > 0 && (
                                                                            <TablePagination
                                                                                totalItems={report.various_artists_distributions.pagination?.title_distribution?.total || 0}
                                                                                totalPages={report.various_artists_distributions.pagination?.title_distribution?.total_pages || 0}
                                                                                itemsPerPage={report.various_artists_distributions.pagination?.title_distribution?.per_page || 10}
                                                                                currentPage={report.various_artists_distributions.pagination?.title_distribution?.current_page || 1}
                                                                                onPageChange={handlePageChange}
                                                                                onItemsPerPageChange={handleItemsPerPageChange}
                                                                                type="title_distribution"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="alert alert-info mt-3">No title distribution data available</div>
                                                                )}
                                                            </div>

                                                            <div
                                                                className="tab-pane fade"
                                                                id="dsp-distributions"
                                                                role="tabpanel"
                                                                aria-labelledby="dsp-tab"
                                                            >
                                                                {isDspLoading ? (
                                                                    <div className="text-center p-3">
                                                                        <div className="spinner-border text-primary" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                        <p className="mt-2">Loading DSP distribution data...</p>
                                                                    </div>
                                                                ) : report?.various_artists_distributions?.dsp_distribution && Array.isArray(report.various_artists_distributions.dsp_distribution) && report.various_artists_distributions.dsp_distribution.length > 0 ? (
                                                                    <div className="table-responsive mt-3">
                                                                        <Table bordered striped hover className="align-middle">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>DSP</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Revenue</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Streams</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Artist</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Correct Artist</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Error</th>
                                                                                    <th style={{ backgroundColor: '#f8f9fa' }}>Action</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {getPaginatedData(report?.various_artists_distributions?.dsp_distribution, 'dsp_distribution').map((item, index) => (
                                                                                    <tr key={index}>
                                                                                        <td>{item.dsp_name}</td>
                                                                                        <td>{item.asset_isrc}</td>
                                                                                        <td>{item.product_upc}</td>
                                                                                        <td>{parseFloat(item.total_revenue).toFixed(2)} {report.provider_currency}</td>
                                                                                        <td>{item.streams}</td>
                                                                                        <td style={{ color: item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? "#dc3545" : "" }}>
                                                                                            <a>Unknown Artist</a>
                                                                                        </td>
                                                                                        <td>
                                                                                            <div className="artist-name-container">
                                                                                                {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                    <>
                                                                                                        <span className="artist-dot"></span>
                                                                                                        <span className="artist-name">{item.correct_product.artist_name}</span>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <span>{item.correct_product ? item.correct_product.artist_name : "Unknown Artist"}</span>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                        <td>
                                                                                            {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-warning me-1" />
                                                                                                    <span className="text-warning">
                                                                                                        <a>Artist is incorrect</a>
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : item.correct_product ? (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-warning me-1" />
                                                                                                    <span className="text-warning">
                                                                                                        DSP data needs review
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <AlertCircle size={16} className="text-danger me-1" />
                                                                                                    <span className="text-danger">Pending Discovery</span>
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            <Button
                                                                                                color="primary"
                                                                                                size="sm"
                                                                                                className="btn-icon"
                                                                                                onClick={() => openEditModal(item)}
                                                                                            >
                                                                                                <Edit size={14} />
                                                                                            </Button>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                                {report?.various_artists_distributions?.dsp_distribution && report.various_artists_distributions.dsp_distribution.length === 0 && (
                                                                                    <tr>
                                                                                        <td colSpan="9" className="text-center">No data available</td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </Table>

                                                                        {/* Pagination for DSP Distribution */}
                                                                        {report?.various_artists_distributions?.pagination?.dsp_distribution?.total > 0 && (
                                                                            <TablePagination
                                                                                totalItems={report.various_artists_distributions.pagination?.dsp_distribution?.total || 0}
                                                                                totalPages={report.various_artists_distributions.pagination?.dsp_distribution?.total_pages || 0}
                                                                                itemsPerPage={report.various_artists_distributions.pagination?.dsp_distribution?.per_page || 10}
                                                                                currentPage={report.various_artists_distributions.pagination?.dsp_distribution?.current_page || 1}
                                                                                onPageChange={handlePageChange}
                                                                                onItemsPerPageChange={handleItemsPerPageChange}
                                                                                type="dsp_distribution"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="alert alert-info mt-3">No DSP distribution data available</div>
                                                                )}
                                                            </div>

                                                            <div
                                                                className="tab-pane fade"
                                                                id="country-distributions"
                                                                role="tabpanel"
                                                                aria-labelledby="country-tab"
                                                            >
                                                                {isCountryLoading ? (
                                                                    <div className="text-center p-3">
                                                                        <div className="spinner-border text-primary" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                        <p className="mt-2">Loading country distribution data...</p>
                                                                    </div>
                                                                ) : report?.various_artists_distributions?.country_distribution ? (
                                                                    Array.isArray(report.various_artists_distributions.country_distribution) &&
                                                                    report.various_artists_distributions.country_distribution.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Country</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Revenue</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Streams</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Artist</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Correct Artist</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Error</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Action</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {getPaginatedData(report?.various_artists_distributions?.country_distribution, 'country_distribution').map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.country_name}</td>
                                                                                            <td>{item.asset_isrc}</td>
                                                                                            <td>{item.product_upc}</td>
                                                                                            <td>{parseFloat(item.total_revenue).toFixed(2)} {report.provider_currency}</td>
                                                                                            <td>{item.streams}</td>
                                                                                            <td style={{ color: item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? "#dc3545" : "" }}>
                                                                                                <a>Unknown Artist</a>
                                                                                            </td>
                                                                                            <td>
                                                                                                <div className="artist-name-container">
                                                                                                    {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                        <>
                                                                                                            <span className="artist-dot"></span>
                                                                                                            <span className="artist-name">{item.correct_product.artist_name}</span>
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <span>{item.correct_product ? item.correct_product.artist_name : "Unknown Artist"}</span>
                                                                                                    )}
                                                                                                </div>
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-warning me-1" />
                                                                                                        <span className="text-warning">
                                                                                                            <a>Artist is incorrect</a>
                                                                                                        </span>
                                                                                                    </div>
                                                                                                ) : item.correct_product ? (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-warning me-1" />
                                                                                                        <span className="text-warning">
                                                                                                            Country data needs review
                                                                                                        </span>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-danger me-1" />
                                                                                                        <span className="text-danger">Pending Discovery</span>
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openEditModal(item)}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                            {/* Pagination for Country Distribution */}
                                                                            {report?.various_artists_distributions?.pagination?.country_distribution?.total > 0 && (
                                                                                <TablePagination
                                                                                    totalItems={report.various_artists_distributions.pagination?.country_distribution?.total || 0}
                                                                                    totalPages={report.various_artists_distributions.pagination?.country_distribution?.total_pages || 0}
                                                                                    itemsPerPage={report.various_artists_distributions.pagination?.country_distribution?.per_page || 10}
                                                                                    currentPage={report.various_artists_distributions.pagination?.country_distribution?.current_page || 1}
                                                                                    onPageChange={handlePageChange}
                                                                                    onItemsPerPageChange={handleItemsPerPageChange}
                                                                                    type="country_distribution"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">
                                                                            {report?.various_artists_distributions?.counts?.country_distribution ?
                                                                                `${report.various_artists_distributions.counts.country_distribution} country distribution records available.` :
                                                                                'Country distribution data is available.'}
                                                                            Use the export function to download the complete data.
                                                                        </div>
                                                                    )
                                                                ) : (
                                                                    <div className="alert alert-info mt-3">No country distribution data available</div>
                                                                )}
                                                            </div>

                                                            <div
                                                                className="tab-pane fade"
                                                                id="typesale-distributions"
                                                                role="tabpanel"
                                                                aria-labelledby="typesale-tab"
                                                            >
                                                                {isTypesaleLoading ? (
                                                                    <div className="text-center p-3">
                                                                        <div className="spinner-border text-primary" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                        <p className="mt-2">Loading type sale distribution data...</p>
                                                                    </div>
                                                                ) : report?.various_artists_distributions?.typesale_distribution ? (
                                                                    Array.isArray(report.various_artists_distributions.typesale_distribution) &&
                                                                    report.various_artists_distributions.typesale_distribution.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Sale Type</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Revenue</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Streams</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Artist</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Correct Artist</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Error</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>Action</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {getPaginatedData(report?.various_artists_distributions?.typesale_distribution, 'typesale_distribution').map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.typesale_name}</td>
                                                                                            <td>{item.asset_isrc}</td>
                                                                                            <td>{item.product_upc}</td>
                                                                                            <td>{parseFloat(item.total_revenue).toFixed(2)} {report.provider_currency}</td>
                                                                                            <td>{item.streams}</td>
                                                                                            <td style={{ color: item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? "#dc3545" : "" }}>
                                                                                                <a>Unknown Artist</a>
                                                                                            </td>
                                                                                            <td>
                                                                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                                                                    {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                        <>

                                                                                                            <span style={{ color: "#28a745" }}>{item.correct_product.artist_name}</span>
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <span>{item.correct_product ? item.correct_product.artist_name : "Unknown Artist"}</span>
                                                                                                    )}
                                                                                                </div>
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.correct_product && item.correct_product.artist_name !== "Unknown Artist" ? (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-warning me-1" />
                                                                                                        <span className="text-warning">
                                                                                                            <a>Artist is incorrect</a>
                                                                                                        </span>
                                                                                                    </div>
                                                                                                ) : item.correct_product ? (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-warning me-1" />
                                                                                                        <span className="text-warning">
                                                                                                            Sale type data needs review
                                                                                                        </span>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <AlertCircle size={16} className="text-danger me-1" />
                                                                                                        <span className="text-danger">Pending Discovery</span>
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openEditModal(item)}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                            {/* Pagination for Type Sale Distribution */}
                                                                            {report?.various_artists_distributions?.pagination?.typesale_distribution?.total > 0 && (
                                                                                <TablePagination
                                                                                    totalItems={report.various_artists_distributions.pagination?.typesale_distribution?.total || 0}
                                                                                    totalPages={report.various_artists_distributions.pagination?.typesale_distribution?.total_pages || 0}
                                                                                    itemsPerPage={report.various_artists_distributions.pagination?.typesale_distribution?.per_page || 10}
                                                                                    currentPage={report.various_artists_distributions.pagination?.typesale_distribution?.current_page || 1}
                                                                                    onPageChange={handlePageChange}
                                                                                    onItemsPerPageChange={handleItemsPerPageChange}
                                                                                    type="typesale_distribution"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">
                                                                            {report?.various_artists_distributions?.counts?.typesale_distribution ?
                                                                                `${report.various_artists_distributions.counts.typesale_distribution} type sale distribution records available.` :
                                                                                'Type sale distribution data is available.'}
                                                                            Use the export function to download the complete data.
                                                                        </div>
                                                                    )
                                                                ) : (
                                                                    <div className="alert alert-info mt-3">No type sale distribution data available</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardBody>

                                            </div>
                                        )}

                                        {/* Artist For Review (AFR) Section */}
                                        <div className="mb-4">
                                            <Card>
                                                <CardHeader>
                                                    <h5 className="mb-0">Artist For Review (AFR)</h5>
                                                </CardHeader>
                                                <CardBody>
                                                    <p className="text-muted small mb-3">
                                                        Unmatched distribution rows for this income (placeholder ISRC/UPC, unknown artist, or Artist For Review).
                                                    </p>

                                                    {id ? (
                                                        <>
                                                            {/* Custom tabs with better styling */}
                                                            <div className="nav-tabs-custom">
                                                                <ul className="nav nav-tabs nav-fill" id="afr-distribution-tabs" role="tablist">
                                                                    <li className="nav-item" role="presentation">
                                                                        <button
                                                                            className="nav-link active"
                                                                            id="afr-title-tab"
                                                                            data-bs-toggle="tab"
                                                                            data-bs-target="#afr-title-distributions"
                                                                            type="button"
                                                                            role="tab"
                                                                            aria-controls="afr-title-distributions"
                                                                            aria-selected="true"
                                                                        >
                                                                            Title Distributions
                                                                        </button>
                                                                    </li>
                                                                    <li className="nav-item" role="presentation">
                                                                        <button
                                                                            className="nav-link"
                                                                            id="afr-dsp-tab"
                                                                            data-bs-toggle="tab"
                                                                            data-bs-target="#afr-dsp-distributions"
                                                                            type="button"
                                                                            role="tab"
                                                                            aria-controls="afr-dsp-distributions"
                                                                            aria-selected="false"
                                                                        >
                                                                            DSP Distributions
                                                                        </button>
                                                                    </li>
                                                                    <li className="nav-item" role="presentation">
                                                                        <button
                                                                            className="nav-link"
                                                                            id="afr-country-tab"
                                                                            data-bs-toggle="tab"
                                                                            data-bs-target="#afr-country-distributions"
                                                                            type="button"
                                                                            role="tab"
                                                                            aria-controls="afr-country-distributions"
                                                                            aria-selected="false"
                                                                        >
                                                                            Country Distributions
                                                                        </button>
                                                                    </li>
                                                                    <li className="nav-item" role="presentation">
                                                                        <button
                                                                            className="nav-link"
                                                                            id="afr-typesale-tab"
                                                                            data-bs-toggle="tab"
                                                                            data-bs-target="#afr-typesale-distributions"
                                                                            type="button"
                                                                            role="tab"
                                                                            aria-controls="afr-typesale-distributions"
                                                                            aria-selected="false"
                                                                        >
                                                                            Type Sale Distributions
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>

                                                            <div className="tab-content" id="afrDistributionTabContent">
                                                                {/* Title Distributions Tab */}
                                                                <div
                                                                    className="tab-pane fade show active"
                                                                    id="afr-title-distributions"
                                                                    role="tabpanel"
                                                                    aria-labelledby="afr-title-tab"
                                                                >
                                                                    {afrLoading.title ? (
                                                                        <div className="text-center p-3">
                                                                            <div className="spinner-border text-primary" role="status">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                            </div>
                                                                            <p className="mt-2">Loading title distribution data...</p>
                                                                        </div>
                                                                    ) : afrData.title && Array.isArray(afrData.title) && afrData.title.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>TITLE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>REVENUE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>STREAMS</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>CORRECT ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ERROR</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ACTION</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {afrData.title.map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.name || item.title_name || 'N/A'}</td>
                                                                                            <td>{item.asset_isrc || 'N/A'}</td>
                                                                                            <td>{item.product_upc || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.revenue_streams || item.revenue_downloads || item.total_revenue
                                                                                                    ? `${parseFloat((parseFloat(item.revenue_streams || 0) + parseFloat(item.revenue_downloads || 0) || item.total_revenue || 0)).toFixed(2)} ${item.artist_currency || report?.provider_currency || 'EUR'}`
                                                                                                    : 'N/A'}
                                                                                            </td>
                                                                                            <td>{item.streams ? item.streams.toLocaleString() : 'N/A'}</td>
                                                                                            <td>{item.artist_name || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.correct_artist_name || item.correct_product?.artist_name || 'N/A'}
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.artist_name && item.correct_artist_name && item.artist_name !== item.correct_artist_name ? (
                                                                                                    <Badge color="warning">Artist Mismatch</Badge>
                                                                                                ) : (
                                                                                                    <Badge color="success">OK</Badge>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openAfrEditModal(item, 'title')}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">No title distribution data available for this artist</div>
                                                                    )}
                                                                </div>

                                                                {/* DSP Distributions Tab */}
                                                                <div
                                                                    className="tab-pane fade"
                                                                    id="afr-dsp-distributions"
                                                                    role="tabpanel"
                                                                    aria-labelledby="afr-dsp-tab"
                                                                >
                                                                    {afrLoading.dsp ? (
                                                                        <div className="text-center p-3">
                                                                            <div className="spinner-border text-primary" role="status">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                            </div>
                                                                            <p className="mt-2">Loading DSP distribution data...</p>
                                                                        </div>
                                                                    ) : afrData.dsp && Array.isArray(afrData.dsp) && afrData.dsp.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>DSP</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>REVENUE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>STREAMS</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>CORRECT ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ERROR</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ACTION</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {afrData.dsp.map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.name || item.dsp_name || 'N/A'}</td>
                                                                                            <td>{item.asset_isrc || 'N/A'}</td>
                                                                                            <td>{item.product_upc || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.revenue_streams || item.revenue_downloads || item.total_revenue
                                                                                                    ? `${parseFloat((parseFloat(item.revenue_streams || 0) + parseFloat(item.revenue_downloads || 0) || item.total_revenue || 0)).toFixed(2)} ${item.artist_currency || report?.provider_currency || 'EUR'}`
                                                                                                    : 'N/A'}
                                                                                            </td>
                                                                                            <td>{item.streams ? item.streams.toLocaleString() : 'N/A'}</td>
                                                                                            <td>{item.artist_name || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.correct_artist_name || item.correct_product?.artist_name || 'N/A'}
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.artist_name && item.correct_artist_name && item.artist_name !== item.correct_artist_name ? (
                                                                                                    <Badge color="warning">Artist Mismatch</Badge>
                                                                                                ) : (
                                                                                                    <Badge color="success">OK</Badge>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openAfrEditModal(item, 'dsp')}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">No DSP distribution data available for this artist</div>
                                                                    )}
                                                                </div>

                                                                {/* Country Distributions Tab */}
                                                                <div
                                                                    className="tab-pane fade"
                                                                    id="afr-country-distributions"
                                                                    role="tabpanel"
                                                                    aria-labelledby="afr-country-tab"
                                                                >
                                                                    {afrLoading.country ? (
                                                                        <div className="text-center p-3">
                                                                            <div className="spinner-border text-primary" role="status">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                            </div>
                                                                            <p className="mt-2">Loading country distribution data...</p>
                                                                        </div>
                                                                    ) : afrData.country && Array.isArray(afrData.country) && afrData.country.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>COUNTRY</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>REVENUE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>STREAMS</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>CORRECT ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ERROR</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ACTION</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {afrData.country.map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.name || item.country_name || 'N/A'}</td>
                                                                                            <td>{item.asset_isrc || 'N/A'}</td>
                                                                                            <td>{item.product_upc || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.revenue_streams || item.revenue_downloads || item.total_revenue
                                                                                                    ? `${parseFloat((parseFloat(item.revenue_streams || 0) + parseFloat(item.revenue_downloads || 0) || item.total_revenue || 0)).toFixed(2)} ${item.artist_currency || report?.provider_currency || 'EUR'}`
                                                                                                    : 'N/A'}
                                                                                            </td>
                                                                                            <td>{item.streams ? item.streams.toLocaleString() : 'N/A'}</td>
                                                                                            <td>{item.artist_name || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.correct_artist_name || item.correct_product?.artist_name || 'N/A'}
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.artist_name && item.correct_artist_name && item.artist_name !== item.correct_artist_name ? (
                                                                                                    <Badge color="warning">Artist Mismatch</Badge>
                                                                                                ) : (
                                                                                                    <Badge color="success">OK</Badge>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openAfrEditModal(item, 'country')}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">No country distribution data available for this artist</div>
                                                                    )}
                                                                </div>

                                                                {/* Type Sale Distributions Tab */}
                                                                <div
                                                                    className="tab-pane fade"
                                                                    id="afr-typesale-distributions"
                                                                    role="tabpanel"
                                                                    aria-labelledby="afr-typesale-tab"
                                                                >
                                                                    {afrLoading.typesale ? (
                                                                        <div className="text-center p-3">
                                                                            <div className="spinner-border text-primary" role="status">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                            </div>
                                                                            <p className="mt-2">Loading type sale distribution data...</p>
                                                                        </div>
                                                                    ) : afrData.typesale && Array.isArray(afrData.typesale) && afrData.typesale.length > 0 ? (
                                                                        <div className="table-responsive mt-3">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>SALE TYPE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>REVENUE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>STREAMS</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>CORRECT ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ERROR</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ACTION</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {afrData.typesale.map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td>{item.name || item.typesale_name || 'N/A'}</td>
                                                                                            <td>{item.asset_isrc || 'N/A'}</td>
                                                                                            <td>{item.product_upc || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.revenue_streams || item.revenue_downloads || item.total_revenue
                                                                                                    ? `${parseFloat((parseFloat(item.revenue_streams || 0) + parseFloat(item.revenue_downloads || 0) || item.total_revenue || 0)).toFixed(2)} ${item.artist_currency || report?.provider_currency || 'EUR'}`
                                                                                                    : 'N/A'}
                                                                                            </td>
                                                                                            <td>{item.streams ? item.streams.toLocaleString() : 'N/A'}</td>
                                                                                            <td>{item.artist_name || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.correct_artist_name || item.correct_product?.artist_name || 'N/A'}
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.artist_name && item.correct_artist_name && item.artist_name !== item.correct_artist_name ? (
                                                                                                    <Badge color="warning">Artist Mismatch</Badge>
                                                                                                ) : (
                                                                                                    <Badge color="success">OK</Badge>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => openAfrEditModal(item, 'typesale')}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info mt-3">No type sale distribution data available for this artist</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Consolidated AFR Table - All Distribution Types Together */}
                                                            {selectedAfrArtistId && (
                                                                <div className="mt-4">
                                                                    <h6 className="mb-3" style={{ fontWeight: 'bold', fontSize: '16px' }}>All AFR Distributions (Consolidated View)</h6>
                                                                    {(afrLoading.title || afrLoading.dsp || afrLoading.country || afrLoading.typesale) ? (
                                                                        <div className="text-center p-3">
                                                                            <div className="spinner-border text-primary" role="status">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                            </div>
                                                                            <p className="mt-2">Loading consolidated AFR data...</p>
                                                                        </div>
                                                                    ) : afrConsolidatedData && afrConsolidatedData.length > 0 ? (
                                                                        <div className="table-responsive">
                                                                            <Table bordered striped hover className="align-middle">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>TYPE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>TITLE/DSP/COUNTRY/SALE TYPE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ISRC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>UPC</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>REVENUE</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>STREAMS</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>CORRECT ARTIST</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ERROR</th>
                                                                                        <th style={{ backgroundColor: '#f8f9fa' }}>ACTION</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {afrConsolidatedData.map((item, index) => (
                                                                                        <tr key={`${item.distribution_type}-${index}-${item.asset_isrc || item.product_upc || index}`}>
                                                                                            <td>
                                                                                                <Badge color={
                                                                                                    item.distribution_type === 'Title' ? 'primary' :
                                                                                                    item.distribution_type === 'DSP' ? 'info' :
                                                                                                    item.distribution_type === 'Country' ? 'success' :
                                                                                                    'warning'
                                                                                                }>
                                                                                                    {item.distribution_type}
                                                                                                </Badge>
                                                                                            </td>
                                                                                            <td>{item.name || 'N/A'}</td>
                                                                                            <td>{item.asset_isrc || 'N/A'}</td>
                                                                                            <td>{item.product_upc || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.revenue_streams || item.revenue_downloads || item.total_revenue
                                                                                                    ? `${parseFloat((parseFloat(item.revenue_streams || 0) + parseFloat(item.revenue_downloads || 0) || item.total_revenue || 0)).toFixed(2)} ${item.artist_currency || report?.provider_currency || 'EUR'}`
                                                                                                    : 'N/A'}
                                                                                            </td>
                                                                                            <td>{item.streams ? item.streams.toLocaleString() : 'N/A'}</td>
                                                                                            <td>{item.artist_name || 'N/A'}</td>
                                                                                            <td>
                                                                                                {item.correct_artist_name || item.correct_product?.artist_name || 'N/A'}
                                                                                            </td>
                                                                                            <td>
                                                                                                {item.artist_name && item.correct_artist_name && item.artist_name !== item.correct_artist_name ? (
                                                                                                    <Badge color="warning">Artist Mismatch</Badge>
                                                                                                ) : (
                                                                                                    <Badge color="success">OK</Badge>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <Button
                                                                                                    color="primary"
                                                                                                    size="sm"
                                                                                                    className="btn-icon"
                                                                                                    onClick={() => {
                                                                                                        const typeMap = {
                                                                                                            'Title': 'title',
                                                                                                            'DSP': 'dsp',
                                                                                                            'Country': 'country',
                                                                                                            'Type Sale': 'typesale'
                                                                                                        };
                                                                                                        openAfrEditModal(item, typeMap[item.distribution_type] || 'title');
                                                                                                    }}
                                                                                                >
                                                                                                    <Edit size={14} />
                                                                                                </Button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="alert alert-info">
                                                                            <strong>No consolidated AFR data available.</strong> Data will appear here once all distribution types finish loading. 
                                                                            <br />
                                                                            <small>Current data status: Title: {afrData.title ? `${afrData.title.length} items` : 'loading...'}, 
                                                                            DSP: {afrData.dsp ? `${afrData.dsp.length} items` : 'loading...'}, 
                                                                            Country: {afrData.country ? `${afrData.country.length} items` : 'loading...'}, 
                                                                            Type Sale: {afrData.typesale ? `${afrData.typesale.length} items` : 'loading...'}</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                        ) : (
                                                            <div className="alert alert-info mt-3">
                                                                Open an income report to view Artist For Review distribution data.
                                                            </div>
                                                        )}
                                                    </CardBody>
                                                </Card>
                                        </div>

                                        {dbData?.artist_info && (
                                            <div className="mb-4">
                                                <h6>Artist Information</h6>
                                                <Row>
                                                    <Col md="12">
                                                        <Table bordered responsive>
                                                            <tbody>
                                                                <tr>
                                                                    <th style={{ width: '20%' }}>Artists in File</th>
                                                                    <td>{dbData.artist_info.artists_in_file}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Artists in Database</th>
                                                                    <td>{dbData.artist_info.artists_in_database}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Total Revenue</th>
                                                                    <td>{dbData.artist_info.total_revenue?.toFixed(2)} {report.provider_currency}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Average Revenue Per Entry</th>
                                                                    <td>{dbData.artist_info.average_revenue_per_entry?.toFixed(2)} {report.provider_currency}</td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}

                                        {dbData?.artist_info && (
                                            <div className="mb-4">
                                                <h6>Artist Information</h6>
                                                <Row>
                                                    <Col md="12">
                                                        <Table bordered responsive>
                                                            <tbody>
                                                                <tr>
                                                                    <th style={{ width: '20%' }}>Artists in File</th>
                                                                    <td>{dbData.artist_info.artists_in_file}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Artists in Database</th>
                                                                    <td>{dbData.artist_info.artists_in_database}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Total Revenue</th>
                                                                    <td>{dbData.artist_info.total_revenue?.toFixed(2)} {report.provider_currency}</td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Average Revenue Per Entry</th>
                                                                    <td>{dbData.artist_info.average_revenue_per_entry?.toFixed(2)} {report.provider_currency}</td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}

                                        {dbData.top_artists && Array.isArray(dbData.top_artists) && dbData.top_artists.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Top Artists</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Artist</th>
                                                            <th>Count</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dbData.top_artists.map((artist, index) => (
                                                            <tr key={index}>
                                                                <td>{artist.name}</td>
                                                                <td>{artist.count}</td>
                                                                <td>{artist.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${artist.percentage}%` }}
                                                                            aria-valuenow={artist.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {dbData.top_titles && Array.isArray(dbData.top_titles) && dbData.top_titles.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Top Titles</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Count</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dbData.top_titles.map((title, index) => (
                                                            <tr key={index}>
                                                                <td>{title.name}</td>
                                                                <td>{title.count}</td>
                                                                <td>{title.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${title.percentage}%` }}
                                                                            aria-valuenow={title.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.top_dsps && report.top_dsps.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Top DSPs (Digital Service Providers)</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>DSP</th>
                                                            <th>Count</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.top_dsps.map((dsp, index) => (
                                                            <tr key={index}>
                                                                <td>{dsp.name}</td>
                                                                <td>{dsp.count}</td>
                                                                <td>{dsp.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${dsp.percentage}%` }}
                                                                            aria-valuenow={dsp.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.top_countries && report.top_countries.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Top Countries</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Country</th>
                                                            <th>Count</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.top_countries.map((country, index) => (
                                                            <tr key={index}>
                                                                <td>{country.name}</td>
                                                                <td>{country.count}</td>
                                                                <td>{country.revenue_percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${country.percentage}%` }}
                                                                            aria-valuenow={country.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.sale_types && report.sale_types.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Sale Types</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Type</th>
                                                            <th>Count</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.sale_types.map((type, index) => (
                                                            <tr key={index}>
                                                                <td>{type.name}</td>
                                                                <td>{type.count}</td>
                                                                <td>{type.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${type.percentage}%` }}
                                                                            aria-valuenow={type.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.revenue_by_country && report.revenue_by_country.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Revenue by Country</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Country</th>
                                                            <th>Revenue</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.revenue_by_country.slice(0, 15).map((country, index) => (
                                                            <tr key={index}>
                                                                <td>{country.name}</td>
                                                                <td>{country.revenue.toFixed(2)} {report.provider_currency}</td>
                                                                <td>{country.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${country.percentage}%` }}
                                                                            aria-valuenow={country.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {report.revenue_by_country.length > 15 && (
                                                    <div className="text-center mt-2">
                                                        <small className="text-muted">Showing top 15 of {report.revenue_by_country.length} countries</small>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {report.revenue_by_dsp && report.revenue_by_dsp.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Revenue by DSP</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>DSP</th>
                                                            <th>Revenue</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.revenue_by_dsp.map((dsp, index) => (
                                                            <tr key={index}>
                                                                <td>{dsp.name}</td>
                                                                <td>{dsp.revenue.toFixed(2)} {report.provider_currency}</td>
                                                                <td>{dsp.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${dsp.percentage}%` }}
                                                                            aria-valuenow={dsp.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.top_performing_assets && report.top_performing_assets.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Top Performing Assets</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>ISRC</th>
                                                            <th>Title</th>
                                                            <th>Artist</th>
                                                            <th>Streams</th>
                                                            <th>Revenue</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.top_performing_assets.map((asset, index) => (
                                                            <tr key={index}>
                                                                <td>{asset.isrc}</td>
                                                                <td>{asset.title}</td>
                                                                <td>{asset.artist}</td>
                                                                <td>{asset.streams.toLocaleString()}</td>
                                                                <td>{asset.revenue.toFixed(2)} {report.provider_currency}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {report.artist_details && report.artist_details.length > 0 && (
                                            <div className="mb-4">
                                                <h6>Artist Details</h6>
                                                <Table bordered responsive striped>
                                                    <thead>
                                                        <tr>
                                                            <th>Artist</th>
                                                            <th>Assets</th>
                                                            <th>Products</th>
                                                            <th>Revenue</th>
                                                            <th>Percentage</th>
                                                            <th>Distribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {report.artist_details.map((artist, index) => (
                                                            <tr key={index}>
                                                                <td>{artist.artist}</td>
                                                                <td>{artist.assets}</td>
                                                                <td>{artist.products}</td>
                                                                <td>{artist.revenue.toFixed(2)} {report.provider_currency}</td>
                                                                <td>{artist.percentage}%</td>
                                                                <td>
                                                                    <div className="progress" style={{ height: '10px' }}>
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            style={{ width: `${artist.percentage}%` }}
                                                                            aria-valuenow={artist.percentage}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        ></div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}


                                    </>
                                )}
                    </Col>
                </Row>
            </Container>
            </div>
        </>
    );
}

export default IncomeReport;
